import type { Messages } from './en';

/**
 * Turkish messages.
 *
 * Typed against the English contract: leave a key out and the build fails.
 */
export const tr: Messages = {
    meta: {
        htmlLang: 'tr',
        nativeName: 'Türkçe',
    },

    common: {
        currencyNames: {
            TRY: 'Türk Lirası (₺)',
            USD: 'ABD Doları ($)',
            EUR: 'Euro (€)',
            GBP: 'İngiliz Sterlini (£)',
        },
        datePlaceholder: 'gg.aa.yyyy',
        pickDate: 'Takvimden Seç',
        chooseFile: 'Dosya Seç',
        noFileChosen: 'Henüz dosya seçilmedi.',
        dropHint: 'Dosyayı buraya bırakın ya da seçin.',
        loading: 'Yükleniyor…',
        and: 've',
        opensInNewTab: '(yeni sekmede açılır)',
        notFound: {
            title: 'Sayfa Bulunamadı',
            body: 'Adres yanlış yazılmış ya da sayfa taşınmış olabilir.',
            home: 'Ana Sayfaya Dön',
        },
        forbidden: {
            title: 'Bu Sayfaya Erişiminiz Yok',
            body: 'Firmadaki rolünüz bu sayfayı kapsamıyor. Rolünüzü firma sahibi ya da yöneticisi değiştirebilir.',
            home: 'Genel Bakışa Dön',
        },
        error: {
            title: 'Sayfa Görüntülenemedi',
            body: 'Beklenmeyen bir sorun oluştu. Tekrar deneyin; sorun sürerse sayfayı yenileyin.',
            retry: 'Tekrar Dene',
            home: 'Ana Sayfaya Dön',
        },
        notInPlan: {
            title: 'Paketinize Dahil Değil',
            body: 'Rolünüz bu sayfayı kapsıyor, firmanızın paketi ise bu özelliği içermiyor.',
            subscription: 'Aboneliğe Git',
        },
        save: 'Kaydet',
        saved: 'Kaydedildi.',
        unsaved: 'Kaydedilmemiş değişiklik var.',
        clean: 'Her şey kayıtlı.',
        leave: {
            title: 'Kaydedilmemiş Değişiklikler Kaybolacak',
            body: 'Bu sayfada kaydetmediğiniz değişiklikler var. Sayfadan çıkarsanız kaybolur.',
            action: 'Sayfadan Çık',
            cancel: 'Sayfada Kal',
        },
        delete: 'Sil',
        select: 'Seçin…',
        edit: 'Düzenle',
        more: 'Diğer',
        cancel: 'Vazgeç',
        close: 'Kapat',
        breadcrumb: 'Sayfa yolu',
        confirm: 'Onayla',
        signOut: 'Çıkış Yap',
        pager: {
            first: 'İlk Sayfa',
            prev: 'Önceki',
            next: 'Sonraki',
            last: 'Son Sayfa',
            position: (page: number, pages: number) => `${page} / ${pages}`,
        },
        language: 'Dil',
        theme: {
            label: 'Tema',
            light: 'Açık',
            dark: 'Koyu',
            system: 'Sistem',
        },
        genericError: 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.',
        search: {
            placeholder: 'Ürün, sipariş, müşteri ara',
            empty: 'Sonuç yok.',
            hint: 'En az iki karakter yazın.',
            results: 'Arama sonuçları',
            kind: {
                product: 'Ürün',
                order: 'Sipariş',
                customer: 'Müşteri',
            },
        },
    },

    errors: {
        status: {
            401: 'Oturumunuz sona erdi. Yeniden giriş yapın.',
            402: 'Paketinize dahil bir sınıra ulaştınız.',
            403: 'Bu işlem için yetkiniz yok.',
            404: 'Aradığınız kayıt bulunamadı. Sayfayı yenileyip yeniden deneyin.',
            409: 'Bu işlem mevcut bir kayıtla çakışıyor. Bilgileri kontrol edip yeniden deneyin.',
            413: 'Dosya çok büyük.',
            422: 'Gönderilen bilgiler kabul edilmedi. Bilgileri kontrol edip yeniden deneyin.',
            429: 'Çok fazla istek gönderildi. Biraz bekleyip yeniden deneyin.',
        },
        field: 'Bu alanı kontrol edin.',
        problems: {
            'auth.signed_out': 'Oturumunuz sona erdi. Yeniden giriş yapın.',
            'auth.no_company': 'Önce bir firma seçin.',
            'auth.forbidden': 'Bu işlem için yetkiniz yok.',
            'auth.not_in_plan': 'Bu işlem firmanızın paketine dahil değil. Paketinizi Abonelik sayfasından değiştirebilirsiniz.',
            'auth.subscription_expired':
                'Aboneliğinizin süresi doldu; yeni kayıt eklenemez. Siparişleriniz, faturalarınız ve stokunuz çalışmaya devam eder, gerisi ödemeniz alındığında hemen açılır.',
            'billing.plan_not_sold': 'Bu paket buradan satılmıyor. Talep bırakın, ekibimiz sizinle iletişime geçsin.',
            'billing.period_not_sold': 'Bu paket bu dönem için satılmıyor.',
            'billing.method_unavailable': 'Bu ödeme yöntemi henüz açık değil.',
            'billing.charge_not_pending': 'Bu ödeme artık beklemiyor.',
            'billing.change_scheduled':
                'Bekleyen bir paket değişikliğiniz var; o başlayana kadar başka bir paket satın alınamaz ve süre uzatılamaz. Değişiklik için destek ekibimizle iletişime geçin.',
            'billing.charge_not_paid': 'Bu ödeme yapılmamış.',
            'billing.card_refused': (quote: string) => `Ödeme başlatılamadı: ${quote}`,
            'billing.card_unavailable': 'Ödeme hizmetine şu an ulaşılamıyor. Birkaç dakika sonra yeniden deneyin.',
            'billing.company_incomplete': 'Paket satın almak için Firma Bilgileri’nde yasal unvan, vergi numarası, adres ve telefon eksiksiz olmalı.',
            'billing.agreement_not_accepted':
                'Devam etmek için Ön Bilgilendirme Formu ve Mesafeli Hizmet Sözleşmesi’nin altındaki üç kutuyu da işaretleyin.',
            'billing.agreement_changed':
                'Ön Bilgilendirme Formu ve Mesafeli Hizmet Sözleşmesi siz okuduktan sonra değişti. Güncel metni okuyup yeniden onaylayın.',
            'billing.support_session': 'Sözleşmeyi satıcı onaylar. Destek erişimi sırasında paket satın alınamaz.',
            'billing.nothing_to_cancel': 'İptal edilecek ücretli bir dönem yok. Deneme süresi ve ücretsiz paket kendiliğinden sona erer.',
            'billing.subscription_ended': 'İptal ettiğiniz paketin dönemi sona erdi. Devam etmek için yeniden paket satın alın.',
            'billing.cancel_support_session': 'Aboneliği satıcı iptal eder ya da iptali geri alır. Destek erişimi sırasında yapılamaz.',
            'identity.terms_not_accepted': 'Hesap oluşturmak için Kullanım Koşulları’nı kabul edin.',
            'identity.terms_changed': 'Kullanım Koşulları siz okuduktan sonra güncellendi. Sayfayı yenileyip güncel metni okuyun ve onaylayın.',
            'identity.terms_support_session': 'Kullanım Koşulları’nı satıcı onaylar. Destek erişimi sırasında onaylanamaz.',
            'auth.terms_not_accepted': 'Devam etmek için güncel Kullanım Koşulları’nı onaylayın.',
            'identity.tax_number_invalid': 'Bu vergi veya T.C. kimlik numarası geçerli değil. Rakamları tek tek kontrol edin.',
            'identity.phone_invalid': 'Telefon numarasını alan koduyla birlikte yazın.',
            'media.not_found': 'Bu görsel artık yok. Sayfayı yenileyin.',
            'media.too_large': "Bu dosya 12 MB'tan büyük.",
            'media.not_an_image': 'Bu dosya bir görsel değil.',
            'media.too_small': 'Bu görsel yayınlamak için çok küçük.',
            'media.too_many': 'Bu ürün izin verilen en fazla görsel sayısına ulaştı.',
            'storefront.image_too_large': "Bu dosya 12 MB'tan büyük.",
            'storefront.image_too_small': 'Bu görsel kullanmak için çok küçük.',
            'storefront.logo_too_small': 'Logonun kısa kenarı en az 36 piksel olmalı.',
            'storefront.favicon_too_small': 'Site simgesi en az 48×48 piksel olmalı.',
            'storefront.not_an_image': 'Bu dosya bir görsel değil.',
            'storefront.unsubscribe_link_unknown': 'Bu bülten çıkış bağlantısı tanınmıyor.',
            'catalog.brand_taken': 'Bu adda bir marka zaten var.',
            'catalog.brand_name_unusable': 'Marka adında en az bir harf ya da rakam olmalı.',
            'catalog.brand_in_use': 'Bu markayı kullanan ürünler olduğu için silinemez. Markayı başka bir markayla birleştirin.',
            'catalog.brand_merge_self': 'Bu markayı birleştirmek için başka bir marka seçin.',
            'catalog.brand_slug_taken': 'Bu mağaza adresi başka bir markada kullanılıyor.',
            'catalog.brand_slug_unusable': 'Mağaza adresinde en az bir harf ya da rakam olmalı.',
            'catalog.brand_logo_unknown': 'Logo bulunamadı. Logoyu yeniden yükleyin.',
            'catalog.brand_logo_too_large': 'Logo 12 MB’tan büyük.',
            'catalog.brand_logo_too_small': 'Logonun her kenarı en az 300 piksel olmalı.',
            'catalog.brand_logo_not_an_image': 'Logo PNG, JPEG ya da WebP biçiminde bir görsel olmalı.',
            'catalog.sku_taken': 'Bu ürün kodu başka bir üründe kullanılıyor.',
            'catalog.barcode_taken': 'Bu barkod başka bir üründe kullanılıyor.',
            'catalog.slug_taken': 'Bu adres başka bir üründe kullanılıyor.',
            'catalog.quota_exceeded':
                'Paketinize dahil ürün sınırına ulaştınız. Mevcut ürünleriniz ve siparişleriniz işlenmeye devam eder. Yeni ürün eklemek için ürün sayınızı azaltın ya da başka bir paket seçin.',
            'catalog.bulk_edit_reverted': 'Bu toplu düzenleme zaten geri alınmış.',
            'catalog.template_name_taken': 'Bu adda bir açıklama şablonu zaten var.',
            'catalog.template_incomplete': 'Şablonun bir adı ve metin içeren bir açıklaması olmalı.',
            'catalog.match_decided': 'Bu eşleşme için karar zaten verilmiş.',
            'catalog.no_products': 'En az bir ürün seçin.',
            'catalog.nothing_to_change': 'Seçtiğiniz ürünler zaten bu durumda.',
            'catalog.variant_exists': 'Bu üründe bu varyant zaten var.',
            'catalog.too_many_variants': 'Bu, bir ürünün sahip olabileceğinden fazla varyant kombinasyonu.',
            'catalog.duplicate_axis_value': 'Bir varyant özelliğinde aynı değer birden fazla yazılmış.',
            'catalog.no_axes': 'Bu ürünün varyant özelliği yok.',
            'catalog.variant_incomplete': 'Her varyantın, her varyant özelliği için bir değeri olmalı.',
            'catalog.price_invalid': 'Fiyat sıfır olamaz. Liste fiyatı için satış fiyatı gerekir ve liste fiyatı satış fiyatından düşük olamaz.',
            'catalog.price_followed': 'Bu fiyatı kullanan bir satış kanalı var. Fiyatı kaldırmadan önce o kanala kanala özel fiyat girin.',
            'catalog.currency_followed':
                'Bu fiyatı şu anki para biriminde kullanan bir satış kanalı var. Para birimini değiştirmeden önce o kanala kanala özel fiyat girin.',
            'catalog.import_too_large': 'Dosya 16 MB’tan büyük. Ürünleri birkaç dosyaya bölün.',
            'catalog.import_workbook_too_large':
                'Bu Excel dosyası okunamayacak kadar büyük. Yalnızca ürünlerin olduğu sayfayı bırakıp yeniden kaydedin.',
            'catalog.import_too_many_rows': 'Dosyada 5.000’den fazla ürün satırı var. Ürünleri birkaç dosyaya bölün.',
            'catalog.import_too_many_columns': 'Dosyanın bir satırında 256’dan fazla sütunda değer var. Yalnızca şablondaki sütunları bırakın.',
            'catalog.import_unreadable': 'Dosya okunamadı. Dosyayı Excel’de .xlsx olarak kaydedip yeniden yükleyin.',
            'catalog.import_legacy_workbook': 'Bu dosya eski Excel biçiminde (.xls) ya da parolalı. Excel’de parolasız .xlsx olarak kaydedin.',
            'catalog.import_columns_missing': 'Dosyanın ilk satırında sku ve title sütunları yok. Şablondaki başlıkları kullanın.',
            'catalog.import_required_missing': 'Ürün kodu (sku) ve ürün adı (title) zorunlu.',
            'catalog.import_tax_rate_invalid': 'KDV oranı (taxRate) 0, 1, 10 ya da 20 olmalı.',
            'catalog.import_sku_too_long': 'Ürün kodu 64 karakterden uzun.',
            'catalog.import_title_too_long': 'Ürün adı 300 karakterden uzun.',
            'catalog.import_sku_repeated': 'Bu ürün kodu dosyanın önceki bir satırında da var.',
            'catalog.import_formula_unresolved':
                'Bu satırdaki bir formülün kaydedilmiş değeri yok ya da değeri bir hata. Formülü düzeltip dosyayı Excel’de yeniden kaydedin.',
            'catalog.import_price_invalid':
                'Satış fiyatı (salePrice) ya da liste fiyatı (listPrice) kullanılamıyor. Satış fiyatı sıfırdan büyük olmalı; liste fiyatı yalnızca satış fiyatıyla birlikte yazılır ve ondan düşük olamaz.',
            'catalog.import_number_ambiguous':
                '1.500 gibi bir tutar iki türlü okunabilir. Ondalık için virgül kullanın, binlik ayırıcı koymayın: 1500 ya da 1,5.',
            'catalog.import_currency_invalid': 'Para birimi (currency) ₺, USD, EUR ya da GBP olmalı. Boş bırakılırsa ₺.',
            'catalog.import_stock_invalid': 'Stok (stock) 0 ile 1.000.000 arasında bir tam sayı olmalı.',
            'catalog.import_image_invalid': 'Görsel adresi kullanılamıyor. Her görsel sütununa https ile başlayan, herkese açık tek bir adres yazın.',
            'channelaccount.field_missing': 'Zorunlu bağlantı bilgilerinin hepsini girin.',
            'channelaccount.setting_missing': 'Bu ayarı doldurun.',
            'channelaccount.setting_not_listed': 'Bu ayar için listedeki değerlerden birini seçin.',
            'channelaccount.environment_unavailable': 'Bu kanal bu ortamda kullanılamıyor.',
            'channelaccount.unknown_channel': 'Bu kanal burada bağlanamaz.',
            'channelaccount.quota_exceeded':
                'Paketinize dahil pazaryeri hesabı sınırına ulaştınız. Bağlı hesaplarınız çalışmaya devam eder. Yeni bir hesap bağlamak için bir bağlantıyı kaldırın ya da başka bir paket seçin.',
            'channelaccount.managed_by_shop': 'Bu satış kanalı mağazanızın kendisidir; Mağaza Ayarları’ndan yönetilir.',
            'newsletter.not_draft': 'Gönderilmiş bir kampanya değiştirilemez ve silinemez.',
            'newsletter.body_empty': 'Kampanya metnine birkaç kelime yazın.',
            'newsletter.no_subscribers': 'İzni geçerli bir abone yok; kampanya gönderilecek kimse bulunmuyor.',
            'newsletter.shop_closed': 'Kampanya gönderebilmek için mağazanızın yayında olması gerekir; e-postadaki bağlantılar mağazaya gider.',
            'notification.type_not_offered': 'Rolünüz bu bildirimi e-posta ile almanıza izin vermiyor. Sayfayı yenileyip yeniden seçin.',
            'channelaccount.duplicate': 'Bu kanal bu bilgilerle zaten bağlı.',
            'channelaccount.credentials_refused':
                'Satış kanalı bağlantı bilgilerini kabul etmedi. Bilgileri Satış Kanalları sayfasından güncelleyin.',
            'channelaccount.channel_refused': (quote: string) => quote,
            'channelaccount.channel_unreachable': 'Satış kanalına şu an ulaşılamıyor. Biraz sonra yeniden deneyin.',
            'channelaccount.check_rate_limited':
                'Satış kanalı şu an bu bağlantıdan daha fazla istek almıyor; kontrol yapılmadı ve bağlantının durumu değişmedi. Birkaç saniye sonra yeniden deneyin.',
            'auth.invalid_credentials': 'E-posta veya parola hatalı.',
            'auth.second_factor_invalid':
                'Kod doğru değil. Doğrulama uygulamanızın şu an gösterdiği kodu ya da kullanılmamış bir kurtarma kodunu girin.',
            'auth.second_factor_expired': 'Bu girişin süresi doldu. Parolanızla yeniden giriş yapın.',
            'auth.two_factor_enabled': 'İki adımlı doğrulama zaten açık.',
            'auth.two_factor_not_started': 'İki adımlı doğrulama kurulumunu yeniden başlatın.',
            'auth.session_expired': 'Oturumunuz sona erdi. Yeniden giriş yapın.',
            'auth.support_link_invalid': 'Bu destek bağlantısı artık geçerli değil.',
            'identity.email_taken': 'Bu e-posta adresiyle açılmış bir hesap zaten var.',
            'identity.company_address_taken': 'Bu firma adresi zaten alınmış.',
            'identity.no_access': 'Buna erişiminiz yok.',
            'identity.company_inactive': 'Bu firma hesabı etkin değil.',
            'identity.password_wrong': 'Mevcut parolanız bu değil.',
            'identity.link_invalid': 'Bu bağlantı artık geçerli değil. Yenisini isteyin.',
            'identity.weak_password': 'En az 12 karakterlik ve birkaç farklı karakter içeren bir parola seçin.',
            'identity.password_breached': 'Bu parola, kamuya açık veri sızıntılarında yer alıyor. Başka bir parola seçin.',
            'identity.name_too_short': 'Bu ad çok kısa.',
            'identity.email_invalid': 'Geçerli bir e-posta adresi girin.',
            'identity.verification_link_invalid': 'Bu doğrulama bağlantısı geçerli değil.',
            'identity.token_scope_not_held': 'Bir API anahtarına yalnızca sizde olan yetkiler verilebilir.',
            'identity.invitation_invalid': 'Bu davet artık geçerli değil. Yenisini isteyin.',
            'identity.role_in_use': 'Bu rolde hâlâ biri var. Önce onu başka bir role taşıyın.',
            'identity.role_built_in': 'Hazır roller değiştirilemez. Bunun yerine kendi rolünüzü oluşturun.',
            'identity.role_name_taken': 'Bu adla bir rol zaten var.',
            'identity.unknown_permission': 'Yetkilerden biri tanınmıyor.',
            'identity.access_not_held': 'Yalnızca sizde olan yetkileri verebilir veya değiştirebilirsiniz.',
            'identity.last_admin': 'Bu değişiklikle firmayı yönetebilecek kimse kalmaz.',
            'identity.already_member': 'Bu kişi zaten bu firmada.',
            'identity.user_limit_reached':
                'Paketinize dahil kullanıcı sınırına ulaştınız. Ekibiniz çalışmaya devam eder. Yeni kullanıcı eklemek için bekleyen bir daveti geri alın, bir üyeyi devre dışı bırakın ya da başka bir paket seçin.',
            'identity.unknown_status': 'Bu durum tanınmıyor.',
            'identity.role_name_required': 'Role bir ad verin.',
            'storefront.legal_texts_missing': 'Mağazayı yayımlamak için Mesafeli Satış Sözleşmesi ve Gizlilik Politikası metinlerini yazın.',
            'storefront.shipping_band_duplicate': 'Aynı desiden başlayan iki aralık var. Her aralığa farklı bir başlangıç desisi verin.',
            'storefront.promotion_not_found': 'Bu indirim bulunamadı; sayfayı yenileyin.',
            'storefront.promotion_terms_invalid':
                '0’dan büyük ve 100’den küçük bir oran girin ya da Al sayısını, en az 1 olan Öde sayısından büyük yapın.',
            'storefront.promotion_category_missing': 'İndirimin uygulanacağı kategoriyi seçin.',
            'storefront.promotion_products_missing': 'İndirimin uygulanacağı en az bir ürün ekleyin.',
            'storefront.promotion_window_invalid': 'Bitiş tarihi başlangıç tarihinden önce.',
            'storefront.same_day_needs_cut_off':
                'Aynı gün kargo için Son Sipariş Saati girin ya da Kargoya Verme süresini 1 gün veya daha fazla yapın.',
            'storefront.slug_taken': 'Bu mağaza adresi zaten alınmış.',
            'storefront.slug_invalid': 'Mağaza adresi yalnızca küçük harf, rakam ve tire içerebilir.',
            'storefront.domain_unverified':
                'Doğrulama kaydı alan adınızda bulunamadı. Kaydı alan adınızın DNS ayarlarına eklediğinizi kontrol edin; yeni eklediyseniz birkaç dakika sonra yeniden deneyin.',
            'storefront.domain_taken': 'Bu alan adını başka bir mağaza doğrulamış. Alan adı sizinse destek ekibimize yazın.',
            'storefront.theme_version_unknown': 'Bu görünüm artık yayın geçmişinde yok. Sayfayı yenileyin.',
            'storefront.size_guide_invalid': 'Beden tablosu için başlık, her sütun için bir ad ve en az bir satır gerekli.',
            'storefront.size_guide_not_found': 'Bu beden tablosu artık yok.',
            'storefront.size_guides_full': 'Mağazada zaten 100 beden tablosu var. Kullanmadığınız birini silin.',
            'storefront.size_guide_category_taken':
                'Seçtiğiniz kategorilerden biri başka bir beden tablosunda. Bir kategorinin tek tablosu olur; önce diğerinden çıkarın.',
            'storefront.banner_alt_required':
                'Başlığı olmayan bir kampanya afişinin görsel açıklaması boş. Görselin ne gösterdiğini ya da üzerindeki yazıyı Vitrin sekmesinden girin.',
            'order.range_reversed': 'Bitiş tarihi başlangıç tarihinden önce.',
            'report.range_reversed': 'Bitiş tarihi başlangıç tarihinden önce.',
            'report.period_too_long': 'Bir rapor en fazla bir yılı kapsar. Daha kısa bir dönem seçin.',
            'report.range_too_long': 'Seçilen aralık 366 dönemden uzun. Daha kısa bir aralık ya da Haftalık veya Aylık dönemler seçin.',
            'report.product_not_found': 'Ürün bulunamadı; silinmiş olabilir.',
            'order.note_empty': 'Nota bir şey yazın.',
            'order.identity_number_invalid': 'Bu TC kimlik numarası geçerli değil.',
            'order.identity_number_taken': 'Bu TC kimlik numarası başka bir müşteride kayıtlı. Aynı kişiyse o kaydı kullanın.',
            'order.tax_number_invalid': 'Bu vergi numarası geçerli değil.',
            'order.not_awaiting_payment': 'Bu sipariş havale beklemiyor; ödemesi kapıda alınıyor ya da zaten alındı.',
            'orderaction.line_not_on_order': 'Satırlardan biri bu siparişe ait değil. Sayfayı yenileyin.',
            'orderaction.quantities_mismatch': 'Paket miktarları sipariş satırlarıyla eşleşmiyor.',
            'orderaction.no_lines': 'En az bir satır seçin.',
            'orderaction.unsupported': 'Bu işlem kanal tarafından desteklenmiyor.',
            'orderaction.no_carrier': 'Bir kargo firması seçin.',
            'orderaction.line_not_eligible': 'Kanal bu satırı bu işlem için uygun görmüyor.',
            'orderaction.no_label': 'Bu paket için kanaldan etiket alınamadı.',
            'orderaction.status_param_required': 'Bu durum, kanalın zorunlu tuttuğu bir bilgi istiyor. İşaretli alanı doldurup yeniden deneyin.',
            'orderaction.order_closed': 'Bu sipariş iptal edildi ya da iade edildi. Durumu artık değiştirilemez.',
            'orderaction.awaiting_transfer': 'Havale henüz alınmadı. Önce Ödeme Alındı ile onaylayın.',
            'orderaction.status_backwards': 'Paket bu durumu geçti. Daha sonraki bir durum seçin.',
            'orderaction.credentials_refused': 'Satış kanalı bağlantı bilgilerini kabul etmedi. Satış Kanalları sayfasından güncelleyin.',
            'orderaction.channel_refused': (quote: string) => quote,
            'orderaction.channel_unreachable': 'Satış kanalına şu an ulaşılamıyor. Biraz sonra yeniden deneyin.',
            'claim.no_items': 'İade için en az bir ürün seçin.',
            'claim.already_decided': 'Bu iade için karar zaten verilmiş.',
            'claim.cancel_after_dispatch': 'Bu sipariş kargoya verildi. Geri gelenleri iade olarak kaydedin.',
            'claim.return_before_dispatch': 'Bu siparişten henüz kargoya verilen bir şey yok. Bunun yerine iptal kaydı açın.',
            'claim.order_closed': 'Bu sipariş iptal edildi ya da iade edildi. Üzerine iptal ya da iade kaydı açılamaz.',
            'claim.not_awaiting_pre_approval': 'Bu talep ön onay beklemiyor. Sayfayı yenileyin.',
            'claim.pre_approval_unsupported': 'Bu talebin pazaryeri ön onayı buradan almıyor. Ön onayı pazaryerinin kendi panelinden verin.',
            'claim.pre_approval_in_flight': 'Bu ön onay az önce gönderildi. Sonucu birazdan burada görünür.',
            'claim.resolution_not_exchange': 'Yalnızca bir değişim talebi yeni ürünle ya da para iadesiyle kapatılır. Sayfayı yenileyin.',
            'claim.exchange_refund_unsupported':
                'Bu talebin pazaryeri değişim talebinin para iadesiyle kapatılmasını buradan almıyor. Talebi pazaryerinin kendi panelinden sonuçlandırın.',
            'claim.channel_refused': (quote: string) => quote,
            'claim.channel_unreachable': 'Pazaryerine şu anda ulaşılamıyor. Birkaç dakika sonra yeniden deneyin.',
            'question.answer_too_short': 'Yanıt bu pazaryerinin kabul ettiğinden kısa. Biraz daha uzun yazın.',
            'question.answer_too_long': 'Yanıt bu pazaryerinin kabul ettiğinden uzun. Kısaltın.',
            'question.already_answered': 'Bu soru yanıtlanmış ve bu pazaryeri soru başına tek yanıt alıyor.',
            'question.closed': 'Pazaryeri bu soruyu kapattı; yanıt süresi doldu.',
            'claim.reason_required': 'İadeyi neden reddettiğinizi yazın. Gerekçesiz ret pazaryerinde bozulur.',
            'claim.item_not_in_claim': 'Bu ürün bu iadede yok. Sayfayı yenileyin.',
            'invoice.no_provider': 'Önce bir e-Fatura hizmeti bağlayın.',
            'invoice.already_invoiced': 'Bu siparişin zaten bir faturası var.',
            'invoice.tax_rates_stale': 'Siparişin kalemleri siz bakarken değişti. Sayfayı yenileyin ve KDV oranlarını yeniden girin.',
            'invoice.nothing_to_refresh': 'Bu belge için e-Fatura hizmetine sorulacak bir şey kalmadı.',
            'invoice.connection_in_use':
                'Bu hizmetle kesilen ve sonucu beklenen belgeler var. Belgeler sonuçlandığında bağlantıyı kaldırabilirsiniz.',
            'invoice.template_channel_taken': 'Bu kanal için zaten bir şablon var. O şablonu düzenleyin ya da başka bir kanal seçin.',
            'invoice.already_cancelled': 'Bu fatura zaten iptal edilmiş.',
            'invoice.cannot_cancel': 'Bu e-Fatura hizmeti kesilmiş bir faturayı geri çekemez. Bunun yerine iade faturası kesin.',
            'invoice.already_returned': 'Bu faturanın iade faturası zaten kesilmiş.',
            'invoice.not_issued': 'Bu fatura henüz kesilmedi.',
            'invoice.lines_unmatched': 'Fatura kesmeden önce siparişteki her kalemi bir ürünle eşleştirin.',
            'invoice.nothing_to_invoice': 'Bu siparişte faturalanacak satır kalmadı.',
            'invoice.unknown_provider': 'Bu e-Fatura hizmeti burada kullanılamaz.',
            'invoice.credential_missing': 'Zorunlu bağlantı bilgilerinin hepsini girin.',
            'payment.unknown_provider': 'Bu ödeme hizmeti burada kullanılamaz.',
            'payment.credential_missing': 'Zorunlu bağlantı bilgilerinin hepsini girin.',
            'payment.unknown_setting': 'Ödeme hizmetinin sunduğu değerlerden birini seçin.',
            'payment.environment_absent': 'Ödeme hizmetinin böyle bir ortamı yok. Hizmetin sunduğu Test ya da Canlı ortamı seçin.',
            'payment.already_connected': 'Bu ödeme hizmeti zaten bağlı. Anahtarlarını bağlantının kendisinden değiştirin.',
            'payment.payments_in_flight': 'Şu anda bu hizmet üzerinden ödeme yapan bir müşteri var. Birkaç dakika sonra yeniden deneyin.',
            'sms.unknown_provider': 'Bu SMS hizmeti burada kullanılamaz.',
            'sms.credential_missing': 'Zorunlu bağlantı bilgilerinin hepsini girin.',
            'sms.unknown_setting': 'SMS hizmetinin sunduğu değerlerden birini seçin.',
            'sms.already_connected': 'Bir SMS hizmeti zaten bağlı. Başka bir hizmet bağlamadan önce onun bağlantısını kaldırın.',
            'sms.not_mobile': 'Bir Türkiye cep telefonu numarası yazın, örneğin 0532 123 45 67.',
            'sms.test_limit': 'Bir saatte en fazla 10 test SMS gönderilebilir. Biraz sonra yeniden deneyin.',
            'invoice.no_lines': 'Faturada en az bir satır olmalı.',
            'invoice.buyer_unidentified': 'Kurumsal fatura için alıcının vergi numarası gerekir. Müşteri kaydına ekleyin.',
            'invoice.exemption_reason_required': "KDV'siz bir satır için istisna kodu seçin.",
            'invoice.issued_in_the_future': 'Fatura ileri bir tarihle kesilemez. Siparişin tarihini kontrol edin.',
            'invoice.rate_required': 'Yabancı para birimindeki fatura için kur girin.',
            'invoice.registration_unknown':
                'Alıcının e-Fatura mükellefi olup olmadığı GİB’den öğrenilemedi, bu yüzden doğru belge türü seçilemiyor. Birkaç dakika sonra yeniden deneyin.',
            'invoice.no_connection_for_kind':
                'Bu alıcıya kesilmesi gereken belgeyi kesen bir e-Fatura hizmetiniz yok: e-Fatura mükellefine e-Fatura, diğer alıcılara e-Arşiv kesilir. Fatura Ayarları’nda bir hizmet için bu belgeyi açın ya da onu kesen bir hizmet bağlayın.',
            'invoice.connection_wrong_kind':
                'Seçilen e-Fatura hizmeti bu alıcıya kesilmesi gereken belgeyi kesmiyor: e-Fatura mükellefine e-Fatura, diğer alıcılara e-Arşiv kesilir. Başka bir hizmet seçin ya da Fatura Ayarları’nda bu hizmet için bu belgeyi açın.',
            'invoice.document_kind_required': 'Bu hizmetin e-Fatura mı, e-Arşiv mi, yoksa ikisini birden mi kestiğini seçin.',
            'invoice.not_recorded':
                'Bu fatura, satışı yalnızca kaydeden bir hizmet üzerinden oluşturulmadı; bu yüzden işlenecek bir seri numarası yok.',
            'invoice.already_issued': 'Bu faturanın seri numarası zaten var. Kesilmiş fatura düzeltilmez; bunun yerine iade faturası kesin.',
            'invoice.upload_not_offered':
                'Bu faturanın belgesi zaten var ya da e-Fatura hizmeti belgeyi kendisi sağlıyor; bu yüzden PDF yüklenemez. Sayfayı yenileyin.',
            'invoice.invalid_tax_number': 'Alıcının vergi veya TC kimlik numarası doğrulama basamağını tutturmuyor. Müşteri kaydında düzeltin.',
            'invoice.internet_sale_incomplete':
                'İnternet satışı faturası için ödeme tipi, ödeme tarihi ve satışın yapıldığı adres gerekir. Önce siparişin bilgilerini tamamlayın.',
            'invoice.no_document': 'Henüz iletilecek bir belge yok. PDF’i yükleyin veya hizmetin belgeyi üretmesini bekleyin.',
            'invoice.buyer_has_no_address':
                'Alıcının belgenin gönderilebileceği bir e-posta adresi yok: pazaryerinin maskeli adresi kimseye ulaşmaz. Müşteri kaydına alıcının kendi adresini ekleyin.',
            'invoice.unknown_exemption_code': 'Seçilen KDV istisna kodu GİB’in yayımladığı kodlardan biri değil. Listeden bir kod seçin.',
            'invoice.not_a_document': 'Yüklenen dosya PDF değil. Faturayı PDF olarak yükleyin.',
            'invoice.document_too_large': 'PDF 5 MB’tan büyük. Faturayı hizmetten yeniden indirip o dosyayı yükleyin.',
            'invoice.unsupported': 'Bu e-Fatura hizmeti bu işlemi yapamıyor.',
            'invoice.provider_unavailable': 'E-Fatura hizmetine şu anda ulaşılamıyor. Birkaç dakika sonra yeniden deneyin.',
            'invoice.range_reversed': 'Bitiş tarihi başlangıç tarihinden önce.',
            'shipping.no_carrier': 'Önce bir kargo firması bağlayın.',
            'shipping.origin_is_default': 'Bu adresi silmeden önce başka bir adresi varsayılan yapın.',
            'shipping.no_origin': 'Kargo kaydı açmadan önce bir çıkış adresi ekleyin.',
            'shipping.already_booked': 'Bu paketin zaten bir kargo kaydı var.',
            'shipping.not_booked': 'Bu gönderi için henüz kargo kaydı açılmadı.',
            'shipping.no_label': 'Bu gönderinin yazdırılacak etiketi yok.',
            'shipping.courier_unknown': 'Seçilen kurye firması burada kullanılamaz. Listeden bir firma seçin.',
            'shipping.courier_incomplete': 'Listede olmayan kurye firması için adını ve vergi numarasını girin.',
            'shipping.courier_tax_number_invalid': 'Kurye firmasının vergi numarası geçerli değil. 10 haneli vergi numarasını kontrol edin.',
            'shipping.unknown_carrier': 'Bu kargo firması burada kullanılamaz.',
            'shipping.credential_missing': 'Zorunlu bağlantı bilgilerinin hepsini girin.',
            'shipping.carrier_unavailable': 'Kargo firmasına şu anda ulaşılamıyor. Birkaç dakika sonra yeniden deneyin.',
            'shipping.awaiting_transfer': 'Havale henüz alınmadı. Kargoya vermeden önce Ödeme Alındı ile onaylayın.',
            'shipping.order_closed': 'Bu sipariş iptal edildi ya da iade edildi. Gönderilecek bir şey yok.',
            'feed.name_taken': 'Bu ad zaten kullanılıyor.',
            'feed.already_running': 'Bu ürün listesi zaten okunuyor.',
            'feed.address_forbidden': 'Bu adres okunamaz. Yalnızca https ile başlayan, herkese açık adresler kabul edilir.',
            'feed.unreachable': 'Tedarikçinin adresine ulaşılamıyor. Adresi kontrol edin ya da daha sonra yeniden deneyin.',
            'feed.unreadable': 'Dosya seçilen biçimde okunamadı. Biçimi kontrol edin.',
            'feed.no_item_path': 'Dosyada tekrarlanan bir kayıt bulunamadı. Birinin yolunu yazın, örneğin Products/Product.',
            'feed.mapping_incomplete': 'Ürün listesinin bir şey aktarabilmesi için stok kodu (ya da barkod) ve başlık eşleştirilmeli.',
            'feed.schedule_invalid': 'Bu zamanlama anlaşılamadı. Cron ifadesi yazın; altı saatte bir için 0 */6 * * *.',
            'mapping.category_unknown': 'Bu pazaryeri kategorisi artık listede yok. Başka bir kategori seçin.',
            'mapping.category_not_leaf': 'En alt kategoriyi seçin. Alt kategorisi olan bir kategoriye ürün gönderilemez.',
            'mapping.category_closed': 'Pazaryeri bu kategoriyi yeni ürünlere kapattı.',
            'mapping.copy_category_mismatch':
                'Bu iki ürün pazaryerinde farklı kategorilerde. Özellikleri başka sorular olduğu için yanıtlar kopyalanamaz.',
            'mapping.copy_source_unmapped':
                'Kopyalanacak ürünün bu pazaryerinde bir kategorisi yok. Önce onun kategorisini eşleyin ya da kategorisi eşlenmiş bir ürün seçin.',
            'mapping.copy_from_self': 'Kopyalamak için başka bir ürün seçin.',
            'mapping.brand_not_in_directory': 'Bu marka artık pazaryerinin listesinde yok. Yeniden arayıp başka bir marka seçin.',
            'mapping.brand_request_unsupported':
                'Bu pazaryeri marka talebi almıyor. Marka ürünle birlikte metin olarak gider ve pazaryerinin kendi incelemesinden geçer.',
            'mapping.brand_request_in_flight': 'Bu marka az önce bu bağlantıdan talep edildi. Yanıtı bekleniyor; talep ikinci kez gönderilmez.',
            'mapping.brand_request_refused': (quote: string) => quote,
            'mapping.brand_request_unauthorized':
                'Pazaryeri bu bağlantının bilgilerini kabul etmedi. Satış Kanalları’nda bilgileri kontrol edip yeniden deneyin.',
            'mapping.brand_request_unreachable': 'Pazaryerine şu anda ulaşılamıyor. Birkaç dakika sonra yeniden deneyin.',
            'mapping.brand_image_too_large': 'Marka görseli en fazla 1 MB olabilir.',
            'mapping.brand_image_unusable': 'Marka görseli PNG ya da JPEG olmalıdır.',
            'pricing.name_taken': 'Bu adla bir kural zaten var.',
            'pricing.already_reverted': 'Bu fiyatlar zaten geri alınmış.',
            'pricing.nothing_to_revert': 'Önizleme hiçbir fiyatı değiştirmedi; geri alınacak bir şey yok.',
            'pricing.rule_unnamed': 'Kurala bir ad verin.',
            'pricing.margin_unreachable': '%100 veya üzeri bir hedef marja hiçbir fiyatla ulaşılamaz.',
            'pricing.rule_no_price': 'Bu kural bir fiyat üretmiyor. Sıfırdan büyük bir değer girin.',
            'pricing.commission_too_high': '%100 veya üzeri komisyon fiyatlanacak bir şey bırakmaz.',
            'pricing.floor_above_ceiling': 'Taban fiyat tavan fiyattan büyük olamaz.',
            'pricing.rule_covers_nothing': 'Kural hiçbir ürünü kapsamıyor. Neye uygulanacağını seçin.',
            'pricing.commission_needs_one_channel': 'Her kanal kendi komisyonunu alır. Bu kuralın fiyatladığı kanalı seçin.',
            'pricing.commission_unsupported': 'Bu kanal komisyon oranlarını bildirmiyor. Oranı kendiniz girin.',
            'pricing.credentials_refused': 'Satış kanalı bağlantı bilgilerini kabul etmedi. Satış Kanalları sayfasından güncelleyin.',
            'pricing.channel_refused': (quote: string) => quote,
            'pricing.channel_unreachable': 'Satış kanalına şu an ulaşılamıyor. Biraz sonra yeniden deneyin.',
            'pricing.undercut_unreachable': 'Rakibin %100 veya daha fazla altında bir fiyat olmaz. 100’den küçük bir değer girin.',
            'pricing.competitor_needs_channel': 'Rakip fiyatları pazaryeri başına okunur. Bu kuralın fiyatlayacağı kanalı seçin.',
            'pricing.competitor_needs_floor': 'Rakibe göre fiyatlayan bir kural için sıfırdan büyük bir taban fiyat girin.',
            'pricing.value_negative': 'Eksi bir değer yalnızca ürün fiyatına göre çalışan bir kurala girilebilir.',
            'pricing.discount_unreachable': 'Yüzde 100 ya da daha fazla bir indirim fiyat bırakmaz.',
            'pricing.standing_rule_change': 'Saklanacak tek bir fiyat değişikliği seçin: sabit fiyat, yüzde ya da tutar.',
            'inventory.insufficient': 'Bunun için yeterli stok yok. Miktarı kontrol edin.',
            'inventory.warehouse_code_taken': 'Bu depo kodu zaten kullanılıyor.',
            'inventory.warehouse_has_stock': 'Depoda hâlâ stok var. Önce Stoğu Taşı ile başka bir depoya taşıyın.',
            'inventory.transfer_same_warehouse': 'Stoğu taşımak için başka bir depo seçin.',
            'inventory.transfer_target_inactive': 'Stok duraklatılmış bir depoya taşınamaz. Satışta olan bir depo seçin.',
            'inventory.transfer_exceeds_free': 'Kaynak depoda bu kadar taşınabilecek stok yok. Siparişlere ayrılmış olanlar dışındaki miktarı girin.',
            'inventory.default_warehouse': 'Varsayılan depo silinemez.',
            'listing.duplicate_row': 'Aynı ürün listede iki kez var.',
            'listing.too_many_rows': 'Listede çok fazla satır var. Daha küçük parçalara bölün.',
            'listing.channel_not_connected': 'Bu kanal bağlı değil.',
            'listing.distribute_no_source': 'Bir kaynak kanal ya da Ürün Fiyatı seçin veya sabit fiyat girin.',
            'listing.distribute_too_many':
                "Bu listede 5.000'den fazla varyant var ve tek seferde en fazla 5.000 varyant eklenebilir. Listeyi arama ya da filtrelerle daraltıp ürünleri parça parça ekleyin.",
            'listing.publish_too_many':
                "Seçtiğiniz ürünlerin bu kanalda 5.000'den fazla varyantı var ve tek seferde en fazla 5.000 varyant yayınlanabilir. Daha az ürün seçip parça parça yayınlayın.",
            'listing.nothing_to_change': 'Değiştirilecek bir şey seçin.',
            'listing.currency_not_accepted': 'Bu kanal bu para biriminde satış yapmıyor.',
            'listing.delivery_days_above_max':
                'Bu kanal daha kısa bir kargoya hazırlık süresi kabul ediyor. Kutunun izin verdiğinden fazla gün girmeyin.',
            'listing.no_product_price': 'Bir varyantın ürün fiyatı yok. Stok ve Fiyat sekmesinden girin.',
            'listing.stock_from_shelf': 'Bu kanalın stoğu raftan gelir. Stoğu burada değil, Stok sayfasından değiştirin.',
            'listing.content_kept_by_channel':
                'Bu kanal, yayındaki bir ürünün başlığını ve açıklamasını entegrasyonla değiştirmeye izin vermiyor. Değişikliği kanalın satıcı panelinden yapın.',
            'listing.sheet_columns_missing':
                'İlk satır kod sütununu (sku) ve salePrice, listPrice, currency ya da stock sütunlarından en az birini adlandırmalı. İndirdiğiniz dosyadan başlayın.',
            'listing.sheet_other_channel': 'Bu dosya başka bir satış kanalı için indirilmiş. O kanalı seçin ya da bu kanalın dosyasını indirin.',
            'listing.sheet_required_missing': 'Kod (sku) eksik.',
            'listing.sheet_sku_repeated': 'Bu kod dosyanın önceki bir satırında da var.',
            'listing.sheet_sku_unknown': 'Bu koda sahip bir ürün varyantı yok.',
            'listing.sheet_number_invalid': 'Bir fiyat sayı değil ya da stok tam sayı değil.',
            'listing.sheet_number_ambiguous':
                '1.500 gibi bir tutar iki türlü okunabilir. Ondalık için virgül kullanın, binlik ayırıcı koymayın: 1500 ya da 1,5.',
            'listing.sheet_zero_price': 'Satış fiyatı sıfırdan büyük olmalı.',
            'listing.sheet_negative_stock': 'Stok sıfırın altında olamaz.',
            'listing.sheet_needs_price': 'Bu ürün bu kanalda henüz yok. Bir satış fiyatı girin.',
            'listing.sheet_list_below_sale': 'Liste fiyatı sıfırdan büyük olmalı ve satış fiyatının altında olmamalı.',
            'listing.withdraw_unsupported': 'Bu kanal ürünü satıştan çekemiyor.',
            'listing.match_decision_unsupported': 'Bu kanal eşleşme kararını buradan almıyor. Kararı kanalın kendi panelinden verin.',
            'listing.no_match_awaiting': 'Bu ürünün bu kanalda kararınızı bekleyen bir eşleşmesi yok. Sayfayı yenileyin.',
            'listing.price_unlock_unsupported': 'Bu kanal fiyat kilidini buradan kaldırmıyor. Kilidi kanalın kendi panelinden kaldırın.',
            'listing.no_price_lock': 'Bu ürünün bu kanalda kilitli bir fiyatı yok. Sayfayı yenileyin.',
            'listing.answer_in_flight': 'Bu yanıt kanala az önce gönderildi. Sonucu birazdan burada görünür.',
            'listing.channel_refused': (quote: string) => quote,
            'listing.channel_unreachable': 'Kanala şu anda ulaşılamıyor. Birkaç dakika sonra yeniden deneyin.',
            'contact.account_email_missing': 'Hesabınızda kullanılabilir bir e-posta adresi yok. Hesap sayfanızdan ekleyin.',
            'contact.message_required': 'Bir mesaj yazın.',
            'webhook.address_refused': 'Bu adres kullanılamaz. https ile başlayan, herkese açık bir adres girin.',
            'channelwebhook.unsupported': 'Bu kanal bildirim göndermiyor.',
            'channelwebhook.channel_unreachable': 'Kanala şu anda ulaşılamıyor. Birkaç dakika sonra yeniden deneyin.',
            'channelwebhook.channel_refused': (quote: string) => quote,
            'shipping.carrier_refused': (quote: string) => quote,
            'shipping.carrier_unsupported': (quote: string) => quote,
            'invoice.provider_refused': (quote: string) => quote,
            'invoice.provider_not_ready': (quote: string) => quote,
            'payment.provider_not_ready': (quote: string) => quote,
            'sms.provider_not_ready': (quote: string) => quote,
        },
    },

    auth: {
        secondFactor: {
            title: 'İki Adımlı Doğrulama',
            hint: 'Doğrulama uygulamanızın gösterdiği kodu girin. Telefonunuz yanınızda değilse kurtarma kodlarınızdan birini girin.',
            code: 'Doğrulama Kodu',
            submit: 'Doğrula',
            startOver: 'Yeniden Giriş Yap',
        },
        signInTitle: 'Panele Giriş',
        signInSubtitle: 'Hesabınıza erişmek için giriş yapın.',
        signUpTitle: 'Hesap Oluştur',
        signUpSubtitle: (trialDays: number) => (trialDays > 0 ? `${trialDays} gün ücretsiz deneyin, kredi kartı gerekmez.` : 'Kredi kartı gerekmez.'),
        email: 'E-posta',
        password: 'Parola',
        passwordHint: 'En az 12 karakter.',
        name: 'Ad Soyad',
        companyName: 'Firma Adı',
        signIn: 'Giriş Yap',
        signUp: 'Hesap Oluştur',
        terms: {
            before: '',
            link: 'Kullanım Koşulları',
            after: "'nı okudum ve kabul ediyorum.",
            noticeBefore: 'Kişisel verilerimin işlenmesine ilişkin ',
            noticeLink: 'Aydınlatma Metni',
            noticeAfter: "'ni okudum.",
        },
        noAccount: 'Hesabınız yok mu?',
        forgot: {
            link: 'Parolamı Unuttum',
            title: 'Parolanızı Sıfırlayın',
            hint: 'E-posta adresinizi yazın, size bir bağlantı gönderelim.',
            submit: 'Bağlantı Gönder',
            sent: 'Bağlantıyı gönderdik.',
            sentHint: 'Bu adrese ait bir hesap varsa mesaj birkaç dakika içinde gelir. Gelmezse adresi yanlış yazmış olabilirsiniz; tekrar deneyin.',
            backToSignIn: 'Girişe Dön',
        },
        reset: {
            title: 'Yeni Parolanızı Belirleyin',
            hint: 'Bu bağlantı bir kez kullanılır.',
            password: 'Yeni Parola',
            passwordHint: 'En az 12 karakter.',
            again: 'Yeni Parola (Tekrar)',
            mismatch: 'İki parola aynı değil.',
            submit: 'Parolayı Değiştir',
            done: 'Parolanız değişti. Artık yeni parolanızla girebilirsiniz.',
            signIn: 'Giriş Yap',
            noToken: 'Bu bağlantı eksik görünüyor.',
            askAgain: 'Yeni Bağlantı İste',
        },
        confirmEmail: {
            title: 'E-posta Adresiniz',
            done: 'Adresiniz güncellendi. Bundan sonra bu adresle girin.',
            failed: 'Bu bağlantı artık geçerli değil. Süresi dolmuş veya daha önce kullanılmış olabilir.',
            signIn: 'Giriş Yap',
        },
        haveAccount: 'Zaten hesabınız var mı?',
        invalidCredentials: 'E-posta veya parola hatalı.',
    },

    // The current terms, accepted before the panel goes on (ADR 0018 §1).
    termsGate: {
        title: 'Kullanım Koşulları’nı Onaylayın',
        body: (company: string) =>
            `${company} firmasında panele devam etmek için Kullanım Koşulları’nı okuyup onaylayın. Bu firma için kayıtlı bir onayınız bulunmuyor.`,
        updatedTitle: 'Kullanım Koşulları Güncellendi',
        updatedBody: (date: string, company: string) =>
            `Kullanım Koşulları ${date} tarihinde güncellendi. ${company} firmasında panele devam etmek için güncel metni okuyup onaylayın.`,
        changeTitle: 'Neler Değişti',
        read: 'Kullanım Koşulları’nı Okuyun',
        evidence: 'Onayınız tarih, saat, IP adresi ve tarayıcı bilgisiyle kaydedilir.',
        accept: 'Onayla ve Devam Et',
    },

    company: {
        selectTitle: 'Firma Seçin',
        selectSubtitle: 'Devam etmek için çalışmak istediğiniz firmayı seçin.',
        noneTitle: 'Bağlı Firma Yok',
        noneSubtitle:
            'Bu hesap hiçbir firmaya bağlı değil. entegrepazar yöneticisiyseniz yönetim panelini kullanın; satıcıysanız sizi firmasına ekleyecek yetkiliyle görüşün.',
        signOut: 'Çıkış Yap',
    },

    bulkEdit: {
        modes: {
            rule: 'Hepsine Aynı Değişiklik',
            rows: 'Ürün Ürün',
            file: 'Excel ile',
        },
        standing: {
            open: 'Kural Olarak Kaydet',
            title: 'Kural Olarak Kaydet',
            description: 'Kural, bu seçime şimdi ve sonradan giren ürünlerin bu kanaldaki fiyatını her gün, diğer kurallarınızdan önce belirler.',
            fromProductPrice:
                'Yüzde ya da tutar, kural her çalıştığında kanalın şu anki fiyatından değil, her ürünün ürün fiyatından hesaplanır. Ürün fiyatı olmayan ürün atlanır.',
            stockNotKept: 'Stok değişikliği kurala girmez.',
            name: 'Kural Adı',
            defaultName: (channel: string) => `Toplu Düzenleme · ${channel}`,
            floor: 'Taban Fiyat',
            floorHint: 'İsteğe bağlı. Kural fiyatı hiçbir zaman bunun altına indirmez.',
            submit: 'Kaydet ve Uygula',
            saved: (changed: number, skipped: number) =>
                `Kural kaydedildi. ${changed} fiyat değişti, ${skipped} ürün atlandı. Kural her gün yeniden çalışır.`,
            viewRules: 'Fiyatlandırma’da Gör',
        },
        sheet: {
            title: 'Excel ile Fiyat ve Stok',
            intro: 'Bu kanaldaki ürünleri indirin, fiyat ve stoğu Excel’de değiştirin ve dosyayı yükleyin. Uygulamadan hiçbir şey değişmez.',
            download: 'Excel Dosyasını İndir',
            preview: 'Önizle',
            apply: (n: number) => `${n} Değişikliği Uygula`,
            summary: (committed: boolean, changed: number, unchanged: number, imported: number, refused: number) =>
                `${changed} ${committed ? 'değişti' : 'değişecek'} · ${unchanged} değişmiyor · ${imported} daha önce aktarıldı · ${refused} reddedildi`,
            applied: (n: number) => `${n} teklif güncellendi. Değişiklikler kanala gönderilmek üzere sıraya alındı.`,
            nothingToApply: 'Bu dosyadaki hiçbir satır bir teklifi değiştirmiyor.',
            verdicts: {
                changed: 'Değişecek',
                changedDone: 'Değişti',
                unchanged: 'Değişmiyor',
                imported: 'Daha Önce Aktarıldı',
            },
            stockIgnored: 'Stok raftan gelir; dosyadaki stok okunmadı.',
            stockFromShelf: 'Bu kanal stoğunu rafınızdan alır; dosyasında stok sütunu yoktur ve bir sütuna yazılan stok okunmaz.',
            columnsTitle: 'Sütunlar',
            columns: {
                sku: 'Varyant kodu. Zorunlu; satır bu kodla bulunur.',
                title: 'Ürün adı, okumanız için. İçe aktarılmaz.',
                salePrice: 'Bu kanaldaki satış fiyatı.',
                listPrice: 'Liste fiyatı (üstü çizili olan). Satış fiyatının altında olamaz.',
                currency: 'Para birimi, örneğin ₺ ya da USD. Boş bırakılırsa teklif kendi para birimini korur.',
                stock: 'Bu kanaldaki stok, tam sayı.',
                channelId: 'Dosyanın indirildiği satış kanalı. Olduğu gibi bırakın.',
            },
            blankHint: 'Boş bir hücre o değeri olduğu gibi bırakır. Bu kanalda yeni olan bir ürüne satış fiyatı gerekir.',
            againHint: 'Bir kez uygulanan satır yeniden uygulanmaz: aynı dosyayı iki kez yüklemek ikincisinde hiçbir şeyi değiştirmez.',
            limitsHint: 'Bir dosya en çok 5.000 satır ve 16 MB olabilir. Formüller hesaplanmaz; Excel’in kaydettiği değer okunur.',
        },
        rows: {
            title: 'Fiyat ve Stok Listesi',
            search: 'Koda veya ada göre arayın',
            empty: 'Bu Kanalda Henüz Ürün Yok',
            emptyHint: 'Ürünler bu kanalda bir teklif taşımaya başladıklarında burada görünür.',
            count: (shown: number, total: number) => `${total} satırın ${shown} tanesi`,
            review: (count: number) => (count === 0 ? 'Değişiklikleri Gözden Geçir' : `${count} Değişikliği Gözden Geçir`),
            reviewTitle: 'Ne Olacak',
            apply: 'Uygula',
            state: 'Sonuç',
            willChange: 'Değişecek',
            rowProblems: {
                not_in_catalogue: 'Katalogda yok',
                zero_price: 'Fiyat sıfır olamaz',
                negative_stock: 'Stok eksi olamaz',
                needs_price: 'Bu kanalda ilk kez; fiyat girin',
                list_below_sale: 'Liste fiyatı satış fiyatının altında',
            },
            unchanged: 'Değişmiyor',
        },
        title: 'Toplu Düzenleme',
        subtitle: 'Fiyat ve stoğu birçok üründe tek seferde değiştir — üstelik uygulamadan önce ne olacağını tam olarak gör.',
        whatToChange: 'Hangi Ürünler',
        channel: 'Kanal',
        pickChannel: 'Bir Kanal Seç',
        category: 'Kategori',
        allCategories: 'Tüm Kategoriler',
        anyStatus: 'Tüm Durumlar',
        searchLabel: 'Ara',
        theChange: 'Değişiklik',
        price: 'Fiyat',
        priceUnchanged: 'Değiştirme',
        priceSet: 'Şu Değere Ayarla',
        priceAdjust: '% Olarak Değiştir',
        priceAmount: 'Tutar Ekle veya Çıkar',
        priceValue: 'Değer',
        setStock: 'Stoğu Şuna Ayarla',
        stockValue: 'Miktar',
        preview: 'Önizle',
        whatWillHappen: 'Ne Olacak',
        willChange: (n: number) => `${n} ürün değişecek`,
        willSkip: (n: number) => `${n} atlanacak`,
        nothingMatches: 'Bu Filtreye Uyan Ürün Yok',
        priceColumn: 'Fiyat',
        skipped: 'Atlandı',
        sampleNote: (shown: number, total: number) => `${total} üründen ${shown} tanesi gösteriliyor`,
        apply: (n: number) => `${n} Ürüne Uygula`,
        applied: (n: number) => `${n} ürün güncellendi. Değişiklikler kanala gönderilmek üzere sıraya alındı.`,
    },

    nav: {
        support: 'Destek',
        menu: 'Menü',
        dashboard: 'Genel Bakış',
        products: 'Ürünler',
        orders: 'Siparişler',
        channels: 'Satış Kanalları',
        inventory: 'Stok',
        invoices: 'Faturalar',
        shipping: 'Kargo',
        claims: 'İadeler',
        customers: 'Müşteriler',
        warehouses: 'Depolar',
        pricing: 'Fiyatlandırma',
        bulkUpdate: 'Toplu Düzenleme',
        suppliers: 'Tedarikçiler',
        team: 'Ekip',
        storefrontSettings: 'Mağaza Ayarları',
        reviews: 'Yorumlar',
        account: 'Hesabım',
        subscription: 'Abonelik',
        questions: 'Sorular',
        webhooks: 'Webhook’lar',
        apiTokens: 'API Erişimi',
        notifications: 'Bildirimler',
        newsletter: 'Bülten',
        coupons: 'İndirim Kodları',
        sizeGuides: 'Beden Tabloları',
        promotions: 'Otomatik İndirimler',
        mapping: 'Eşleştirme',
        finance: 'Finans',
        settlements: 'Hakediş',
        profitability: 'Kârlılık',
        productPerformance: 'Ürün Performansı',
        categories: 'Kategoriler',
        brands: 'Markalar',
        brandMapping: 'Marka Eşleştirme',
        developer: 'Geliştirici',
        salesTrend: 'Satış Trendi',
    },

    navGroups: {
        catalog: 'Katalog',
        sales: 'Satış',
        storefront: 'Tezgah',
        channels: 'Kanallar',
    },

    finance: {
        title: 'Finans',
        subtitle: 'Pazaryerlerinin gerçekte ödediği tutar — satışlardan komisyon, ücret ve iadeler düşülmüş hâli.',
        fromDate: 'Başlangıç',
        toDate: 'Bitiş',
        clearDates: 'Tarihleri Temizle',
        period: (from: string, to: string, count: number) =>
            from && to
                ? `${from} – ${to} arasındaki ${count} işlem.`
                : from
                  ? `${from} tarihinden bu yana ${count} işlem.`
                  : to
                    ? `${to} tarihine kadar ${count} işlem.`
                    : `Tüm dönem: ${count} işlem.`,
        net: 'Net Ödeme',
        breakdown: 'Nasıl Hesaplandı',
        breakdownHint: 'Pazaryerlerinin ödediği ve kestiği tutarlar. Arkasındaki işlemler için bir satıra basın.',
        filtered: (bucket: string) => `Gösterilen işlem türü: ${bucket}. Diğer kalemler gizli.`,
        showAll: 'Tümünü Göster',
        netHint: 'Kesintiler sonrası hesabınıza geçen',
        bucket: {
            sale: 'Satışlar',
            commission: 'Komisyon',
            fee: 'Ücretler',
            return: 'İadeler',
            discount: 'İndirimler',
            other: 'Diğer',
        },
        /**
         * The marketplaces' own words for a movement, in Turkish.
         *
         * Keyed by the type lower-cased and stripped of everything but letters
         * and digits, because the marketplaces spell the same movement
         * `CustomerSatisfaction`, `CUSTOMER_SATISFACTION` and
         * `customer-satisfaction` between them. It is not the whole vocabulary
         * — one marketplace names eighty types — only the ones their own
         * documents name; anything else keeps the channel's word, which is
         * still better than nothing (EPZ-0074).
         */
        txType: {
            sale: 'Satış',
            payment: 'Ödeme',
            commission: 'Komisyon',
            commissionrefund: 'Komisyon İadesi',
            commissionagreementinvoice: 'Komisyon Sözleşme Faturası',
            return: 'İade',
            returninvoice: 'İade Faturası',
            manualrefund: 'Elle Yapılan İade',
            discount: 'İndirim',
            tydiscount: 'Pazaryeri İndirimi',
            campaigndiscount: 'Kampanya İndirimi',
            coupon: 'İndirim Kodu',
            tycoupon: 'Pazaryeri İndirim Kodu',
            provision: 'Provizyon',
            deliveryfee: 'Kargo Ücreti',
            deliveryprocessingfee: 'Kargo İşlem Ücreti',
            shipmentcostsharingexpense: 'Kargo Masraf Payı',
            customersatisfaction: 'Müşteri Memnuniyeti Kesintisi',
            stoppage: 'Stopaj',
            integrationfee: 'Pazaryeri Entegrasyon Bedeli',
            bnplorder: 'Sonra Öde Siparişi',
            paybylink: 'Link ile Ödeme',
            cashadvance: 'Nakit Avans',
            wiretransfer: 'Havale',
            incomingtransfer: 'Gelen Transfer',
            paymentorder: 'Ödeme Talimatı',
            deductioninvoices: 'Kesinti Faturaları',
            financialitem: 'Finansal Kalem',
            settlementdifference: 'Hakediş Farkı',
        },
        transactions: 'İşlemler',
        noTransactions: 'Henüz İşlem Yok',
        date: 'Tarih',
        type: 'Tür',
        order: 'Sipariş',
        amount: 'Tutar',
        empty: 'Henüz Mutabakat Verisi Yok',
        emptyHint: 'Bir pazaryeri bağlanıp finans bilgisi ilk kez güncellendiğinde burada görünür.',
        noChannelHint: 'Finans bilgisi pazaryerlerinden gelir. Önce bir pazaryeri bağlayın.',
    },

    profitability: {
        subtitle: 'Her satışın pazaryeri, KDV, ürün maliyeti ve kendi kargonuz düşüldükten sonra kazandırdığı.',
        groupBy: 'Gruplama',
        grouping: {
            order: 'Siparişe Göre',
            product: 'Ürüne Göre',
            channel: 'Satış Kanalına Göre',
        },
        channelFilter: 'Satış kanalı',
        allChannels: 'Tüm Satış Kanalları',
        rowFilter: 'Satırlar',
        allRows: 'Tüm Satırlar',
        lossRows: 'Yalnızca Zarar Edenler',
        clearFilters: 'Filtreleri Temizle',
        period: (from: string, to: string) => `${from} – ${to} arasında verilen siparişler.`,
        totals: {
            revenue: 'Satış',
            deductions: 'Kesintiler',
            goodsCost: 'Ürün Maliyeti',
            profit: 'Kâr',
            margin: 'Marj',
        },
        columns: {
            order: 'Sipariş',
            date: 'Tarih',
            channel: 'Satış Kanalı',
            product: 'Ürün',
            units: 'Adet',
            orders: 'Sipariş',
            revenue: 'Satış',
            deductions: 'Kesintiler',
            unattributed: 'Diğer Kesintiler',
            vat: 'KDV',
            goodsCost: 'Ürün Maliyeti',
            shipping: 'Kargo',
            profit: 'Kâr',
            margin: 'Marj',
        },
        pending: 'Hakediş Bekleniyor',
        costMissing: 'Maliyet Eksik',
        shippingUnpriced: 'Ücreti Bilinmiyor',
        unmatched: 'Ürünle eşleşmeyen satışlar',
        leftOut: (pending: number, missing: number) =>
            [pending > 0 ? `${pending} sipariş hakediş bekliyor` : '', missing > 0 ? `${missing} siparişte maliyet eksik` : '']
                .filter(Boolean)
                .join(' · '),
        pendingAlert: (count: number) => `${count} sipariş, pazaryeri ödediğini bildirene kadar toplamlara katılmıyor.`,
        costAlert: (count: number) =>
            `${count} sipariş, içindeki bir ürünün satıldığı anda maliyeti girilmemiş olduğu için toplamlara katılmıyor. Sonraki satışların hesaplanması için ürünlerinizin maliyetini girin.`,
        costAlertLink: 'Ürünlere Git',
        hint: 'Satış, pazaryerinin hakedişte bildirdiği ya da mağazanızda alıcının ödediği KDV dahil tutardır. Kesintiler pazaryerinin komisyonu, ücretleri, iadeleri ve indirimleridir. Kâr; KDV, satıldığı andaki maliyetiyle ürün ve kendi kargonuz düşüldükten sonra kalandır.',
        unattributedHint: 'Diğer kesintiler, pazaryerinin bir siparişe bağlamadan yaptığı kesintilerdir; stopaj gibi.',
        empty: 'Bu Dönemde Sipariş Yok',
        emptyHint: 'Başka bir dönem ya da satış kanalı seçin.',
        emptyLoss: 'Zararına Satış Yok',
        emptyLossHint: 'Bu dönemde zarar eden bir satır yok. Başka bir dönem seçin ya da tüm satırları gösterin.',
        count: (shown: number, total: number) => `${total} satırdan ${shown} tanesi`,
    },

    productPerformance: {
        subtitle: 'Satışlarınızı hangi ürünlerin getirdiği ve raftaki hangi stokun satmayı bıraktığı.',
        deadDays: 'Satışsız Süre',
        leadDays: 'Tedarik Süresi (Gün)',
        coverDays: 'Hedef Stok Süresi (Gün)',
        reorderNote: (lead: number, cover: number) =>
            `Önerilen sipariş son 30 günün satışına göre hesaplanır: şimdi verilen sipariş ${lead} günde gelir ve stok ondan sonra ${cover} gün yeter. Tedarikçi listesinden gelen varyantlar için öneri yapılmaz.`,
        days: (days: number) => `${days} gün`,
        clearFilters: 'Filtreleri Temizle',
        period: (from: string, to: string) => `${from} ile ${to} arasında verilen siparişlerdeki satışlar.`,
        deadPeriod: (days: number) => `Ölü stok, son ${days} günde satılmamış raftaki stoktur.`,
        view: 'Göster',
        filters: {
            all: 'Tümü',
            A: 'A',
            B: 'B',
            C: 'C',
            dead: 'Ölü Stok',
        },
        totals: {
            revenue: 'Satış',
            units: 'Satılan Adet',
            sold: 'Satılan Varyant',
            dead: 'Ölü Stok',
        },
        columns: {
            product: 'Ürün',
            units: 'Adet',
            revenue: 'Satış',
            share: 'Pay',
            class: 'Sınıf',
            stock: 'Stok',
            coverDays: 'Kaç Gün Yeter',
            suggestedOrder: 'Önerilen Sipariş',
            lastSold: 'Son Satış',
        },
        deadBadge: 'Ölü Stok',
        neverSold: 'Hiç satılmadı',
        hint: 'Varyantlar getirdikleri tutara göre, KDV dahil ve iptal edilen ya da iade gelen düşülerek sıralanır. A, satışlarınızın ilk %80’ini oluşturan varyantlar; B sonraki %15; C geri kalanıdır.',
        deadHint: 'Rafta stoku olup seçilen gün sayısı içinde satılmamış varyantlar; en uzun süredir satılmayan en üstte.',
        empty: 'Bu Dönemde Satış Yok',
        emptyHint: 'Başka bir dönem seçin.',
        deadEmpty: 'Ölü Stok Yok',
        deadEmptyHint: (days: number) => `Raftaki her varyant son ${days} günde satıldı.`,
        count: (shown: number, total: number) => `${total} satırdan ${shown} tanesi`,
    },

    salesTrend: {
        title: 'Satış Trendi',
        subtitle: 'Zaman içinde sipariş, adet ve ciro. Ciro, Genel Bakış’taki gibi satılı kalan tutardır: iptal ve iadeler düşülür.',
        bucket: 'Gruplama',
        buckets: {
            day: 'Günlük',
            week: 'Haftalık',
            month: 'Aylık',
        },
        channel: 'Satış Kanalı',
        allChannels: 'Tüm Satış Kanalları',
        measure: 'Grafik',
        measures: {
            revenue: 'Ciro',
            orders: 'Sipariş',
            units: 'Adet',
        },
        columns: {
            period: 'Dönem',
            orders: 'Sipariş',
            units: 'Adet',
            revenue: 'Ciro',
        },
        period: (from: string, to: string) => `${from} – ${to} arası. Bir hafta, pazartesi gününün tarihiyle gösterilir.`,
        summary: (measure: string, from: string, to: string) => `${from} – ${to} arası ${measure}.`,
        product: (title: string) => `Yalnızca bir ürün gösteriliyor: ${title}. Ciro, siparişlerin tamamı değil bu ürünün satırlarıdır.`,
        productUnnamed: 'Yalnızca bir ürün gösteriliyor. Ciro, siparişlerin tamamı değil bu ürünün satırlarıdır.',
        allProducts: 'Tüm Ürünleri Göster',
        empty: 'Bu Dönemde Sipariş Yok',
        emptyHint: 'Daha uzun bir dönem ya da başka bir satış kanalı seçin.',
    },

    mapping: {
        copy: {
            open: 'Başka Üründen Kopyala',
            title: 'Kanal Yanıtlarını Kopyala',
            hint: 'Aynı türden iki ürün aynı yanıtları alır. Kopyalama boşlukları doldurur, sizin girdiğiniz yanıtlara dokunmaz.',
            from: 'Şu Üründen',
            preview: 'Neyin Değişeceğini Gör',
            apply: 'Kopyala',
            plan: (copied: number, kept: number) =>
                kept > 0 ? `${copied} yanıt kopyalanacak. Farklı yanıtladığınız ${kept} tanesine dokunulmayacak.` : `${copied} yanıt kopyalanacak.`,
            withCategory: 'Pazaryeri kategorisi de kopyalanacak.',
        },
        channel: 'Kanal',
        subtitle: 'Kategorilerinizi kanalın kendi ağacına eşleyin — eşlenmeden ürün yayınlanamaz.',
        noChannel: 'Bağlı Pazaryeri Yok',
        noChannelHint: 'Eşleştirme için önce bir pazaryeri bağlayın.',
        mappedCategories: 'Eşlenen Kategoriler',
        mappableProducts: 'Yayınlanabilir Ürünler',
        mappableProductsHint: 'Kategorisi eşlenmiş ürünler',
        categories: 'Kategorileriniz',
        noCategories: 'Henüz Kategori Yok',
        editTitle: 'Kategoriyi Düzenle',
        editBody: 'Mağazadaki adresi ve ağaçtaki yeri aynı kalır.',
        categoryName: 'Kategori Adı',
        description: 'Açıklama',
        descriptionHint: 'En fazla 400 karakter. Mağazanızda kategori başlığının altında ve arama motorlarında görünür. Yalnızca düz metin.',
        ourCategory: 'Kategoriniz',
        channelCategory: 'Pazaryeri Kategorisi',
        unmapped: 'Eşlenmedi',
        map: 'Eşle',
        select: 'Seç',
        unmap: 'Kaldır',
        mapTitle: (name: string) => `${name} İçin Pazaryeri Kategorisi Seç`,
        searchPlaceholder: 'Pazaryeri kategorisi ara',
        searchHint: 'Aramak için en az iki harf yazın.',
        searchOtherHint: 'Aradığınız burada yoksa tüm kategorilerde aramak için en az iki harf yazın.',
        suggested: 'Önerilen Kategoriler',
        similarity: 'Benzerlik',
        noResults: 'Sonuç Yok',
        noResultsHint: 'Pazaryerinin kategorileri henüz güncellenmemiş olabilir.',
        matches: 'Bulunan kategoriler',
        filter: {
            channel: 'Hazır olduğu pazaryeri',
            // Worded as readiness, not as a place: beside the channel filter, "Tüm
            // pazaryerleri" read as a second way to pick where a product is sold.
            anyChannel: 'Pazaryeri Hazırlığı',
            readyFor: (marketplace: string) => `${marketplace} İçin Hazırlık`,
            state: 'Eşleştirme durumu',
            anyState: 'Tüm Durumlar',
            unmapped: 'Eşleştirilmemiş',
            incomplete: 'Bilgisi Eksik',
            ready: 'Gönderime Hazır',
        },
        bulk: {
            setCategory: 'Kategori Ata',
            setCategoryHint: (count: number) => `Seçili ${count} ürün bu kategoriye taşınacak.`,
            noCategory: 'Kategorisiz',
            setChannelCategory: 'Pazaryeri Kategorisi Ata',
            setChannelCategoryHint: (count: number) =>
                `Seçili ${count} ürün, kendi kategorisinin eşleştirmesinden bağımsız olarak bu pazaryeri kategorisine gönderilecek.`,
            apply: 'Uygula',
            overwrite: 'Ürün Bazlı Mevcut Seçimleri Değiştir',
            overwriteHint: 'Varsayılan olarak kapalıdır; tek bir ürün için yapılmış seçim toplu işlemle ezilmez.',
            wouldOverwrite: (count: number) => `Bu ürünlerden ${count} tanesinin kendine ait bir pazaryeri kategorisi var.`,
            mapDone: (mapped: number, skipped: number) =>
                skipped > 0 ? `${mapped} ürün eşleştirildi, ${skipped} ürün mevcut seçimiyle bırakıldı.` : `${mapped} ürün eşleştirildi.`,
        },
        scope: {
            product: 'Yalnızca Bu Ürün',
            category: 'Kategorisinden Geliyor',
            productHint: 'Bu ürün, kategorisindeki diğer ürünlerden farklı bir pazaryeri kategorisine gönderilir.',
            categoryHint: 'Bu kategorinin eşleştirmesinden geliyor. Değiştirmek kategorideki tüm ürünleri taşır.',
            override: 'Bu Ürün İçin Farklı Bir Kategori Seç',
            clearOverride: 'Yeniden Kategori Eşleştirmesini Kullan',
        },
        stale: {
            title: 'Kontrol Edilmesi Gereken Eşleştirmeler',
            description: 'Pazaryeri, bu eşleştirmeler yapıldıktan sonra kategorilerini değiştirdi.',
            missing: 'Kategori Artık Mevcut Değil',
            notLeaf: 'Kategori Alt Kategori Kazandı, Ürün Alamıyor',
            unavailable: 'Kategori Yeni Ürüne Kapalı',
            outdated: 'Ağacın Daha Eski Bir Sürümüne Göre Yapılmış',
        },
        import: {
            title: 'Katalog İçe Aktarma',
            description: 'Bu pazaryerinde hâlihazırda sattığınız ürünleri Tezgah\u2019a aktarın.',
            run: 'Ürünleri İçe Aktar',
            running: 'İçe Aktarılıyor…',
            never: 'Henüz içe aktarılmadı',
            lastRun: (imported: number, created: number, matched: number) =>
                `${imported} ürün · ${created} yeni · ${matched} tanesi başka pazaryerinden tanındı`,
            pending: (count: number) => `${count} ürün için, hâlihazırda sattığınız bir ürün olup olmadığını belirtmeniz gerekiyor.`,
            overLimit: (count: number) =>
                `${count} ürün, paketinize dahil ürün sınırı nedeniyle eklenmedi. Eşleşen ürünleriniz güncellenmeye devam eder.`,
            pendingLink: 'İncele',
            failed: (count: number) =>
                `${count} ürün içe aktarılamadı. Teknik ayrıntı sunucu kaydında; taramayı tekrar çalıştırmak bunları yeniden dener.`,
            queued: 'İçe aktarma sıraya alındı. Arka planda çalışır ve biraz sürebilir.',
            alreadyRunning: 'Zaten devam eden bir içe aktarma var.',
        },
    },

    matches: {
        subtitle: 'Hâlihazırda sattığınız ürünlerden ayırt edemediğimiz pazaryeri ürünleri. Siz onaylamadan hiçbir kayıt birleştirilmez.',
        empty: 'Bekleyen Kayıt Yok',
        emptyHint: 'İçe aktarılan her ürün ya tanındı ya da yeni eklendi. Emin olamadığımız kayıtlar burada görünür.',
        failed: 'Karar kaydedilemedi. Daha önce verilmiş olabilir.',
        channelId: 'Pazaryeri Kimliği',
        barcodes: 'Barkodlar',
        stockCodes: 'Stok Kodları',
        candidates: (count: number) => `Kataloğunuzda eşleşebilecek ${count} ürün`,
        link: 'Bu Ürünle Aynı',
        separate: 'Ayrı Ürün Olarak Ekle',
        confirmLinkTitle: 'Kayıtlar İlişkilendirilecek',
        confirmLinkBody: (incoming: string, existing: string) =>
            `"${incoming}", "${existing}" ürününün pazaryerindeki karşılığı olarak kabul edilecek. Kendi başlığınız, açıklamanız ve kategoriniz olduğu gibi kalır; yalnızca pazaryeri kaydı bağlanır.`,
        reason: {
            ambiguous: 'Bu kayda birden fazla ürününüz uyuyor. Hangisi olduğunu belirleyen bir bilgi yok.',
            conflict: 'Tanımlayıcılar çelişiyor: stok kodu bir ürünle eşleşiyor, barkod başka bir yeri gösteriyor.',
        },
        evidence: {
            barcode: (codes: string) => `Aynı Barkod: ${codes}`,
            stockCode: (codes: string) => `Aynı Stok Kodu: ${codes}`,
            title: 'Aynı Ad',
        },
    },

    brandMapping: {
        subtitle:
            'Markalarınızı her pazaryerinin markalarıyla eşleştirin. Ürün, o kanal için kendine özel bir marka seçilmedikçe burada eşleştirilen markayla gönderilir.',
        listTitle: 'Markalarınız',
        noBrands: 'Henüz Marka Yok',
        noBrandsHint: 'Markalar içe aktardığınız ürünlerle gelir ya da bir ürünün Marka alanından eklenir.',
        noRows: 'Bu Durumda Marka Yok',
        count: (shown: number, total: number) => `${total} markadan ${shown} tanesi`,
        columns: {
            brand: 'Markanız',
            counterpart: 'Pazaryeri Markası',
            state: 'Durum',
            source: 'Kaynak',
        },
        filter: 'Durum',
        anyState: 'Tüm Durumlar',
        state: {
            mapped: 'Eşleştirildi',
            suggested: 'Öneri Var',
            missing: 'Eşleştirilmedi',
            retired: 'Pazaryerinde Yok',
        },
        source: {
            imported: 'İçe Aktarmadan',
            matched: 'Otomatik Eşleşti',
            suggested: 'Öneriden',
            manual: 'Sizin Seçiminiz',
            name: 'Markanın Adı',
        },
        suggestion: (name: string, score: string) => `Öneri: ${name} (${score})`,
        accept: 'Kabul Et',
        change: 'Değiştir',
        lastRefresh: (when: string) => `Son güncelleme ${when}`,
        neverRefreshed: 'Bu pazaryerinin marka listesi henüz alınmadı. Liste her gece güncellenir; o zamana kadar arama listede bir şey bulmaz.',
        refreshing: 'Liste şu anda güncelleniyor.',
        refreshFailed: 'Listenin son güncellemesi tamamlanamadı. Kayıtlı liste kullanılmaya devam ediyor ve yayınlar beklemez.',
        textChannel: (channel: string) =>
            `${channel}, markayı ürünle birlikte metin olarak alır ve kendi incelemesinden geçirir. Markalarınız, eşleştirdiğiniz kategorilerin marka listeleriyle karşılaştırılır; listesi olmayan bir kategoride marka, eşleştirilen yazımıyla ya da markanın kendi adıyla metin olarak gider. Entegrasyon üzerinden marka talebi almaz: bir markayı kısıtlarsa ya da bir kategori yalnızca kendi listesindeki markaları kabul ederse ${channel} satıcı panelinden ya da oradaki kategori yöneticinizden talepte bulunun.`,
        noRequest: (channel: string) =>
            `${channel}, entegrasyon üzerinden marka talebi almaz. Listesinde olmayan bir marka için ${channel} satıcı panelinden talepte bulunun.`,
        acceptAll: 'Önerileri Kabul Et',
        acceptTitle: 'Tam Eşleşen Öneriler Kabul Edilecek',
        acceptCount: (count: number) =>
            `${count} marka, adı birebir aynı olan pazaryeri markasıyla eşleştirilecek. Eşleştirilmiş markalara dokunulmaz.`,
        acceptNone: 'Bu listede bekleyen tam eşleşen öneri yok.',
        acceptDone: (count: number) => `${count} marka eşleştirildi.`,
        mapTitle: (brand: string) => `${brand} İçin Pazaryeri Markası Seç`,
        searchPlaceholder: 'Pazaryeri markası ara',
        suggested: 'Önerilen Markalar',
        noResultsDirectory: 'Pazaryerinin listesi henüz alınmamış olabilir ya da marka listede yok.',
        noResultsText: 'Yalnızca eşleştirdiğiniz kategorilerin marka listelerinde aranır. Adı yazdığınız gibi gönderebilirsiniz.',
        scopeCategory: 'Ürünün kategorisinin marka listesinden.',
        scopeFallback: 'Ürünün kategorisinin marka listesinde yok; eşleştirdiğiniz diğer kategorilerin listelerinden.',
        scopeChannel: 'Eşleştirdiğiniz kategorilerin marka listelerinden.',
        useTyped: (name: string) => `“${name}” Olarak Gönder`,
        typedHint: 'Pazaryeri markayı metin olarak alır; hiçbir listede olmayan bir ad yazıldığı gibi gönderilir.',
        requestBrand: 'Marka Talep Et',
        requestDescription: (channel: string) =>
            `${channel} talebi görsellere göre değerlendirir; kabul ederse markayı hemen listesine ekler. Gönderilen talep geri alınamaz.`,
        requestFor: (brand: string) => `Pazaryeri markayı oluşturduğunda ${brand} markanız onunla eşleştirilir.`,
        connection: 'Satış Kanalı',
        nameHint: 'Pazaryerinde görünmesini istediğiniz şekliyle.',
        images: 'Marka Görselleri',
        imagesHint: 'En az bir, en fazla üç PNG ya da JPEG görsel, her biri en fazla 1 MB: logo ve markanın ürün ya da ambalaj üzerindeki görüntüsü.',
        addImages: 'Görsel Seç',
        removeImage: (name: string) => `${name} Görselini Kaldır`,
        send: 'Talebi Gönder',
        // Trendyol answered with the brand's id at once and found it by name about a
        // minute later, with no approval state (channels/trendyol.md §5).
        sent: (name: string, channel: string) =>
            `Talep gönderildi: ${name}. ${channel} markayı hemen kaydetti; marka genellikle birkaç dakika içinde kullanılabilir. ${channel} bir yayında yine de reddederse nedeni ürünün kanal kartında görünür.`,
    },

    companyBrands: {
        subtitle: 'Şirketinizin markaları: ürünleriniz, mağazanız ve pazaryerleri bu markaları kullanır.',
        add: 'Yeni Marka',
        edit: 'Markayı Düzenle',
        search: 'Marka ara',
        empty: 'Henüz Marka Yok',
        emptyHint: 'Buradan marka ekleyin; markalar içe aktardığınız ürünlerle de gelir.',
        noMatch: 'Aramayla Eşleşen Marka Yok',
        count: (shown: number, total: number) => `${total} markadan ${shown} tanesi`,
        columns: {
            brand: 'Marka',
            products: 'Ürünler',
            shop: 'Mağaza',
            marketplaces: 'Pazaryerleri',
        },
        products: (count: number) => `${count} ürün`,
        shown: 'Mağazada',
        hidden: 'Gizli',
        marketplaceState: (channel: string, state: string) => `${channel}: ${state}`,
        name: 'Ad',
        slug: 'Mağaza Adresi',
        slugHint: 'Mağazanız markayı /marka/ ve bu adresle gösterir. Boş bırakırsanız addan oluşturulur.',
        description: 'Açıklama',
        descriptionHint: 'En fazla 400 karakter düz metin; mağazanızda markanın başlığının altında görünür.',
        logo: 'Logo',
        logoChoose: 'Logo Seç',
        logoHint: 'PNG, JPEG veya WebP; en az 300 piksel.',
        logoUploading: 'Yükleniyor…',
        logoRemove: 'Logoyu Kaldır',
        showInShop: 'Mağazada Göster',
        showInShopHint: 'Markanın mağazanızda bir sayfası olur ve ürünleri bu sayfaya bağlanır.',
        save: 'Kaydet',
        manage: 'Birleştir veya Sil',
        merge: 'Birleştir',
        mergeTitle: (name: string) => `${name} Markasını Birleştir`,
        mergeInto: 'Kalacak Marka',
        mergeIntoHint: 'Markanın ürünleri seçtiğiniz markaya taşınır; o marka pazaryeri eşleştirmelerini korur. Ardından bu marka silinir.',
        mergePick: 'Marka Seçin',
        noOtherBrand: 'Bu markayı birleştirebileceğiniz başka bir marka yok.',
        confirmMerge: {
            title: 'Markalar Birleştirilecek',
            body: (from: string, into: string, count: number) =>
                `${from} markasının ${count} ürünü ${into} markasına taşınacak. ${from}, pazaryeri eşleştirmeleriyle birlikte silinecek; ${into} kendi eşleştirmelerini koruyacak.`,
            action: 'Birleştir',
        },
        merged: (from: string, into: string, count: number) => `${from}, ${into} markasıyla birleştirildi; ${count} ürün taşındı.`,
        delete: 'Sil',
        inUse: (count: number) => `${count} ürün bu markayı kullandığı için silinemez. Markayı başka bir markayla birleştirin.`,
        confirmDelete: {
            title: 'Marka Silinecek',
            body: (name: string) => `${name}, pazaryeri eşleştirmeleriyle birlikte silinecek.`,
            action: 'Sil',
        },
        deleted: (name: string) => `${name} silindi.`,
    },

    sources: {
        title: 'Pazaryeri Bağlantıları',
        subtitle:
            'Bu ürünü hangi pazaryerleri, hangi kimlikle tanıyor. Bir kimlik yalnızca kendi bağlantısı içinde anlamlıdır; bu ürüne ait olmayan bir kaydı Eşleştirmeyi Kaldır ile ayırın.',
        unlink: 'Eşleştirmeyi Kaldır',
        unlinkTitle: 'Eşleştirme Kaldırılacak',
        unlinkBody: (channel: string) =>
            `${channel} üzerindeki ürün bu kayıttan ayrılacak ve bir sonraki içe aktarmada kendi ürünü olarak eklenecek. Düzeltme hatırlanır, yeniden eşleştirilmez.`,
    },

    products: {
        moreActions: 'Diğer İşlemler',
        addVariant: {
            open: 'Varyant Ekle',
            title: 'Varyant Ekle',
            description:
                'Üründe hâlihazırda bulunan her şey kimliğini korur; siparişler, listeler ve stok bağlı kalır. Bir varyant özelliğinde henüz bulunmayan bir değer de yazabilirsiniz; o özelliğe eklenir.',
            axisHint: 'Mevcut bir değeri seçin ya da yeni bir değer yazın.',
            submit: 'Ekle',
        },
        title: 'Ürünler',
        subtitle: 'Tüm satış kanallarındaki ürün kataloğunuz.',
        add: 'Ürün Ekle',
        distribute: {
            button: 'Kanala Ekle',
            title: 'Ürünleri Kanala Ekle',
            subtitle: (count: number) =>
                `Listede görünen ${count} ürün seçtiğiniz kanala eklenir. O kanalda zaten olan bir ürünün oradaki fiyatı ve stoğu değişmez; kaynaktan bir şey silinmez.`,
            target: 'Hedef Kanal',
            source: 'Fiyat Kaynağı',
            sourceHint: 'Yeni ilanların başlangıç fiyat ve stoğu bu kanaldan kopyalanır. Sonrasında her kanal bağımsızdır.',
            pick: 'Seçin…',
            productPrice: 'Ürün Fiyatı',
            productPriceHint:
                'Her ilan ürünün fiyatıyla açılır ve ürün fiyatı değiştikçe onunla birlikte değişir. Fiyatı olmayan ya da bu kanalın satış yapmadığı para biriminde fiyatlanan ürünler atlanır.',
            adjust: 'Fiyat Farkı (%)',
            adjustHint: 'Kaynak fiyata uygulanır: 10 yüzde on artırır, -5 azaltır.',
            flatPrice: 'Sabit Fiyat',
            flatPriceHint: 'Kaynakta fiyatı olmayan ürünler için başlangıç fiyatı. Kaynak seçilmediyse her ürüne uygulanır.',
            defaultQuantity: 'Varsayılan Stok',
            publish: 'Hemen Yayınla',
            submit: 'Ekle',
            done: (created: number, existing: number, skipped: number) =>
                [
                    `${created} ürün eklendi.`,
                    existing > 0 ? `${existing} ürün bu kanalda zaten vardı, olduğu gibi bırakıldı.` : '',
                    skipped > 0 ? `${skipped} ürün atlandı (kaynakta fiyatı yok).` : '',
                ]
                    .filter(Boolean)
                    .join(' '),
            doneFollowing: (created: number, existing: number, skipped: number) =>
                [
                    `${created} ürün eklendi.`,
                    existing > 0 ? `${existing} ürün bu kanalda zaten vardı, olduğu gibi bırakıldı.` : '',
                    skipped > 0 ? `${skipped} ürün atlandı (ürün fiyatı yok ya da bu kanal o para biriminde satış yapmıyor).` : '',
                ]
                    .filter(Boolean)
                    .join(' '),
        },
        publishSelection: {
            button: 'Kanalda Yayınla',
            title: 'Seçilenleri Kanalda Yayınla',
            subtitle: (count: string) =>
                `Seçilen ürün: ${count}. Her biri hedef kanalda kayıtlı fiyatı ve stoğuyla yayınlanır; hiçbir fiyat değiştirilmez.`,
            target: 'Hedef Kanal',
            pick: 'Seçin…',
            submit: 'Yayınla',
            retry: 'Başarısızları Yeniden Dene',
            nothing: 'Seçilenlerden bu kanala gönderilmeyi bekleyen bir şey yok.',
            outcome: {
                creates: 'İlk Kez Satışa Çıkacak',
                updates: 'Bekleyen Değişikliği Gidecek',
                current: 'Zaten Yayında',
                sending: 'Gönderiliyor',
                unlisted: 'Bu Kanalda Yok',
                not_active: 'Ürün Aktif Değil',
                no_price: 'Fiyatı Yok',
                currency: 'Para Birimi Satılmıyor',
                in_review: 'Onay Bekliyor',
                withdrawn: 'Satıştan Çekildi',
                held: 'Kanalı Bekliyor',
            },
            hint: {
                current: 'Bu kanalda kayıtlı hâliyle duruyor; gönderilecek bir değişikliği yok.',
                sending: 'Gönderimi sürüyor; ikinci kez gönderilmez.',
                unlisted: 'Önce Kanala Ekle ile bu kanala ekleyin.',
                not_active: 'Aktif olmayan bir ürün hiçbir satış kanalına ilk kez gönderilmez. Ürünler sayfasında Aktifleştir ile aktifleştirin.',
                no_price: 'Bir varyantının bu kanalda fiyatı yok. Fiyatı ürünün Kanallar sekmesinde girin.',
                currency: 'Bu kanalın satmadığı bir para biriminde fiyatlanmış.',
                in_review: 'Kanal ürünü inceliyor; karar gelene kadar bir şey gönderilmez.',
                withdrawn: 'Bu kanalda satıştan çekildi. Ürünün sayfasından yeniden satışa açın.',
                held: 'Kanal şu an daha fazlasını almıyor. Değişiklik birkaç dakika içinde kendiliğinden gider.',
            },
            hintCompletable: {
                unlisted: 'Aşağıdan bu kanala ekleyebilirsiniz.',
                not_active: 'Aktif olmayan bir ürün hiçbir satış kanalına ilk kez gönderilmez. Aşağıdan aktifleştirebilirsiniz.',
                no_price: 'Bir varyantının bu kanalda fiyatı yok. Fiyatı aşağıdan girebilirsiniz.',
            },
            activate: {
                title: 'Aktif Olmayan Ürünler',
                hint: (count: string) =>
                    `Aktif olmayan ürün: ${count}. Aktifleştirilen ürünler satış kanallarına gönderilebilir; yayını siz başlatana kadar hiçbir şey gönderilmez.`,
                submit: 'Aktifleştir',
                done: (count: string) => `Aktifleştirilen ürün: ${count}.`,
            },
            add: {
                title: 'Bu Kanala Ekle',
                hint: (count: string) =>
                    `Bu kanalda olmayan ürün: ${count}. Fiyatın nereden başlayacağını seçin. Eklenenler yayınlanacaklar arasına geçer; yayını siz başlatırsınız.`,
                submit: 'Kanala Ekle',
            },
            price: {
                title: 'Eksik Fiyatlar',
                hint: 'Bu varyantların bu kanalda fiyatı yok. Girdiğiniz fiyat bu kanala özeldir; ürün fiyatını izlemez.',
                label: (sku: string) => `${sku} Satış Fiyatı`,
                submit: 'Fiyatları Kaydet',
                done: (count: string) => `Fiyatı kaydedilen varyant: ${count}.`,
                refused: (sku: string, reason: string) => `${sku}: ${reason}`,
            },
            more: (count: string) => `ve ${count} ürün daha`,
            failure: (sku: string, reason: string) => `${sku}: ${reason}`,
            sent: (count: string) => `Kanala gönderilen ürün: ${count}.`,
            batches: 'Gönderimler',
            batch: (state: string, count: string) => `${state} · ${count}`,
            published: 'Yayında',
            failed: 'Gönderilemedi',
            failedHint: 'Nedeni her ürünün yanında. Düzeltip yalnız başarısız olanları yeniden gönderin.',
        },
        search: 'İsim veya koda göre ara',
        empty: 'Henüz Ürün Yok',
        emptyHint: 'Satışa başlamak için ilk ürününüzü ekleyin.',
        noMatches: 'Aramanızla Eşleşen Ürün Bulunamadı',
        count: (shown: number, total: number) => `${total} üründen ${shown} tanesi`,

        columns: {
            mpn: 'MPN',
            product: 'Ürün',
            code: 'Kod',
            variants: 'Varyant',
            channels: 'Kanallar',
            status: 'Durum',
            updated: 'Güncellenme',
            barcode: 'Barkod',
            cost: 'Maliyet',
            desi: 'Desi',
            weightGram: 'Ağırlık (g)',
            stock: 'Stok',
            listPrice: 'Liste Fiyatı',
            salePrice: 'Satış Fiyatı',
        },

        status: {
            draft: 'Taslak',
            active: 'Aktif',
            archived: 'Pasif',
            all: 'Tüm Durumlar',
        },

        filter: {
            channel: 'Kanal',
            allChannels: 'Tüm Kanallar',
            unlisted: 'Hiçbir Kanalda Değil',
            channelStatus: 'Kanaldaki Durum',
            anyChannelStatus: 'Kanalda Tüm Durumlar',
        },

        channelState: {
            offer: (channel: string, state: string) => `${channel}: ${state}`,
            notSelling: (channel: string, state: string) => `${channel}: ${state}, satışta değil`,
        },

        variantCount: (count: number) => `${count} varyant`,
        publish: 'Aktifleştir',
        archive: 'Pasife Al',
        salesTrend: 'Satış Trendini Gör',
        delete: 'Sil',
        templates: {
            choose: 'Şablon Seç',
            chooseHint: 'Şablonun metni açıklamaya yazılır; ardından bu ürüne göre düzenleyebilirsiniz.',
            use: 'Kullan',
            useLabel: (name: string) => `${name} Şablonunu Kullan`,
            delete: 'Sil',
            deleteLabel: (name: string) => `${name} Şablonunu Sil`,
            empty: 'Kayıtlı şablon yok',
            emptyHint: 'Bir açıklama yazıp Şablon Olarak Kaydet ile saklayın; sonraki ürünlerde buradan seçebilirsiniz.',
            save: 'Şablon Olarak Kaydet',
            saveHint: 'Editördeki açıklama bu adla firmanızın şablonlarına eklenir.',
            name: 'Şablon Adı',
            confirmReplace: {
                title: 'Açıklama Değiştirilecek',
                body: (name: string) => `Editördeki metnin yerine “${name}” şablonu yazılır.`,
                action: 'Şablonu Kullan',
            },
            confirmDelete: {
                title: 'Şablon Silinecek',
                body: (name: string) => `“${name}” şablonu silinir. Bu şablonla başlayan ürünlerin açıklaması değişmez.`,
                action: 'Sil',
            },
        },
        duplicate: {
            action: 'Kopyala',
            title: 'Ürünü Kopyala',
            hint: 'Bu ürünün açıklaması, kategorisi, markası, KDV oranı, varyantları ve özellikleriyle yeni bir taslak ürün oluşturulur. Görseller, barkodlar, stok ve satış kanalları kopyalanmaz.',
            noPrices: 'Fiyatlar da kopyalanmaz.',
            submit: 'Kopyala',
        },
        confirmDelete: {
            title: 'Ürün Silinecek',
            body: 'Katalogdan kalkar ve kodu tekrar kullanılabilir. Geçmiş siparişler korunur.',
            action: 'Sil',
        },
        confirmBulkDelete: {
            title: 'Ürünler Silinecek',
            body: (count: number) =>
                `${count} ürün katalogdan kalkar ve bulunduğu her kanaldan çekilir. Geçmiş siparişler korunur. Bu işlem buradan geri alınamaz.`,
            action: 'Sil',
        },
        tabs: {
            general: 'Genel',
            images: 'Görseller',
            stock: 'Stok ve Fiyat',
            channels: 'Kanallar',
            variants: 'Varyantlar',
        },
        basics: 'Temel Bilgiler',
        editBasics: 'Temel Bilgileri Düzenle',
        editBasicsHint: 'Ad, açıklama, kategori, marka ve KDV. Ürün kodu ve varyantlar kendi sekmelerinde.',
        brand: 'Marka',
        noBrand: 'Marka Yok',
        addBrand: 'Yeni Marka',
        editor: {
            editor: 'Ürün Açıklaması',
            block: 'Yazı Biçimi',
            paragraph: 'Paragraf',
            heading2: 'Başlık',
            heading3: 'Alt Başlık',
            bold: 'Kalın',
            italic: 'İtalik',
            underline: 'Altı Çizili',
            strike: 'Üstü Çizili',
            bulletList: 'Madde İşaretli Liste',
            orderedList: 'Numaralı Liste',
            quote: 'Alıntı',
            link: 'Bağlantı Ekle',
            linkPrompt: 'Bağlanacak adres (kaldırmak için boş bırakın)',
            linkApply: 'Uygula',
            linkCancel: 'Vazgeç',
            source: 'HTML',
        },
        brandSearch: 'Marka ara',
        brandSearchHint:
            'Markalarınızda ve bağlı pazaryerlerinin marka listelerinde arar. Bir pazaryerinin markasını seçtiğinizde marka firmanıza eklenir ve o pazaryerinin markasıyla eşleştirilir.',
        brandOwn: 'Markalarınız',
        brandMarketplace: 'Pazaryeri',
        brandNotFound: 'Marka bulunamadı. Hiçbir listede olmayan bir markayı Yeni Marka ile ekleyin.',
        brandChosen: (name: string) => `Seçili marka: ${name}`,
        clearBrand: 'Markayı Kaldır',
        addBrandNote: 'Marka yalnızca firmanıza eklenir; pazaryerine bir şey gönderilmez.',
        addBrandRequestable: (channels: string) =>
            `${channels}: listesinde olmayan bir markayı Eşleştirme, Marka Eşleştirme sekmesinden talep edebilirsiniz.`,
        addBrandText: (channels: string) => `${channels}: marka ürünle birlikte metin olarak gider ve pazaryerinin kendi incelemesinden geçer.`,
        addBrandAsk: (channels: string) => `${channels}: listesinde olmayan bir marka için pazaryerinin satıcı panelinden talepte bulunun.`,
        addBrandRequestHere: (channels: string) =>
            `${channels}: listesinde olmayan bir markayı, marka eklendikten sonra buradan talep edebilirsiniz.`,
        brandAdded: (name: string) => `${name} firmanıza eklendi ve ürünün markası olarak seçildi.`,
        requestOffer: (channel: string) => `${channel}: listesinde bu marka yoksa talep edebilirsiniz.`,
        requestBrandOn: (channel: string) => `${channel} İçin Marka Talep Et`,
        publishDrawer: {
            title: 'Yeniden Satışa Aç',
            subtitle: 'Ürün burada satışa açılır; işaretlediğiniz kanallarda pazaryeri tarafında da yeniden satışa açılır.',
            channels: 'Yeniden Açılacak Kanallar',
            noChannels: 'Bu ürün henüz hiçbir kanalda yok. Bir kanala çıkarmak için Kanallar sekmesinden yayınlayın.',
        },
        brandName: 'Marka Adı',
        noCategory: 'Kategori Yok',
        basicsSaved: 'Kaydedildi.',
        noDescription: 'Açıklama girilmedi.',
        summary: 'Özet',
        imagesCount: 'Görsel',
        imagesCountPending: (stored: number, pending: number) => `${stored} · ${pending} işleniyor`,
        blockers: 'Eksikler',
        noBlockers: 'Eksik bir şey yok.',
        blocker: {
            draft: 'Taslak, Henüz Aktifleştirilmedi',
            noImage: 'Görsel Yüklenmedi',
            imagesPending: (count: number) => `Görseller İşleniyor (${count})`,
            noDescription: 'Açıklama Eklenmedi',
        },
        addDescription: 'Açıklama Ekle',

        form: {
            title: 'Yeni Ürün',
            subtitle: 'Varyant özelliklerini tanımlayın, kombinasyonlar sizin için üretilsin.',
            sku: 'Ürün Kodu',
            skuHint: 'Kendi referansınız. Varyant kodları bundan türetilir.',
            name: 'Ürün Adı',
            description: 'Açıklama',
            taxRate: 'KDV Oranı',
            category: 'Kategori',
            axes: 'Varyant Özellikleri',
            axesHint: 'Renk, beden gibi; değerleri virgülle ayırın. Varyantı olmayan ürün için boş bırakın.',
            axisName: 'Özellik Adı',
            axisValues: 'Özellik Değerleri',
            axisNameHint: 'Renk',
            axisValuesHint: 'Kırmızı, Mavi, Siyah',
            addAxis: 'Özellik Ekle',
            removeAxis: 'Kaldır',
            preview: 'Oluşturulacak Varyantlar',
            previewCount: (count: number) => `${count} varyant oluşturulacak`,
            tooMany: 'Bu, bir ürünün sahip olabileceğinden fazla varyant kombinasyonu.',
            prices: 'Fiyat ve Stok',
            pricesHint:
                'Türk lirası cinsinden. Her satış kanalı bu fiyatı kullanabilir ya da kendi fiyatını alabilir; para birimi Stok ve Fiyat sekmesinden değiştirilir.',
            variant: 'Varyant',
            salePrice: 'Satış Fiyatı',
            listPrice: 'Liste Fiyatı',
            openingStock: 'Açılış Stoğu',
            submit: 'Ürünü Oluştur',
        },
    },

    channels: {
        title: 'Satış Kanalları',
        subtitle: (ownStore: string) => `Pazaryerleri ve ${ownStore}, tek yerden bağlı.`,
        connected: 'Bağlı Kanallar',
        available: 'Eklenebilir Kanallar',
        connect: 'Bağlan',
        empty: 'Henüz Kanal Bağlamadınız',
        emptyHint: 'Satışa başlamak için bir kanal bağlayın.',

        status: {
            pending: 'Kontrol Edilmedi',
            connected: 'Bağlı',
            failed: 'Bağlantı Sorunu',
            disabled: 'Kapalı',
        },

        problem: {
            auth: 'Mağaza bilgileri kabul edilmedi. Bilgileri kontrol edip yeniden deneyin.',
            credentials_lost: 'Kayıtlı bağlantı bilgileri artık okunamıyor. Ayarlar sekmesinden bilgileri yeniden girin.',
            sandbox_credentials:
                'Bu bilgiler pazaryerinin test ortamına ait. Bu bağlantı canlı ortamda çalışır; pazaryerinin satıcı panelindeki canlı API bilgilerini girin.',
            validation: 'Pazaryeri gönderilen bilgileri kabul etmedi. Bilgileri kontrol edip yeniden deneyin.',
            conflict: 'Kayıt bu arada pazaryerinde değişmiş. Sayfayı yenileyip yeniden deneyin.',
            not_found: 'Pazaryeri bu kaydı bulamadı.',
            deprecated: 'Pazaryeri bu işlemi artık desteklemiyor. Destek ekibine yazın.',
            rate_limited: 'Pazaryeri çok fazla istek aldı.',
            transient: 'Pazaryerine şu anda ulaşılamıyor.',
            channel_down: 'Pazaryerine şu anda ulaşılamıyor.',
            unknown: 'Beklenmeyen bir sorun oluştu.',
        },
        problemRetry: {
            automatic: 'Kendiliğinden yeniden denenecek.',
            manual: 'Birkaç dakika sonra yeniden deneyin.',
        },
        events: {
            created: 'Bağlantı eklendi',
            updated: 'Bağlantı bilgileri değiştirildi',
            verified: 'Bağlantı kontrol edildi',
            failed: 'Bağlantı kontrolünde sorun bulundu',
            disabled: 'Bağlantı kapatıldı',
        },

        kind: {
            marketplace: 'Pazaryeri',
            own_store: 'Kendi Siteniz',
            b2b: 'B2B',
        },

        environment: {
            label: 'Ortam',
            sandbox: 'Test',
            production: 'Canlı',
            sandboxNote: 'Canlı mağaza bilgileri bu ortamda kabul edilmez.',
            sandboxForm: (channel: string) => `Bu kurulum ${channel} için test ortamına bağlanır. Canlı mağaza bilgileri burada kabul edilmez.`,
        },

        feeds: {
            title: 'Güncelleme Durumu',
            orders: 'Siparişler',
            products: 'Ürünler',
            claims: 'İadeler',
            settlements: 'Finans',
        },

        verify: 'Bağlantıyı Kontrol Et',
        verifiedAt: (when: string) => `Son kontrol ${when}`,
        neverVerified: 'Henüz kontrol edilmedi',
        sync: {
            now: 'Şimdi Güncelle',
            never: 'Siparişler henüz güncellenmedi',
            neverShort: 'Henüz güncellenmedi',
            lastSynced: (when: string) => `Siparişler güncellendi ${when}`,
        },
        disconnect: 'Bağlantıyı Kes',
        confirmDisconnect: {
            title: 'Kanal Bağlantısı Kesilecek',
            body: 'Bu hesaptan sipariş ve ürün güncellemeleri durur. İlanlar kanalda kalır.',
            action: 'Bağlantıyı Kes',
        },
        active: 'Aktif',
        passive: 'Pasif',
        activate: 'Aktif Yap',
        deactivate: 'Pasif Yap',
        manageShop: 'Mağaza Ayarları',
        setUpShop: 'Mağazayı Kur',
        edit: 'Düzenle',
        add: 'Kanal Ekle',
        syncAll: 'Tümünü Güncelle',
        manage: 'Yönet',
        connectedShort: 'Bağlı',
        searchPlaceholder: 'Kanal ara',
        summary: (count: number) => (count === 1 ? '1 bağlı kanal' : `${count} bağlı kanal`),
        filters: {
            all: 'Tümü',
            active: 'Aktif',
            broken: 'Sorunlu',
        },
        columns: {
            channel: 'Kanal',
            status: 'Durum',
            lastSync: 'Son Güncelleme',
        },
        detail: {
            overview: 'Genel Bakış',
            settings: 'Ayarlar',
            unavailable: 'Satış Kanalı Açılamadı',
            unknownChannel: 'Bu Bağlantının Kanalı Bulunamadı',
            unknownChannelHint: (key: string) =>
                `Bağlantı "${key}" kanalını gösteriyor; bu sürümde böyle bir kanal yok. Kanal kaldırılmış veya adı değişmiş olabilir. Bağlantıyı silip yeniden kurabilirsiniz.`,
            history: 'Bağlantı Geçmişi',
            noHistory: 'Henüz Kayıt Yok',
            webhook: 'Webhook',
        },
        unavailableHere: 'Bu kanal bu ortamda kullanılamıyor.',
        settings: {
            title: 'Kanal Ayarları',
            hint: 'Bu kanalda ürün yayınlamak için gereken işletim ayarları.',
            save: 'Kaydet',
            saved: 'Kanal ayarları kaydedildi',
            blocked: {
                pending:
                    'Bağlantı henüz kontrol edilmedi. Bu ayarlar kanaldan alınır; Bağlantıyı Kontrol Et ile doğrulayın, bağlantı çalışınca burada görünür.',
                failed: 'Bağlantıda sorun olduğu için bu ayarlar kanaldan alınamıyor. Bağlantı bilgilerini düzeltip Bağlantıyı Kontrol Et ile yeniden deneyin; bağlantı çalışınca ayarlar burada görünür.',
                disabled: 'Bağlantı kapalı olduğu için bu ayarlar kanaldan alınamıyor.',
            },
            optionsUnavailable: 'Seçenekler kanaldan alınamadı; değeri pazaryeri panelinizdeki adıyla elle girebilirsiniz.',
            optionsEmpty:
                'Kanalda bu ayar için kayıtlı bir seçenek yok. Pazaryeri panelinizde oluşturduktan sonra burada seçebilirsiniz; şimdilik adını elle girebilirsiniz.',
        },
        carrier: {
            pick: 'Seçin…',
            none: 'Seçilmedi',
            unavailable: 'Kargo firması listesi kanaldan alınamadı; firmanın kodunu elle girebilirsiniz.',
            manualHint: 'Pazaryerinin kargo firmasına verdiği kod.',
        },

        webhook: {
            title: 'Gelen Bildirimler',
            description:
                'Pazaryeri sipariş değişikliklerini bu adrese bildirir. Adresi kanala tanıtın; bir bildirim geldiğinde ilgili siparişler anında güncellenir.',
            urlLabel: 'Bildirim Adresi',
            copy: 'Kopyala',
            copied: 'Kopyalandı',
            register: 'Kanala Tanıt',
            reRegister: 'Yeniden Tanıt',
            unregister: 'Kaldır',
            registered: (at: string) => `Tanıtıldı · ${at}`,
            notRegistered: 'Henüz Tanıtılmadı',
            recent: 'Son Gelen Bildirimler',
            noEvents: 'Henüz Bildirim Gelmedi',
            status: {
                processed: 'İşlendi',
                ignored: 'Yok Sayıldı',
                failed: 'Başarısız',
                received: 'Alındı',
            },
        },

        form: {
            title: (channel: string) => `${channel} Bağlantısı`,
            editTitle: (channel: string) => `${channel} Bağlantısını Düzenle`,
            name: 'Bağlantı Adı',
            nameHint: 'Bu satış kanalını panelde ayırt etmeniz için kendi verdiğiniz ad. Kanala gönderilmez.',
            secretStored: 'Kayıtlı. Güvenliğiniz için gösterilmez.',
            secretMissing: 'Henüz girilmedi.',
            replaceSecrets: 'Gizli Bilgileri Değiştir',
            replacingSecrets: 'Yeni değerleri girin; kaydettiğinizde kayıtlı olanların yerine geçer.',
            credentialsLost: 'Kayıtlı bağlantı bilgileri artık okunamıyor. Bağlantının yeniden çalışması için bütün bilgileri yeniden girin.',
            submit: 'Kaydet ve Bağlan',
            saveChanges: 'Değişiklikleri Kaydet',
        },
    },

    listings: {
        tabPending: (count: number) => `${count} Gönderilecek`,
        priceSource: {
            legend: 'Fiyat',
            product: 'Ürün Fiyatını Kullan',
            productHint: 'Ürün fiyatıyla birlikte değişir.',
            own: 'Kanala Özel Fiyat',
            ownHint: 'Yalnızca bu kanal için girilir.',
            noProductPrice: 'Bu ürünün henüz kendi fiyatı yok.',
            currencyRefused: (currency: string) => `Bu kanal, ürün fiyatının para birimi olan ${currency} ile satış yapmıyor.`,
            openPrices: 'Stok ve Fiyat Sekmesini Aç',
        },
        approve: {
            title: 'Bu Kanala Gönderilmeyi Bekleyenler',
            description:
                'Kanalın halihazırda taşıdığı ürünlerdeki stok ve fiyat değişiklikleri kendiliğinden gider. Bir ürünü bu kanalda ilk kez yayına almak ise bir güncelleme değil bir karardır ve buradan yapılır.',
            creating: (count: number) => `${count} ürün bu kanalda ilk kez satışa çıkacak.`,
            updating: (count: number) => `${count} listede bekleyen fiyat, stok veya içerik değişikliği var.`,
            confirmTitle: 'Kanala Gönder',
            confirmCreating: (creating: number, updating: number) =>
                `${creating} ürün bu kanalda ilk kez satışa çıkacak, ${updating} mevcut liste güncellenecek.`,
            confirmUpdating: (updating: number) => `${updating} mevcut liste güncellenecek.`,
            waitingProduct: 'Ürün',
            waitingChange: 'Gönderilmeyi Bekleyen',
            waitingChangedAt: 'Değişiklik',
            waitingNew: 'Kanalda Henüz Yok',
            catalogueKept:
                'Kilit simgeli bilgileri bu kanal kendi kataloğundan alır; yayınlamak, kanalda zaten olan ürünlerde bunları değiştirmez. Değişikliği kanalın satıcı panelinden talep edin.',
            contentPartKept: (part: string) => `${part}, kanalın kataloğunda kalır`,
            waitingMore: (count: number) => `Değişikliği bekleyen ${count} ürün daha var.`,
        },
        stockPolicy: {
            title: 'Bu Kanala Gösterilen Stok',
            description:
                'Pazaryeri yeni rakamı bir sonraki gönderimde öğrenir ve o aralıkta başka bir sipariş gelebilir. Bir miktar tutmak, satılmayan stok karşılığında fazla satmamayı satın alır.',
            none: 'Mevcut miktarın tamamı gönderiliyor.',
            current: (buffer: number, cap: number | null) =>
                cap != null ? `${buffer} adet tutuluyor, en fazla ${cap} adet gönderiliyor.` : `${buffer} adet tutuluyor.`,
            buffer: 'Tutulacak Miktar',
            bufferHint: '\u00dcr\u00fcn\u00fcn kendi tamponuna ek olarak bu kanaldan saklanır.',
            cap: 'Gönderilecek En Fazla',
            capHint: 'Stoğu biten ilanı cezalandıran pazaryerleri için. Üst sınır istemiyorsanız boş bırakın.',
        },

        failure: {
            'listing.connection_unavailable': 'Kanal bağlantısı kullanılamıyor.',
            'listing.listing_write_unimplemented': 'Bu kanal için listeleme aktarımı henüz uygulanmadı.',
            'listing.product_write_unimplemented': 'Bu kanal için ürün aktarımı henüz uygulanmadı.',
            'listing.facet_unsupported': 'Kanal bu alanı güncelleyemiyor.',
            'listing.content_update_unsupported': 'Bu kanal yayındaki ürünün içeriğini güncelleyemiyor.',
            'listing.nothing_sendable': 'Hiçbir ürün gönderilebilir durumda değil.',
            'listing.result_mismatch': 'Pazaryerinin yanıtı gönderilen ürünlerle eşleşmedi. Ürünü kontrol edip yeniden yayınlayın.',
            'listing.result_expired': 'Kanal sonucu saklama süresi doldu, durum bilinmiyor.',
            'listing.result_unreadable': 'Kanalın sonucu okunamadı, durum bilinmiyor; fark varsa yeniden gönderilecek.',
            'listing.batch_refused': 'Kanal partiyi reddetti.',
            'listing.create_unconfirmed':
                'Kanal ürünü oluşturup oluşturmadığını bildirmedi; bu yüzden yeniden gönderilmedi. Ürünü kanalda kontrol edin: oluştuysa Eşleştirme’den bağlayın, oluşmadıysa yeniden yayınlayın.',
            'listing.sku_missing': 'Bu kanal ürünü stok koduyla tanır; ürünün stok kodu yok.',
            'listing.not_yet_published': 'Bu ürün kanalda henüz yayınlanmadı, bu yüzden güncellenemiyor. Önce yayınlayın.',
            'listing.variant_missing': 'Bu ürünün varyantı bulunamadı.',
            'listing.barcode_missing': 'Bu kanal ürünü barkodla tanır; ürünün barkodu yok.',
            'publish.setting_missing': (setting: string) => `Kanal ayarı eksik: ${setting}`,
            'publish.product_not_found': 'Ürün katalogda bulunamadı.',
            'publish.variant_not_found': 'Varyant katalogda bulunamadı.',
            'publish.barcode_missing': (sku: string) => `Barkod girilmedi: ${sku}`,
            'publish.sku_too_short': (least: string, sku: string) => `Stok kodu en az ${least} karakter olmalı: ${sku}`,
            'publish.price_missing': (sku: string) => `Satış fiyatı girilmedi: ${sku}`,
            'publish.category_missing': 'Ürünün kategorisi yok.',
            'publish.category_unmapped': 'Ürünün kategorisi bu kanalla eşlenmedi.',
            'publish.attributes_missing': (names: string) => `Zorunlu kanal özellikleri eksik: ${names}`,
            'publish.image_missing': 'Ürünün görseli yok.',
            'publish.brand_missing': 'Marka seçilmedi.',
            'publish.brand_not_in_catalogue': 'Marka katalogda bulunamadı.',
            'publish.brand_not_mapped': (brand: string) =>
                `${brand} markası bu kanalın markalarından biriyle eşleştirilmedi. Eşleştirme, Marka Eşleştirme sekmesinde eşleştirin ya da bu kanalda bu ürüne özel marka seçin.`,
            'publish.brand_retired': (brand: string) =>
                `Kanal ${brand} markasını artık listelemiyor. Eşleştirme, Marka Eşleştirme sekmesinde başka bir marka ya da bu kanalda bu ürüne özel marka seçin.`,
            'publish.brand_not_in_category': (brand: string) =>
                `Bu kategori yalnızca kendi listesindeki markaları kabul ediyor ve ${brand} bu listede yok. Bu kanalda bu ürüne özel marka olarak listedeki markalardan birini seçin.`,
        },

        batches: {
            title: 'Son Gönderimler',
            description: 'Bu kanala gerçekte ne gönderildi ve kanal ne yaptı.',
            empty: 'Bu Kanala Henüz Bir Şey Gönderilmedi',
            what: 'İşlem',
            items: 'Ürün Sayısı',
            when: 'Tamamlanma',
            counts: (succeeded: number, failed: number) => `${succeeded} gönderildi, ${failed} reddedildi`,
            operation: {
                update: (what: string) => `${what} Güncelleme`,
                content: 'Ürün İçeriği',
                withdrawal: 'Satıştan Kaldırma',
                reinstatement: 'Yeniden Satışa Açma',
                deletion: 'Kanaldan Silme',
            },
            error: 'Reddedilme Nedeni',
            state: {
                queued: 'Sırada',
                pending: 'Gönderildi, Bekleniyor',
                succeeded: 'Kabul Edildi',
                partial: 'Kısmen Kabul Edildi',
                failed: 'Reddedildi',
                expired: 'Sonuç Bilinmiyor',
            },
        },

        listPrice: 'Liste Fiyatı',
        brand: {
            title: 'Bu Kanaldaki Marka',
            scope: {
                product: 'Bu Ürüne Özel',
                mapping: 'Marka Eşleştirmesi',
                name: 'Markanın Adı',
            },
            line: (brand: string, scope: string) => `${brand} · ${scope}`,
            pick: 'Bu Ürüne Özel Marka Seç',
            change: 'Değiştir',
            clear: 'Marka Eşleştirmesini Kullan',
            drawerTitle: 'Bu Ürüne Özel Marka',
            drawerHint: (channel: string) =>
                `Bu ürün ${channel} kanalına, kendi markası ve marka eşleştirmesi ne olursa olsun bu markayla gönderilir. Diğer kanallar etkilenmez.`,
            listedLocked: (channel: string) =>
                `${channel}, yayındaki bir ürünün markasını entegrasyonla değiştirmeye izin vermiyor. Burada seçtiğiniz marka o kanala gönderilmez. Markanın düzeltilmesini ${channel} satıcı panelinden talep edin.`,
            listedNote: (channel: string) =>
                `Bu ürün ${channel} kanalında yayında. Değişen marka sonraki yayında gönderilir ve ${channel} kendi incelemesinden geçirebilir; değişiklik görünmezse ${channel} satıcı desteğine başvurun.`,
            openMapping: 'Marka Eşleştirmesine Git',
        },
        requirements: {
            title: 'Yayın İçin Gerekenler',
            intro: (channel: string) => `${channel}, bunlar olmadan ürünü kabul etmez.`,
            ok: 'Tamam',
            missing: 'Eksik',
            category: 'Pazaryeri Kategorisi',
            attributes: 'Zorunlu Kategori Özellikleri',
            attributesMissing: (names: string) => `Eksik: ${names}.`,
            attributesDone: (count: number) => `${count} zorunlu özelliğin tümü dolu.`,
            attributesNone: 'Bu kategoride zorunlu özellik yok.',
            images: 'Görsel',
            imagesMissing: 'Ürünün görseli yok.',
            imagesDone: (count: number) => `${count} görsel.`,
            brand: 'Marka',
            barcode: 'Barkod',
            barcodeMissing: (count: number) => `${count} varyantın barkodu girilmemiş. Aşağıdaki tabloda kırmızı çerçeveli kutulara yazıp kaydedin.`,
            barcodeDone: 'Her varyantın barkodu var.',
            code: (least: number) => `Stok Kodu, En Az ${least} Karakter`,
            codeShort: (codes: string) => `Kısa olan: ${codes}. Kodu Varyantlar sekmesinde değiştirin.`,
            codeDone: 'Her varyantın kodu yeterince uzun.',
            openImages: 'Görsellere Git',
            openGeneral: 'Genel Bilgilere Git',
            openVariants: 'Varyantlara Git',
            settingUnset: (channel: string) => `Seçilmedi; ${channel}, hesabınızdaki varsayılanı kullanır.`,
            settingMissing: 'Seçilmedi; bu ayar olmadan ürün gönderilmez.',
            settingUnknown: (value: string) => `${value} kanalın listesinde yok; ürünler bu değerle reddedilir.`,
            openSettings: 'Kanal Ayarlarına Git',
        },
        content: {
            title: 'Kanal İçeriği',
            hint: 'Ürünün yalnızca bu kanalda, katalogdaki başlık ve açıklama yerine gösterilecek metni. Diğer kanalları ve ürünün kendi bilgilerini değiştirmez; boş bıraktığınız alan için katalog metni kullanılır.',
            reachOwnStore: 'Kaydettiğiniz metin mağazanızda hemen görünür.',
            reachMarketplace: (channel: string) =>
                `${channel} bu metni ürünü ilk yayınladığınızda alır; sonraki değişiklikler kartta Yayınla'ya bastığınızda gönderilir.`,
            titleLocked: (channel: string) =>
                `${channel}, yayındaki bir ürünün başlığını entegrasyonla değiştirmeye izin vermiyor. Burada yazdığınız başlık o kanala gönderilmez; açıklama gönderilir.`,
            keptByChannel: (channel: string) =>
                `${channel}, yayındaki bir ürünün başlığını, açıklamasını ve görsellerini entegrasyonla değiştirmeye izin vermiyor. Değişikliği kanalın satıcı panelinden talep edin.`,
            needsPrice: 'İçeriği kaydetmek için önce bu kanal için bir fiyat girin.',
            edit: 'İçeriği Düzenle',
            overridden: 'Kanala özel içerik kullanılıyor',
            fromCatalogue: 'Katalog metni kullanılıyor',
            titleField: 'Kanal Başlığı',
            titleHint: 'Boş bırakırsanız ürün başlığı kullanılır.',
            descriptionField: 'Kanal Açıklaması',
            currency: 'Para Birimi',
            currencyHint: 'Bu teklifin fiyatlandığı para birimi.',
            deliveryDays: 'Kargoya Hazırlık (Gün)',
            deliveryDaysHintOwnStore:
                'Bu ürünü kaç iş gününde kargoya verdiğiniz; 0 aynı gün kargo demektir. Boş bırakırsanız mağaza ayarlarınızdaki kargoya verme süresi geçerli olur.',
            deliveryDaysHintMarketplace:
                'Bu ürünü bu kanalda kaç günde kargoya verdiğiniz; 0 aynı gün kargo demektir. Boş bırakırsanız bu kanala süre gönderilmez ve kanaldaki mevcut süre geçerli kalır.',
            deliveryDaysMax: (days: number) => `Bu kanal en fazla ${days} gün kabul ediyor; daha uzun bir süre kaydedilmez.`,
            reset: 'Katalog Metnini Kullan',
        },
        title: 'Satış Kanalları',
        subtitle: 'Bu ürün için her kanalın göstereceği bilgiler.',
        channelImage: 'Kanalın Gösterdiği Görsel',
        channelImageHint: 'Bu pazaryeri ürün için kendi katalog görselini kullanıyor; galerinize karışmaz.',
        noChannels: 'Bu Ürünü Yayınlamak İçin Önce Bir Satış Kanalı Bağlayın',
        channel: 'Kanal',
        price: 'Fiyat',
        stock: 'Stok',
        stockFromShelf:
            'Bu kanal raftaki stoğunuzu satar; stoğu raftan gelir. Değiştirmek için ürünün Stok ve Fiyat sekmesini ya da Stok sayfasını kullanın. Bu kanala daha azını göstermek için kanalın Bu Kanala Gösterilen Stok ayarını kullanın.',
        status: 'Durum',
        pending: 'Gönderilmeyi bekliyor',
        nothingPending: 'Güncel',
        notOnChannel: 'Bu kanalda henüz yok',
        save: 'Kaydet',
        publish: 'Yayınla',
        flowHint:
            "Kaydettiğiniz fiyat ve stok kanala kendiliğinden gider. Ürünü kanalda ilk kez yayına almak ve içerik değişikliklerini göndermek için Yayınla'ya basın.",
        flowHintFromCatalogue:
            "Kaydettiğiniz fiyat ve stok kanala kendiliğinden gider; ürün bilgilerinde yaptığınız değişiklikler bu kanalda kaydettiğiniz anda görünür. Ürünü kanalda ilk kez yayına almak için Yayınla'ya basın.",
        nothingToPublish: 'Bekleyen yok',
        publishQueued: (channel: string) =>
            `${channel} için gönderim sıraya alındı. Sonuç birkaç dakika içinde bu kartta görünür; sayfayı yenilemeniz gerekmez.`,
        publishNothing: 'Şu an gönderilecek bir şey yok: önceki gönderim sürüyor ya da değişiklikler zaten gönderildi.',
        // Taking a product off sale on one channel and putting it back (EPZ-0147).
        // The channel confirms later, so the card says what was asked and where
        // the answer will show.
        withdraw: 'Satıştan Çek',
        reinstate: 'Yeniden Satışa Aç',
        confirmWithdraw: {
            title: 'Ürün Satıştan Çekilecek',
            body: (channel: string) =>
                `Ürün yalnızca ${channel} kanalında satıştan çekilir; diğer kanallarınızda satışa devam eder. Yeniden satışa açana kadar ${channel} kanalına stok ve fiyat gönderilmez; güncel rakamlar bekler, açtığınızda gider.`,
            action: 'Satıştan Çek',
        },
        withdrawQueued: (channel: string) =>
            `${channel} kanalında satıştan çekme sıraya alındı. Sonuç birkaç dakika içinde bu kartta görünür; sayfayı yenilemeniz gerekmez.`,
        withdrawNothing: 'Satıştan çekilecek bir şey yok: ürün bu kanalda zaten satıştan çekildi ya da hiç yayınlanmadı.',
        reinstateQueued: (channel: string) =>
            `${channel} kanalında yeniden satışa açma sıraya alındı. Sonuç birkaç dakika içinde bu kartta görünür; sayfayı yenilemeniz gerekmez.`,
        withdrawn: 'Satıştan Çekildi',
        notSelling: 'Satışta Değil',
        productNotActive: (status: string) =>
            `Ürün ${status}: aktifleştirilene kadar mağazanızda görünmez. Sayfanın üstündeki Aktifleştir'i kullanın.`,
        productNotActiveChannel: (status: string) =>
            `Ürün ${status}: aktifleştirilene kadar bu kanala ilk kez gönderilmez. Bu kanalda yayında olan ilanı stok ve fiyat almaya devam eder; satıştan çekmek için Satıştan Çek'i kullanın.`,
        withdrawnNotice: (channel: string) =>
            `Ürün ${channel} kanalında satıştan çekildi. Yeniden Satışa Aç'a basana kadar bu kanala stok ve fiyat gönderilmez; açtığınızda güncel rakamlar gider.`,
        awaitingMatch: 'Eşleşme Kararınızı Bekliyor',
        matchHint: (channel: string) =>
            `${channel} bu ürünü kataloğundaki bir ürünle eşleştirdi ve kararınızı bekliyor. Aynı ürünse eşleşmeyi onaylayın, değilse reddedin.`,
        approveMatch: 'Eşleşmeyi Onayla',
        rejectMatch: 'Eşleşmeyi Reddet',
        confirmApproveMatch: {
            title: 'Eşleşme Onaylanacak',
            body: (channel: string) =>
                `${channel} kanalına bu ürünün kataloğunda eşleştirdiği ürün olduğu bildirilir. Karar ${channel} kanalına hemen gönderilir.`,
            action: 'Eşleşmeyi Onayla',
        },
        confirmRejectMatch: {
            title: 'Eşleşme Reddedilecek',
            body: (channel: string) =>
                `${channel} kanalına bu ürünün eşleştirdiği ürün olmadığı bildirilir. Karar ${channel} kanalına hemen gönderilir; kanalın bundan sonra ne yaptığı bu kartta görünür.`,
            action: 'Eşleşmeyi Reddet',
        },
        matchDecided: (channel: string) =>
            `Kararınız ${channel} kanalına iletildi. Ürünün yeni durumu, ${channel} incelemesini bitirdiğinde bu kartta görünür.`,
        priceLocked: 'Fiyat Kilitli',
        priceLockedHint: (channel: string) =>
            `${channel}, yeni fiyat izin verdiği aralığın dışında kaldığı için ürünü önceki fiyatında kilitledi. Aralık içinde bir fiyat girip yayınlayın ya da kilidi kaldırın.`,
        releasePriceLock: 'Fiyat Kilidini Kaldır',
        confirmReleasePriceLock: {
            title: 'Fiyat Kilidi Kaldırılacak',
            body: (channel: string) =>
                `Kilit ${channel} kanalında hemen kaldırılır. Girdiğiniz fiyat gönderilmeyi bekler; değiştirmeden yayınlarsanız ${channel} ürünü yeniden kilitleyebilir.`,
            action: 'Kilidi Kaldır',
        },
        priceLockReleased: (channel: string) =>
            `Fiyat kilidi ${channel} kanalında kaldırıldı. Ürünün durumu, ${channel} bir sonraki okumada bildirdiğinde burada görünür.`,
        queued: (count: number) => `${count} gönderim kuyruğa alındı`,

        facet: {
            price: 'Fiyat',
            stock: 'Stok',
            delivery_time: 'Teslimat Süresi',
            status: 'Durum',
            content: 'İçerik',
        },
        // What changed of a product's content, as a channel's waiting list names it.
        contentPart: {
            title: 'Başlık',
            description: 'Açıklama',
            category: 'Kategori',
            brand: 'Marka',
            vat: 'KDV',
            variants: 'Varyantlar',
            images: 'Görseller',
            attributes: 'Kanal Özellikleri',
            channel_category: 'Pazaryeri Kategorisi',
            channel_content: 'Kanal İçeriği',
        },
        contentPartLink: (part: string, product: string) => `${product}: ${part} değişikliğini aç`,

        attributes: {
            title: 'Kanal Özellikleri',
            edit: 'Özellikleri Düzenle',
            hint: 'Pazaryeri, ürünü kabul etmeden önce kendi kategorisinin zorunlu alanlarını ister.',
            notMapped: 'Bu ürünün kategorisi henüz bu kanalın bir kategorisiyle eşlenmedi.',
            noCategory: 'Ürünün kategorisi yok. Genel sekmesinde Düzenle ile bir kategori seçin.',
            mapLink: 'Kategori Eşleştirmeye Git',
            channelCategory: 'Pazaryeri Kategorisi',
            required: 'Zorunlu',
            missing: (count: number) => `${count} Zorunlu Özellik Eksik`,
            ready: 'Zorunlu Özelliklerin Tümü Dolu',
            pick: 'Seçin…',
            customPlaceholder: 'Serbest Metin',
            save: 'Özellikleri Kaydet',
            saved: 'Özellikler kaydedildi',
            none: 'Bu kategori için kanal ek bilgi istemiyor.',
            noneRequired: 'Pazaryeri bu kategoride bunların hiçbirini zorunlu tutmuyor; hepsi isteğe bağlı.',
        },

        // Where the listing stands among the marketplace's offers for the
        // product, on a channel that declares product.buybox (EPZ-0171, EPZ-0223).
        buyBox: {
            column: 'Buy Box Sırası',
            rank: (rank: number, offers: number) => `${rank} / ${offers}`,
            notRanked: 'Sıralamada Yok',
            notRead: 'Henüz Okunmadı',
            history: {
                title: 'Rakip Fiyatları',
                hint: 'Bu pazaryerindeki teklifler arasındaki yerinizin her değişimi. Saatte bir okunur, 90 gün saklanır.',
                variant: 'Varyant',
                date: 'Tarih',
                rank: 'Sıranız',
                ownPrice: 'Fiyatınız',
                winnerPrice: 'Kazanan Fiyat',
                lowestRival: 'En Düşük Rakip Fiyatı',
                offers: 'Teklif Sayısı',
                empty: 'Henüz Okunmadı',
                emptyHint: 'Ürün yayına girdikten sonra bir saat içinde ilk okuma burada görünür.',
                more: 'Daha Fazla Göster',
            },
        },

        state: {
            draft: 'Gönderilmedi',
            pending: 'Gönderiliyor',
            in_review: 'Onay Bekliyor',
            active: 'Yayında',
            passive: 'Duraklatıldı',
            rejected: 'Reddedildi',
            error: 'Başarısız',
        },
    },

    orders: {
        channelFilter: 'Kanal',
        allChannels: 'Tüm Kanallar',
        fromDate: 'Başlangıç',
        toDate: 'Bitiş',
        clearFilters: 'Filtreleri Temizle',
        title: 'Siparişler',
        subtitle: 'Tüm kanallar tek listede.',
        search: 'Sipariş numarası, isim veya telefonla ara',
        empty: 'Henüz Sipariş Yok',
        emptyHint: 'Bir kanaldan sipariş geldiği anda burada görünür.',
        noChannelHint: 'Sipariş almak için önce bir satış kanalı bağlayın.',
        count: (shown: number, total: number) => `${total} siparişten ${shown} tanesi`,
        actionReason: {
            payment: 'Havale bekleniyor',
            pack: 'Paket oluşturulmalı',
            ship: 'Kargoya verilmeli',
            invoice: 'Fatura kesilmeli',
        },
        actionReasonLong: {
            payment: 'İşlem Gerekiyor: Havale bekleniyor. Ödeme hesabınıza geçmeden siparişi kargoya vermeyin.',
            pack: 'İşlem Gerekiyor: Siparişin paketi oluşturulmalı.',
            ship: 'İşlem Gerekiyor: Paket kargoya verilmeli.',
            invoice: 'İşlem Gerekiyor: Siparişin faturası kesilmeli.',
        },
        actionReasonCta: {
            payment: 'Ödeme Alındı',
            pack: 'Paketi Oluştur',
            ship: 'Kargoya Ver',
            invoice: 'Faturayı Kes',
        },
        claimState: {
            cancellation_requested: 'İptal Talebi',
            return_requested: 'İade Sürecinde',
        },
        unmatchedShort: 'Eşleşmedi',
        unmatchedWarning: (count: number) => `${count} satılan ürün katalogdaki bir ürünle eşleşmedi.`,
        showUnmatched: 'Göster',
        clearUnmatched: 'Tüm Siparişleri Göster',

        columns: {
            number: 'Sipariş',
            channel: 'Kanal',
            customer: 'Müşteri',
            total: 'Tutar',
            status: 'Durum',
            invoice: 'Fatura',
            date: 'Tarih',
        },

        /** The list's own filter: which orders still owe a document. */
        invoiceFilter: {
            all: 'Fatura Durumu',
            invoiced: 'Faturalandırılmış',
            notInvoiced: 'Faturalandırılmamış',
        },

        /** What an order's invoice has got to, for the list's own column. */
        invoiceState: {
            issued: 'Kesildi',
            none: 'Kesilmedi',
            failed: 'Başarısız',
            recorded: 'Resmileşmedi',
            cancelled: 'İptal',
            pending: 'Kesiliyor',
        },

        status: {
            all: 'Tüm Durumlar',
            pending: 'Bekliyor',
            action_required: 'İşlem Gerekiyor',
            shipped: 'Kargoda',
            delivered: 'Teslim Edildi',
            cancelled: 'İptal',
            returned: 'İade Edildi',
            unknown: 'Bilinmiyor',
        },

        source: {
            channel: 'Kanal',
            seller: 'Siz',
            customer: 'Müşteri',
            system: 'Sistem',
        },

        event: {
            received: 'Sipariş alındı',
            updated: 'Sipariş güncellendi',
            rematched: 'Ürün eşleştirmesi düzeltildi',
            channel_cancel: 'Kanala iptal bildirildi',
            channel_split: 'Paket kanalda bölündü',
            channel_status: 'Kanal durumu güncellendi',
            channel_carrier: 'Kargo firması değiştirildi',
            channel_tracking: 'Kargo takip numarası kanala iletildi',
            channel_invoice_link: 'Fatura bağlantısı kanala iletildi',
            channel_action_failed: 'Kanal işlemi başarısız',
            channel_action_duplicate: 'Tekrarlanan işlem kanala gönderilmedi',
            channel_action: 'Kanala işlem gönderildi',
            shipped: 'Kargoya verildi',
            claim_return: 'İade talebi açıldı',
            claim_cancellation: 'İptal talebi açıldı',
            claim_missing: 'Ulaşmadı olarak bildirildi',
            claim_damaged: 'Hasarlı geldi olarak bildirildi',
            claim_exchange: 'Değişim talebi açıldı',
            claim_other: 'Talep açıldı',
            payment_received: 'Havale alındı',
            payment_refunded: 'Ödeme karta iade edildi',
            payment_refund_manual: 'İade ödeme hizmetinin kendi ekranından yapılmalı',
            payment_refund_failed: 'Ödeme hizmeti iadeyi kabul etmedi',
        },

        channelActions: {
            sent: 'İşlem kanala gönderildi. Sonuç birkaç dakika içinde burada görünür; sayfayı yenilemeniz gerekmez.',
            done: 'Kanal işlemi kabul etti.',
            duplicate:
                'Aynı işlem kanala zaten gönderilmişti, bu yüzden yeniden gönderilmedi. Sipariş kanaldan yeniden okunuyor; işlemi bilerek tekrarlıyorsanız 10 dakika sonra yeniden gönderin.',
            lastFailed: (when: string, reason: string) => `Son kanal işlemi başarısız oldu (${when}): ${reason}`,
            more: 'Diğer İşlemler',
            openStatus: 'Kanal Durumunu Güncelle',
            statusButton: 'Durumu Gönder',
            timeOfDay: (label: string) => `${label}, Saat`,
            timePlaceholder: 'SS:DD',
            statusLabel: 'Yeni Durum',
            unknownStatus: 'Kanal Durumu',
            statuses: {
                Picking: 'Onaylandı',
                Invoiced: 'Faturalandı',
                Packaged: 'Paketlendi',
                ReadyToShip: 'Gönderime Hazır',
                Shipped: 'Kargolandı',
                Delivered: 'Teslim Edildi',
                DeliveredWithoutShipment: 'Teslim Edildi (Kargosuz Ürün)',
                Cancelled: 'İptal Edildi',
                UnSupplied: 'Tedarik Edilemedi',
                Unpacked: 'Paketi Boz',
                InTransit: 'Kargoda',
                Undelivered: 'Teslim Edilemedi',
                OutForDelivery: 'Dağıtıma Çıktı',
                ReturnedToSeller: 'Satıcıya Geri Döndü',
                // The shop's own words for its statuses (ADR 0003 §7).
                confirmed: 'Onaylandı',
                packed: 'Paketlendi',
                shipped: 'Kargolandı',
                delivered: 'Teslim Edildi',
                cancelled: 'İptal Edildi',
            },
            statusHints: {
                Unpacked: 'Paket pazaryerinde bozulur, içindeki ürünler yeniden paketlenmeyi bekleyen ürünlere döner.',
                DeliveredWithoutShipment:
                    'Yalnızca pazaryerinde kargosuz ürün olarak tanımlı ürünlerin, örneğin dijital ürünlerin siparişleri içindir; kargolu bir siparişte pazaryeri bunu reddeder.',
            },
            title: 'Kanal İşlemleri',
            hint: 'Siparişin satış kanalında yapılan işlemler; sonucu sipariş geçmişine düşer.',
            openCancel: 'Kanalda İptal',
            openSplit: 'Paketi Böl',
            openCarrier: 'Kargo Firmasını Değiştir',
            package: 'Paket',
            pickReason: 'İptal Gerekçesi Seçin',
            penaltyTable: 'İptal Ceza Koşulları',
            penaltyValue: 'Ürün Bedeli',
            penaltyFee: 'Ceza Tutarı',
            penaltyUpTo: (from: string, to: string) => `${from} – ${to}`,
            penaltyAbove: (from: string) => `${from} ve üzeri`,
            penaltyAccepted: 'İptal ceza koşullarını okudum ve kabul ediyorum.',
            pickCarrier: 'Kargo Firması Seçin',
            cancelQuantity: 'İptal Edilecek Adet',
            statusLines: 'İlerletilecek Ürünler',
            statusLinesHint: 'Boş bırakırsanız paketin tamamı ilerler.',
            statusPackages: 'Paketler',
            cancelLines: 'İptal Edilecek Ürünler',
            reason: 'Gerekçe',
            reasonHint: 'İsteğe bağlı',
            reasonCodeHint:
                'Bu pazaryeri sayısal bir gerekçe kodu istiyor. Pazaryerinin satıcı panelinde gösterilen iptal nedenleri bu kodla birebir eşleşmeyebilir; kodu bilmiyorsanız pazaryerinin entegrasyon desteğine danışın.',
            cancelButton: 'Kanalda İptal Et',
            confirmCancel: {
                title: 'Ürünler Kanalda İptal Edilecek',
                body: 'Seçili ürünler satış kanalında iptal edilir.',
                action: 'Kanalda İptal Et',
            },
            carrier: 'Kargo Firması',
            carrierHint: 'Kanalın kargo firması kodu',
            carrierButton: 'Kargo Firmasını Değiştir',
            carrierQueued: 'Kargo firması değiştirme isteği kanala gönderildi. Sonucu Geçmiş sekmesine düşer.',
            splitLines: 'Yeni Pakete Taşınacak Ürünler',
            splitQuantityHint: 'Yeni pakete taşınacak adet. Kalan ürünler diğer pakette tutulur.',
            splitHint: 'İşaretlenen ürünler kanalda yeni bir paket olur; kalanlar mevcut pakette kalır.',
            splitButton: 'Paketi Böl',
            confirmSplit: {
                title: 'Ürünler Ayrı Pakete Taşınacak',
                body: 'Seçili ürünler pazaryerinde yeni bir paket olur; her paket ayrı kargolanır.',
                action: 'Paketi Böl',
            },
        },

        openLineActions: {
            title: 'Paketlenmemiş Ürünler',
            hint: 'Kanalın henüz paketlemediği ürünler için doğrudan pazaryerinde yapılan işlemler; sonucu sipariş geçmişine düşer.',
            openPack: 'Paket Oluştur',
            openCancel: 'Ürünü İptal Et',
            openCarrier: 'Kargo Firmasını Değiştir',
            packHint: 'Yalnızca kanalın uygun gördüğü ürünler listelenir. 0 bırakılan adetler açık kalır.',
            packLines: 'Ürünler',
            packButton: 'Paket Oluştur',
            confirmPack: {
                title: 'Kanalda Yeni Bir Paket Oluşturulacak',
                body: 'Seçili ürünler ve adetler pazaryerinde yeni bir paket olur.',
                action: 'Paket Oluştur',
            },
            cancelButton: 'Kanalda İptal Et',
            confirmCancel: {
                title: 'Ürünler Kanalda İptal Edilecek',
                body: 'Seçili ürünler, kendileri için henüz bir paket oluşmadan pazaryerinde iptal edilir.',
                action: 'Kanalda İptal Et',
            },
            carrierHint: 'Yalnızca kanalın bu ürün için şu anda kabul ettiği kargo firmaları listelenir.',
            noCarriers: 'Bu ürün için şu anda uygun kargo firması yok.',
        },

        detail: {
            tabs: {
                summary: 'Özet',
                shipping: 'Kargo',
                claims: 'Talepler',
                invoice: 'Fatura',
                timeline: 'Geçmiş',
            },
            shipTo: 'Teslimat',
            billTo: 'Fatura',
            noBilling: 'Kanal fatura bilgisi göndermedi.',
            billSameAsShip: 'Fatura adresi teslimat adresi ile aynıdır.',
            taxIdentity: {
                title: 'Vergi Bilgileri',
                taxOffice: 'Vergi Dairesi',
                taxNumber: 'Vergi Numarası',
                identityNumber: 'TC Kimlik Numarası',
                none: 'Bu alıcı için kayıtlı vergi numarası veya TC kimlik numarası yok.',
                masked: 'Numaraların tamamı müşteri kaydında görünür.',
            },
            cancellation: {
                title: 'İptal',
                by: 'İptal Eden',
                reason: 'İptal Nedeni',
                at: 'İptal Tarihi',
                line: 'İptal Edildi',
                parties: { customer: 'Müşteri', seller: 'Siz', channel: 'Pazaryeri' },
            },
            returnedQuantity: (count: number) => `${count} adet iade edildi`,
            purchase: {
                title: 'Ödeme',
                method: 'Yöntem',
                status: 'Durum',
                coupon: 'İndirim Kodu',
                note: 'Müşteri Notu',
                email: 'Alıcının E-postası',
                methods: { cod: 'Kapıda Ödeme', bank_transfer: 'Havale/EFT', card: 'Kart ile Ödeme' },
                statuses: {
                    on_delivery: 'Kapıda ödenecek',
                    awaiting_transfer: 'Havale bekleniyor',
                    paid: 'Ödendi',
                    refunded: 'İade edildi',
                    partially_refunded: 'Kısmen iade edildi',
                },
            },
            columns: 'Sütunlar',
            image: 'Görsel',
            barcode: 'Barkod',
            package: 'Paket',
            noPackage: 'Paketlenmedi',
            process: {
                title: 'Süreç',
                order: 'Sipariş',
                packages: 'Paket',
                shipping: 'Kargo',
                invoice: 'Fatura',
                noPackages: 'Oluşturulmadı',
                packageCount: (count: number) => `${count} paket`,
            },
            lines: 'Ürünler',
            quantity: 'Adet',
            unitPrice: 'Birim Fiyat',
            parcels: 'Paketler',
            noAmount: 'Tutar kanaldan gelmedi',
            placedOn: (when: string) => `${when} tarihinde`,
            carrier: 'Kargo',
            tracking: 'Takip',
            channelLabel: 'Etiket',
            printChannelLabel: 'Etiketi Yazdır',
            printSlip: 'Paket Fişi',
            slipTitle: 'Paket Fişi Bas',
            slipHint:
                'Kargo etiketi değildir. Hiçbir gönderi oluşturmaz, kargo firmasına bir şey bildirilmez; paketi siparişiyle eşleştirmek için barkodlu bir fiştir.',
            slipProducts: 'Pakettekileri Listele',
            slipPrices: 'Tutarları Göster',
            slipPricesHint: 'Fiş, yolculuk boyunca görünür durumdadır.',
            timeline: 'Zaman Çizelgesi',
            unmatchedNotice: (count: number) => `Bu siparişteki ${count} ürün katalogla eşleşmedi; stoğu etkilemedi ve faturalandırılamaz.`,
            rematch: 'Yeniden Eşleştirmeyi Dene',
            transferAwaited: 'Havale hâlâ bekleniyor. Ödeme hesabınıza geçtiyse Ödeme Alındı ile kaydedin.',
            pendingClaim: {
                cancellation_requested: 'Bu sipariş için yanıtınızı bekleyen bir iptal talebi var. Karar verene kadar siparişi kargoya vermeyin.',
                return_requested: 'Bu sipariş için yanıtınızı bekleyen bir iade talebi var.',
            },
            answerClaim: 'Talebi Yanıtla',
            rematched: (count: number) => (count === 0 ? 'Hâlâ eşleşen yok.' : `${count} tanesi çözüldü.`),
        },
    },

    stock: {
        title: 'Stok',
        subtitle: 'Ürün başına tek miktar, tüm kanallara gönderilir.',
        withPrice: {
            title: 'Stok ve Fiyat',
            subtitle: 'Ürün fiyatı ve tek miktar; her biri onu kullanan tüm kanallara gönderilir.',
            currency: 'Fiyat Para Birimi',
            currencyHint: 'Bu ürünün tüm varyantlarının para birimi.',
            hint: 'Fiyatı ve raftaki sayıyı yazın; değişen satırlar işaretlenir ve birlikte kaydedilir.',
            elsewhere: (count: number) => `Bu depodaki stok; diğer depolarda ${count} adet daha var.`,
            warehouse: 'Depo',
            warehouseHint: 'Rafta ve Kanallardan Saklanan bu deponun rakamlarıdır; yazdığınız sayı bu depoya kaydedilir.',
        },
        move: {
            action: 'Taşı',
            actionLabel: (sku: string) => `${sku} Stoğunu Taşı`,
            title: (sku: string) => `${sku} Stoğunu Taşı`,
            hint: 'Seçtiğiniz miktar bir depodan diğerine taşınır. Siparişlere ayrılmış olanlar taşınmaz. Her hareket stok geçmişine Transfer olarak yazılır.',
            from: 'Kaynak Depo',
            to: 'Hedef Depo',
            free: (count: number) => `Taşınabilecek stok: ${count} adet.`,
            submit: 'Stoğu Taşı',
        },
        onHand: 'Rafta',
        buffer: 'Kanallardan Saklanan',
        available: 'Kanallara Bildirilen',
        save: 'Kaydet',
        empty: 'Bu Ürün İçin Henüz Stok Kaydı Yok',
        low: 'Azalanlar',
        lowEmpty: 'Azalan Ürün Yok',
        count: (shown: number, total: number) => `${total} satırdan ${shown} tanesi`,
        bulkSet: 'Şu Değere Ayarla',
        bulkIncrease: 'Şu Kadar Artır',
        bulkDecrease: 'Şu Kadar Azalt',
        quantity: 'Miktar',
        apply: 'Uygula',
        bulkApplied: (n: number) => `${n} ürün güncellendi`,
        bulkFailed: (n: number) => `${n} ürün güncellenemedi`,
        movements: 'Stok Geçmişi',
        movementsOf: (sku: string) => `Stok Geçmişi · ${sku}`,
        saveChanges: (n: number) => (n === 0 ? 'Değişiklikleri Kaydet' : `Değişiklikleri Kaydet (${n})`),
        changedHint: 'Rafta sayısını yazın; değişen satırlar işaretlenir ve birlikte kaydedilir.',
        search: 'Ürün adı veya koda göre ara',
        product: 'Ürün',
        code: 'Kod',
        noMatch: 'Aramaya Uyan Azalan Ürün Yok',
        movementsEmpty: 'Henüz Hareket Yok',
        balance: 'Kalan',

        // A shelf opened from a marketplace's figure, before anybody counted it.
        openedFromChannel: 'Pazaryerinden Açılış',
        reason: {
            manual: 'Elle Düzeltme',
            order: 'Satış',
            cancellation: 'İptal',
            return: 'İade',
            stocktake: 'Sayım',
            damage: 'Zayi',
            transfer: 'Transfer',
        },
    },

    images: {
        title: 'Görseller',
        subtitle: 'İlk sıradaki, pazaryerinin arama sonuçlarında gösterdiğidir.',
        add: 'Görsel Ekle',
        empty: 'Henüz Görsel Yok',
        emptyHint: 'Görselsiz ürün nadiren satar.',
        remove: 'Kaldır',
        makeFirst: 'Başa Al',
        first: 'İlk',
        alt: 'Açıklama',
        uploading: 'Yükleniyor…',
        pending: (count: string) => `${count} görsel aktarılıyor`,
        failed: (count: string) => `${count} görsel alınamadı`,
        emptyPending: 'Görseller İşleniyor',
        emptyPendingHint: 'Birkaç dakika içinde burada olacaklar.',
        failure: {
            'media.import_unreachable': 'Adres yanıt vermedi ya da görsel göndermedi.',
            'media.import_not_served': 'Site bu görseli vermeyi reddetti; adres yanlış, kaldırılmış ya da erişime kapalı.',
            'media.import_refused': 'Bu adresten görsel alınmaz. Adres https ile başlamalı ve herkese açık olmalı.',
            'media.import_not_an_image': 'Adresteki dosya bir görsel değil.',
            'media.import_too_small': 'Görsel çok küçük; kısa kenarı en az 300 piksel olmalı.',
            'media.import_too_large': 'Görsel dosyası en fazla 12 MB olabilir.',
            'media.import_gallery_full': 'Ürünün galerisi dolu; bir ürün en fazla 12 görsel taşır.',
            'media.import_not_stored': 'Görsel alındı ama saklanamadı. Yeniden deneyin.',
        },
        failureUnknown: 'Görsel alınamadı.',
    },

    dashboard: {
        byChannel: {
            title: 'Kanala Göre',
            orders: (count: number) => `${count} sipariş`,
            unknown: 'Bağlantısı Kaldırılmış Kanal',
        },
        title: 'Genel Bakış',
        metrics: {
            todayOrders: 'Bugünkü Sipariş',
            todayRevenue: 'Bugünkü Ciro',
            returnedOrders: 'İade, 30 Gün',
            returnedRevenue: 'İade Tutarı, 30 Gün',
            periodOrders: 'Sipariş, 30 Gün',
            periodRevenue: 'Ciro, 30 Gün',
        },
        work: {
            title: 'Sizi Bekleyenler',
            none: 'Bekleyen İş Yok',
            noneHint: 'Her şey güncel.',
            actionRequired: 'İşlem Bekleyen Sipariş',
            unmatched: 'Ürünü Eşleşmeyen Satış',
            pending: 'Gönderilmeyi Bekleyen Değişiklik',
            failed: 'Kanalın Reddettiği Listeleme',
            failedInvoices: 'Reddedilen Faturalar',
            openClaims: 'Yanıt Bekleyen İade',
            failingChannels: 'Çalışmayan Bağlantı',
        },
        lowStock: {
            title: 'Azalanlar',
            none: 'Azalan Ürün Yok',
        },
        plan: {
            trialLeft: (days: number, date: string) => `Deneme süreniz ${date} tarihinde bitiyor, ${days} gün kaldı.`,
            trialOver: (date: string) => `Deneme süreniz ${date} tarihinde sona erdi.`,
        },
        channelSummary: {
            counts: (connected: number, active: number) => `${connected} kanal bağlı · ${active} aktif`,
            allWorking: 'Tüm kanallarınız çalışıyor.',
            broken: (count: number) => `${count} kanalda sorun var.`,
            manage: 'Satış Kanallarını Yönet',
        },
        welcome: (name: string) => `Hoş geldiniz, ${name}`,
        emptyState: 'Henüz Satış Kanalı Bağlamadınız',
        emptyStateHint: 'Ürünlerinizi pazaryerlerine göndermek için bir kanal ekleyin.',
        // The steps a person's role may do (docs/11 §4).
        setup: {
            title: 'Kurulum',
            progress: (done: number, total: number) => `${done} / ${total} tamamlandı`,
            done: 'Tamam',
            todo: 'Eksik',
            optional: 'İsteğe Bağlı',
            steps: {
                channel: {
                    title: 'Satış Kanalı Bağlayın',
                    hint: 'Pazaryeri hesabınızı bağlayın. Siparişleriniz ve ürünleriniz bu panelde toplanır.',
                },
                products: {
                    title: 'Ürün Ekleyin',
                    hint: 'Ürünlerinizi tek tek ekleyin ya da pazaryerinizden içe aktarın.',
                },
                mapping: {
                    title: 'Kategorileri Eşleştirin',
                    hint: 'Kategorilerinizi pazaryerinin kategorileriyle eşleştirin. Ürünleri yayınlamak için gerekir.',
                },
                carrier: {
                    title: 'Kargo Firması Bağlayın',
                    hint: 'Siparişleriniz için kargo kaydı açmak üzere anlaşmalı kargo firmanızı bağlayın.',
                },
                invoice: {
                    title: 'e-Fatura Hizmeti Bağlayın',
                    hint: 'Faturalarınızı panelden kesmek için e-Fatura hizmetinizi bağlayın.',
                },
                shop: {
                    title: 'Mağazanızı Yayınlayın',
                    hint: 'Tezgah ile kendi mağazanızı açın. Bu adım isteğe bağlıdır.',
                },
            },
        },
    },

    impersonation: {
        banner: 'Bu oturum destek ekibi tarafından görüntüleniyor. Tüm işlemler kayıt altındadır.',
    },

    providerForm: {
        name: 'Bağlantı Adı',
        nameHint: 'Birden fazla bağlantınız olduğunda ayırt etmek için kendi etiketiniz.',
        environment: 'Ortam',
        sandbox: 'Test',
        production: 'Canlı',
        submit: 'Kaydet ve Bağla',
        useDefault: 'Varsayılan',
        testNote: (service: string) => `${service} test ortamına bağlanır; bu bağlantıyla gerçek bir işlem yapılmaz.`,
    },

    invoices: {
        manual: {
            title: 'Kanal Dışı Satış İçin Fatura',
            hint: 'Toptan satış, tamir, anlaşmayla yapılan her iş — cironuzun bir kısmı başka bir sistemde kalmasın.',
            open: 'Yeni Fatura',
            issue: 'Kes',
            buyerKind: 'Alıcı',
            person: 'Şahıs',
            company: 'Kurumsal',
            buyerName: 'Ad / Unvan',
            taxNumber: 'Vergi Numarası',
            taxNumberHint: 'Kurumsal fatura bu numaraya kesilir; numarasız belge olmaz.',
            taxOffice: 'Vergi Dairesi',
            identityNumber: 'TC Kimlik Numarası',
            identityNumberHint: 'İsteğe bağlı. Elinizde yoksa boş bırakın.',
            address: 'Adres',
            city: 'İl',
            lines: 'Kalemler',
            lineName: 'Açıklama',
            quantity: 'Adet',
            unitPrice: 'Birim Fiyat',
            taxRate: 'KDV %',
            addLine: 'Kalem Ekle',
            lineIncomplete: 'Kalemin faturaya girmesi için bu alanı doldurun ya da kalemi silin.',
            total: 'KDV Dahil Toplam',
            notes: 'Fatura Notu',
        },
        fromDate: 'Başlangıç',
        toDate: 'Bitiş',
        clearDates: 'Tarihleri Temizle',
        title: 'Faturalar',
        subtitle: 'Kesilen her belge ve kesilmeyi bekleyenler.',
        empty: 'Henüz Fatura Yok',
        emptyFiltered: 'Filtreye Uyan Fatura Yok',
        emptyFilteredHint: 'Filtreleri değiştirin ya da temizleyin.',
        emptyHint: 'Bir siparişe fatura kestiğinizde burada görünür.',
        count: (shown: number, total: number) => `${total} faturadan ${shown} tanesi`,
        search: 'Alıcı adı veya seri numarası ile ara',

        kind: {
            all: 'Tüm Belgeler',
            einvoice: 'e-Fatura',
            earchive: 'e-Arşiv',
            credit_note: 'İade Faturası',
        },

        status: {
            all: 'Tüm Durumlar',
            draft: 'Taslak',
            queued: 'Sırada',
            sent: 'Gönderildi',
            delivered: 'Ulaştı',
            failed: 'Başarısız',
            cancelled: 'İptal Edildi',
            // The service has the document and cannot make it legal. Not a
            // draft, which never left this system, and not a failure: the sale
            // is in the seller's books and what is missing is the filing.
            recorded: 'Kaydedildi, Resmileşmedi',
        },

        problem: {
            unavailable: 'e-Fatura hizmetine şu anda ulaşılamıyor. Birkaç dakika sonra yeniden deneyin.',
            unsupported: 'Bağlı e-Fatura hizmeti bu işlemi yapamıyor.',
            failed: 'e-Fatura hizmeti işlemi tamamlayamadı. Birkaç dakika sonra yeniden deneyin.',
        },

        columns: {
            serial: 'Seri No',
            buyer: 'Alıcı',
            kind: 'Belge',
            status: 'Durum',
            total: 'Tutar',
            issued: 'Kesildi',
        },

        // The number is missing, which is not the same as the document being
        // missing: a service can hold a document it has not numbered.
        noSerial: 'Numara Yok',
        detail: {
            download: 'Faturayı İndir',
            downloadXml: 'XML (UBL) Dosyasını İndir',
            refresh: 'Durumu Yenile',
            refreshed: 'e-Fatura hizmetine yeniden soruldu; belgenin güncel durumu aşağıda.',
            recordSerial: 'Numarayı Kaydet',
            // Names the service, because "hizmetin kendi ekranı" left the
            // seller guessing which one, and says plainly what typing the
            // number does — it makes the document legal here.
            recordSerialHint: (provider: string) =>
                `Belgeyi ${provider} ekranından kesin, sonra aldığı numarayı buraya girin. Fatura o anda geçerli sayılır ve bağlantısı pazaryerine iletilir.`,
            serial: 'Fatura Numarası',
            issuedAt: 'Düzenleme Tarihi',
            // Said, because the field is optional and an empty date box beside a
            // required one reads as something forgotten.
            issuedAtHint: 'Belgenin üzerindeki tarih. Boş bırakırsanız bugün yazılır.',
            uploadDocument: 'Belgeyi Yükle',
            uploadHint: 'Bu hizmet fatura görüntüsü üretmiyor. Hizmetten indirdiğiniz PDF’i yükleyin; pazaryerine iletilen bağlantı budur.',
            sendToBuyer: 'Alıcıya Gönder',
            title: (serial: string, status?: string) =>
                serial
                    ? `${serial} Numaralı Fatura`
                    : // A document the service holds is not a draft: a draft never
                      // left this system, and calling it one hides that the sale
                      // already reached the provider.
                      status === 'recorded'
                      ? 'Numarasız Fatura'
                      : 'Taslak Fatura',
            summary: 'Özet',
            // Named for what it does rather than for its category: a seller
            // pressing it wants to do something to this document, and
            // "İşlemler" could as easily be the order's transactions.
            actions: 'Fatura İşlemleri',
            buyer: 'Alıcı',
            lines: 'Kalemler',
            order: 'Sipariş',
            subtotal: 'Matrah',
            tax: 'KDV',
            total: 'Toplam',
            exemption: (code: string) => `KDV İstisnası ${code}`,
            cancel: 'Faturayı İptal Et',
            confirmCancel: {
                title: 'Fatura İptal Edilecek',
                body: 'Kesilmiş belge e-Fatura hizmetinde geri çekilir.',
                action: 'Faturayı İptal Et',
            },
            creditNote: 'İade Faturası Kes',
            confirmCreditNote: {
                title: 'İade Faturası Kesilecek',
                body: 'Bu faturayı ters kaydeder. Kesilmiş fatura değiştirilemez.',
                action: 'İade Faturası Kes',
            },
            reason: 'Gerekçe',
        },

        issue: {
            action: 'Fatura Kes',
            title: 'Bu Siparişe Fatura Kes',
            serial: 'Seri No',
            serialHint: 'Yalnızca belgeyi başka bir yerde kestiyseniz gerekir.',
            exemptionCode: 'İstisna Kodu',
            exemptionCodeHint: 'Bu satışta neden KDV olmadığını anlatan GİB kodu. Listeyi mali müşaviriniz veya e-Fatura hizmetiniz verir.',
            exemptionCodeEmpty: 'Kod seçin',
            exemptionReason: 'İstisna Açıklaması',
            exemptionLines: (names: string[]) =>
                names.length === 1 ? `"${names[0]}" satırında KDV yok.` : `${names.length} satırda KDV yok: ${names.join(', ')}.`,
            invalidLines: (names: string[]) =>
                names.length === 1
                    ? `"${names[0]}" satırı belgeye giremiyor, bu yüzden aşağıdaki satırlarda yok.`
                    : `${names.length} satır belgeye giremiyor, bu yüzden aşağıdaki satırlarda yok: ${names.join(', ')}.`,
            notes: 'Fatura Notu',
            submit: 'Kes',
            // What an internet sale's e-Archive lacks, one by one (ADR 0008 §11).
            missing: {
                website: 'satışın yapıldığı web adresi',
                payment_method: 'ödeme şekli',
                paid_at: 'ödeme tarihi',
                carrier_tax_number: 'taşıyıcı firmanın vergi numarası; kendi anlaşmanızla gönderdiyseniz kargo kaydında kurye firmasını seçin',
                shipped_at: 'gönderim tarihi',
            },
            blocked: {
                unmatched_lines: 'Fatura kesmeden önce Özet sekmesinde eşleşmeyen kalemleri bir ürünle eşleştirin.',
                nothing_to_bill: 'Bu siparişteki tüm kalemler iptal edildi veya tedarik edilemedi; faturalanacak bir şey kalmadı.',
                buyer_unidentified: 'Kurumsal fatura için alıcının vergi numarası gerekir. Müşteri kaydına ekleyin:',
                invalid_tax_number: 'Alıcının vergi veya TC kimlik numarası doğrulama basamağını tutturmuyor. Müşteri kaydında düzeltin:',
                invalid_lines: 'Bu siparişte belgeye giremeyecek bir kalem var — KDV oranlarını ve indirimleri kontrol edin.',
                exemption_required: 'Bir satırda KDV yok. Mevzuat bunun için kodlu bir gerekçe istiyor ve pazaryeri göndermedi.',
                internet_sale_incomplete: 'Bu internet satışının e-Arşiv faturası için yasanın istediği bir bilgi eksik:',
            },
            previewHint: 'Faturada yer alacaklar',
            previewItem: 'Kalem',
            previewQuantity: 'Adet',
            previewTaxRate: 'KDV %',
            previewTaxRateFor: (line: string) => `${line} için KDV oranı`,
            taxRateSource:
                'KDV oranları siparişin geldiği satış kanalından gelir; kargo satırı siparişteki en yüksek oranı alır. Satışa uymuyorsa fatura kesilmeden önce burada değiştirin.',
            previewTotal: 'Toplam',
            excludedLines: (count: number) => `${count} satır faturaya girmedi: tedarik edilmedi.`,
            issued: (serial: string) => `${serial} numarasıyla kesildi`,
            existing: 'Bu siparişin faturası kesildi.',
            notIssued: 'Bu Sipariş İçin Henüz Fatura Kesilmedi',
            view: 'Faturayı Aç',
        },

        settings: {
            title: 'Fatura Ayarları',
            subtitle: 'Belgelerinizi kim kesiyor ve belge neler taşıyor.',
            tabs: {
                providers: 'e-Fatura Hizmetleri',
                templates: 'Şablonlar',
            },
            addProvider: 'e-Fatura Hizmeti Ekle',
            providers: 'e-Fatura Hizmetleri',
            connected: 'Bağlı',
            notReady: 'Henüz Kullanılamıyor',
            connect: 'Bağla',
            verify: 'Bağlantıyı Kontrol Et',
            disconnect: 'Bağlantıyı Kaldır',
            confirmDisconnect: {
                title: 'e-Fatura Hizmeti Bağlantısı Kaldırılacak',
                body: 'Bu hizmetle yeni belge kesilmez. Kesilmiş belgeler kalır. İade ve iptal bundan sonra hizmetin kendi panelinden yapılır.',
                action: 'Bağlantıyı Kaldır',
            },
            default: 'Varsayılan',
            // What the service is called where the connection that filed a
            // document has since been withdrawn and cannot be named.
            providerFallback: 'e-Fatura hizmetinizin',
            makeDefault: 'Varsayılan Yap',
            issuesEInvoice: 'e-Fatura keser',
            issuesEArchive: 'e-Arşiv keser',
            empty: 'Henüz Bağlı Bir e-Fatura Hizmeti Yok',
            emptyHint: 'Faturalar bir e-Fatura hizmeti üzerinden kesilir. Siparişlerinizi buradan faturalamak için hizmetinizi bağlayın.',
            form: {
                title: (provider: string) => `${provider} Bağlantısı`,
                settingsHint: 'Bu hizmetin belgeyi nasıl keseceğini belirleyen seçimler. Boş bırakılanlar hizmetin kendi varsayılanını alır.',
                editTitle: (connection: string) => `${connection} Bağlantısını Düzenle`,
                secretsNote:
                    'Kayıtlı şifre ve anahtarlar saklanır, gösterilmez. Değiştirmek için hepsini yeniden girin; boş bırakırsanız olduğu gibi kalır.',
                save: 'Kaydet ve Kontrol Et',
                documents: 'Kestiği Belgeler',
                documentsHint:
                    'Belge türünü alıcı belirler: e-Fatura mükellefine e-Fatura, diğer alıcılara e-Arşiv kesilir. Bu hizmetle kesmediğiniz belgeyi kapatın; o belge, onu kesen hizmetiniz üzerinden kesilir.',
            },
            edit: 'Düzenle',
            moreActions: 'Diğer İşlemler',
        },

        templates: {
            title: 'Fatura Şablonları',
            subtitle: 'Belgenin satışın dışında taşıdığı bilgiler.',
            add: 'Yeni Şablon',
            name: 'Şablon Adı',
            sellerFromCompany: 'Belgedeki satıcı bilgileri Firma Bilgileri’nden gelir.',
            editCompany: 'Firma Bilgilerini Düzenle',
            sellerLegalName: 'Yasal Unvan',
            sellerTaxNumber: 'Vergi Numarası',
            sellerTaxOffice: 'Vergi Dairesi',
            sellerAddress: 'Adres',
            note: 'Not',
            footer: 'Dipnot',
            channel: 'Yalnızca Bu Kanal İçin',
            allChannels: 'Tüm Kanallar',
            isDefault: 'Varsayılan Olarak Kullan',
            save: 'Kaydet',
            delete: 'Sil',
            confirmDelete: {
                title: 'Şablon Silinecek',
                body: 'Bu şablonla kesilmiş belgeler değişmez.',
                action: 'Sil',
            },
            empty: 'Henüz Şablon Yok',
            emptyHint: 'Şablon eklenene kadar belgeler logosuz ve dipnotsuz kesilir.',
        },
    },

    support: {
        title: 'Destek Oturumu',
        working: 'Hesap açılıyor…',
        failed: 'Bu destek bağlantısı artık geçerli değil. Yenisini isteyin.',
    },

    team: {
        title: 'Ekip',
        subtitle: 'Bu firmadaki kişiler ve her birinin neleri yapabildiği.',
        members: 'Kişiler',
        you: 'siz',
        role: 'Rol',
        status: {
            invited: 'Davet Edildi',
            disabled: 'Kapatıldı',
            active: 'Aktif',
        },
        lastSeen: (when: string) => `Son giriş ${when}`,
        neverSignedIn: 'Henüz giriş yapmadı',
        disable: 'Kapat',
        enable: 'Aç',

        pending: 'Kabul Bekleyenler',
        expires: (when: string) => `${when} tarihinde geçersiz olur`,
        revoke: 'Geri Al',

        invite: 'Birini Davet Et',
        email: 'E-posta',
        emailHint: 'Yedi gün geçerli bir bağlantı alır.',
        chooseRole: 'Rol Seçin',
        sendInvite: 'Daveti Gönder',
        invited: 'Davet gönderildi.',

        roles: 'Roller',
        rolesHint:
            'Bir rolün izinleri, o rolü taşıyan herkesin yapabilecekleridir. Hazır roller tüm firmalarda ortaktır ve değiştirilemez — kendi rolünüzü oluşturun.',
        grantableHint: 'Yalnızca sizde olan izinleri verebilirsiniz; sizden fazla izni olan bir rolü de değiştiremezsiniz.',
        builtin: 'Hazır',
        memberCount: (count: number) => `${count} kişi`,
        permissionCount: (count: number) => `${count} izin`,
        newRole: 'Yeni Rol',
        roleName: 'Rol Adı',
        roleDescription: 'Açıklama',
        deleteRole: 'Sil',
        confirmDeleteRole: {
            title: 'Rol Silinecek',
            body: 'Bu rolü taşıyan kimse kalmamalı. Yerleşik roller silinemez.',
            action: 'Sil',
        },

        module: {
            identity: 'Firma ve Kişiler',
            catalog: 'Ürünler',
            channel: 'Satış Kanalları',
            listing: 'Listelemeler',
            inventory: 'Stok',
            pricing: 'Fiyatlama',
            order: 'Siparişler',
            invoice: 'Faturalama',
            billing: 'Abonelik',
            analytics: 'Raporlar',
        },
    },

    verifyEmail: {
        title: 'E-posta Doğrulama',
        banner: 'E-posta adresiniz henüz doğrulanmadı. Gelen kutunuzdaki bağlantıya tıklayın.',
        resend: 'Yeniden Gönder',
        sent: 'Doğrulama e-postası gönderildi. Gelen kutunuzu kontrol edin.',
        sendFailed: 'E-posta gönderilemedi. Daha sonra tekrar deneyin.',
        done: 'E-posta adresiniz doğrulandı.',
        invalid: 'Bu doğrulama bağlantısı artık geçerli değil. Panelden yenisini isteyin.',
        continue: 'Panele Git',
    },
    invite: {
        title: 'Davet',
        heading: (company: string) => `${company} Ekibine Katılın`,
        sentTo: (email: string) => `Bu davet ${email} adresine gönderildi.`,
        existingAccount: 'Zaten bir hesabınız var. Kabul ettiğinizde bu firma hesabınıza eklenir.',
        accept: 'Kabul Et ve Katıl',
        invalid: 'Bu davet artık geçerli değil. Yenisini isteyin.',
    },

    permissions: {
        'tenant.read': 'Firma Bilgilerini Görüntüle',
        'tenant.write': 'Firma Bilgilerini Düzenle',
        'user.read': 'Firmadaki Kişileri Gör',
        'user.write': 'Kişi Davet Et, Rol Değiştir, Hesap Kapat',
        'role.write': 'Rol Oluştur ve Düzenle',
        'audit.read': 'Denetim Kaydını Görüntüle',
        'api_token.read': 'API Anahtarlarını Gör',
        'api_token.write': 'API Anahtarı Oluştur ve İptal Et',
        'product.read': 'Ürünleri Görüntüle',
        'product.write': 'Ürün Oluştur ve Düzenle',
        'product.delete': 'Ürün Sil',
        'category.write': 'Kategorileri Yönet',
        'feed.read': 'Tedarikçileri ve Kataloglarını Görüntüle',
        'feed.write': 'Tedarikçi Ekle ve Kataloglarını İçe Aktar',
        'channel.read': 'Satış Kanallarını Görüntüle',
        'channel.write': 'Satış Kanalı Bağla ve Ayarla',
        'channel.credentials.write': 'Kanal Erişim Bilgilerini Ekle veya Değiştir',
        'listing.read': 'Kanal Listelemelerini Görüntüle',
        'listing.publish': 'Kanala Ürün Gönder ve Güncelle',
        'inventory.read': 'Stok Seviyelerini Görüntüle',
        'inventory.write': 'Stok Seviyelerini Değiştir',
        'price.write': 'Fiyatları Değiştir',
        'price_rule.read': 'Fiyat Kurallarını Gör',
        'price_rule.write': 'Fiyat Kuralı Ekle, Uygula ve Geri Al',
        'storefront.read': 'Mağazayı, İndirim Kodlarını, Yorumları ve Bülteni Gör',
        'storefront.write': 'Mağazayı, İndirim Kodlarını, Yorumları ve Bülteni Değiştir',
        'webhook.read': 'Webhook’ları Gör',
        'webhook.write': 'Webhook Ekle, Değiştir ve Sil',
        'order.read': 'Siparişleri Görüntüle',
        'order.write': 'Siparişlerde İşlem Yap — Paketle, Böl, Gönder',
        'order.cancel': 'Sipariş Kalemi İptal Et',
        'claim.write': 'İade Taleplerini Onayla veya Reddet',
        'invoice.read': 'Faturaları Görüntüle',
        'invoice.write': 'Fatura Kes',
        'billing.read': 'Aboneliği Görüntüle',
        'billing.write': 'Aboneliği Değiştir',
        'report.read': 'Ciro ve Raporları Gör',
    },

    storefront: {
        title: 'Tezgah',
        subtitle: 'Siteniz, pazaryerleriyle aynı katalogdan, sizin fiyatlarınızla satar.',
        noChannel: (ownStore: string) => `Önce ${ownStore} Mağazanızı Kurun`,
        noChannelHint: 'İndirim kodları mağazanızda kullanılır. Önce mağazanızı kurun.',
        settings: 'Mağaza Ayarları',
        channel: 'Hangi Bağlantı',
        name: 'Mağaza Adı',
        slug: 'Adres',
        slugHint: 'Küçük harf, rakam ve tire. Mağazanız bu adreste hemen açılır.',
        domain: 'Kendi Alan Adınız',
        domainHint:
            'İsteğe bağlı. Kaydettikten sonra burada gösterilen DNS kaydıyla doğrularsınız; o zamana kadar mağazanız ve bağlantıları yukarıdaki mağaza adresinizde kalır.',
        domainVerification: {
            verified: 'Doğrulandı',
            unverified: 'Doğrulama Bekliyor',
            verifiedNote: 'Mağazanızın e-postalardaki, faturalardaki ve bu paneldeki bağlantıları bu alan adını kullanır.',
            instructions: (type: string) =>
                `Alan adınızın DNS ayarlarına aşağıdaki ${type} kaydını ekleyin, ardından Alan Adını Doğrula’ya basın. Yeni bir kaydın görünmesi bir saati bulabilir.`,
            recordName: 'Kayıt Adı',
            recordNameHint: 'Bazı DNS panelleri alan adınızı kendisi ekler; o durumda yalnızca alan adınızdan önceki kısmı yazın.',
            recordValue: 'Kayıt Değeri',
            copy: { copy: 'Kopyala', copied: 'Kopyalandı' },
            verify: 'Alan Adını Doğrula',
            done: 'Alan adınız doğrulandı.',
        },
        description: 'Mağaza Hakkında',
        descriptionHint:
            'Hakkımızda sayfasında görünür. SEO sekmesinde ana sayfa açıklaması yazmadıysanız ana sayfanın arama sonucunda da bu metin kullanılır.',
        theme: {
            appearanceHint:
                'Beş hazır görünümden birini seçin ya da kendi renginizi girin. Her seçim sağdaki önizlemeye anında yansır; mağazanız yayınladığınızda değişir.',
            presets: 'Hazır Görünümler',
            presetsHint: 'Renk, yazı ve köşe birlikte gelir; sonra dilediğinizi değiştirin.',
            preset: {
                turuncu: 'Turuncu',
                kiraz: 'Kiraz',
                gece: 'Gece',
                orman: 'Orman',
                antrasit: 'Antrasit',
            },
            custom: 'Özel Renk',
            customHint: 'Butonlar, bağlantılar ve rozetler bu rengi kullanır. Üzerindeki yazı rengini okunabilirliğe göre biz seçeriz.',
            contrast: (ink: string) => (ink === '#FFFFFF' ? 'Butonlardaki yazı beyaz olacak.' : 'Butonlardaki yazı koyu gri olacak.'),
            contrastWarning:
                'Bu renk beyaz yazıyla yeterince okunmuyor; butonlarda koyu gri yazı kullanılacak ve yazı okunsun diye buton gerekirse bir ton açık çizilecek. Daha koyu bir ton seçmenizi öneririz.',
            font: 'Yazı Tipi',
            fontOptions: {
                outfit: 'Outfit (Varsayılan)',
                manrope: 'Manrope',
                dm_sans: 'DM Sans',
                nunito_sans: 'Nunito Sans',
            },
            fontScale: 'Yazı Boyutu',
            scaleOptions: { small: 'Küçük', normal: 'Normal', large: 'Büyük' },
            radius: 'Köşe Yuvarlaklığı',
            radiusOptions: {
                sharp: 'Keskin',
                soft: 'Yumuşak (Varsayılan)',
                round: 'Yuvarlak',
            },
            density: 'Yoğunluk',
            densityOptions: {
                comfortable: 'Ferah',
                normal: 'Normal',
                compact: 'Sıkı',
            },
            gridColumns: 'Masaüstünde Sütun Sayısı',
            gridColumnsHint: 'Ürün listesinde yan yana kaç kart. Telefonda her zaman 2.',
            headerStyle: 'Üst Bar',
            headerOptions: { white: 'Beyaz', colored: 'Vurgu Rengi' },
            buttonStyle: 'Buton Stili',
            buttonOptions: { filled: 'Dolu', outline: 'Çerçeveli' },
            logo: 'Logo',
            logoHint: 'PNG veya WebP; 36 piksel yüksekliğe sığdırılır. Yoksa mağaza adı yazılır.',
            logoFormat: 'Logo için PNG veya WebP dosyası seçin.',
            favicon: 'Site Simgesi (Favicon)',
            faviconHint: 'Kare PNG, en az 48×48; 64×64 yeterli.',
            faviconFormat: 'Site simgesi için PNG dosyası seçin.',
            remove: 'Kaldır',
            reset: 'Varsayılana Dön',
            preview: 'Önizleme',
            previewButton: 'Sepete Ekle',
            previewText: 'Ahşap Eğitici Puzzle 24 Parça',
            previewNote: 'Ücretsiz kargo · 2 iş gününde teslim',
            // The miniature shop drawn beside the settings. Every word in it is a
            // stand-in for the seller's own, so all of it is translated.
            previewShop: 'Mağaza',
            previewCart: 'Sepet',
            previewAll: 'Tümü',
            previewBrand: 'Marka',
            previewCategories: ['Oyuncak', 'Bebek', 'Puzzle'],
            previewRating: '4,6 (128)',
            newShop: 'Önce mağazayı kaydedin; görünümü ardından burada düzenleyip yayınlayabilirsiniz.',
            publish: 'Yayınla',
            saveDraft: 'Taslağı Kaydet',
            discard: 'Taslağı Sil',
            unsaved: 'Görünümde kaydedilmemiş değişiklikler var. Mağazanız yayındaki görünümü göstermeye devam ediyor.',
            draftWaiting: (at: string) =>
                `${at} tarihinde kaydedilen taslak yayınlanmayı bekliyor. O zamana kadar mağazanız yayındaki görünümü gösterir.`,
            upToDate: 'Mağazanız bu görünümü gösteriyor.',
            publishedNotice: 'Yayınlandı. Mağazanız yeni görünümü gösteriyor.',
            draftSaved: 'Taslak kaydedildi. Mağazanızda bir şey değişmedi.',
            discarded: 'Taslak silindi.',
            restored: 'Geri yüklendi. Mağazanız bu görünümü yeniden gösteriyor.',
            confirmDiscard: {
                title: 'Taslak Silinecek',
                body: 'Yayınlanmamış değişiklikler silinir. Mağazanız yayındaki görünümünü korur.',
                action: 'Sil',
            },
            confirmRestore: {
                title: 'Görünüm Geri Yüklenecek',
                body: 'Bu görünüm hemen yeniden yayınlanır ve mağazanız onu gösterir.',
                bodyWithDraft: 'Bu görünüm hemen yeniden yayınlanır ve mağazanız onu gösterir. Yayınlanmamış değişiklikleriniz silinir.',
                action: 'Geri Yükle',
            },
            history: 'Yayın Geçmişi',
            historyHint: 'Yayınlanan son 10 görünüm. Birini geri yüklediğinizde hemen yeniden yayınlanır.',
            historyEmpty: 'Henüz burada yayınlanmış bir görünüm yok. İlk yayında mevcut görünüm de buraya eklenir.',
            live: 'Yayında',
            restore: 'Geri Yükle',
        },
        shippingFee: 'Kargo Ücreti',
        freeShippingOver: 'Şu Tutarın Üzerinde Kargo Bedava',
        freeShippingHint: 'Boş bırakırsanız kargo her zaman ücretlidir.',
        desiBands: {
            title: 'Desiye Göre Kargo Ücreti',
            none: 'Tanımlı değil. Her sepete Kargo Ücreti uygulanır.',
            step: (desi: string, fee: string) => `${desi} desiden ${fee}`,
            stepNumber: (n: number) => `${n}. Aralık`,
            edit: 'Desi Aralıklarını Düzenle',
            hint: 'Sepet, desisinin ulaştığı aralığın ücretini öder: her satırın desisi ya da kilogram cinsinden ağırlığından büyük olanı adetle çarpılıp toplanır. İlk aralığın altında kalan ya da desisi ve ağırlığı girilmemiş bir ürün içeren sepete Kargo Ücreti uygulanır. Ücretsiz kargo tutarı ve ücretsiz kargo kodları geçerliliğini korur. Değişiklikler sayfayla birlikte kaydedilir.',
            fromDesi: 'Başlangıç Desisi',
            fee: 'Ücret',
            add: 'Aralık Ekle',
            remove: 'Kaldır',
            apply: 'Uygula',
        },
        couriers: 'Mağazada Görünen Kargo Firmaları',
        couriersNone: 'Henüz kargo firması seçilmedi; ödeme adımında "Standart Kargo" yazar.',
        couriersEdit: 'Kargo Firmalarını Seç',
        dispatchDays: 'Kargoya Verme (gün)',
        dispatchDaysHint:
            'Sipariş kaç iş günü içinde kargoya verilir. 0 = aynı gün. Kendi Kargoya Hazırlık süresi olan ürün o süreyle kargoya verilir.',
        cutOffHour: 'Son Sipariş Saati',
        cutOffHourHint:
            "Bu saatten önceki siparişler o gün, sonrakiler bir sonraki kargo çıkış gününde işleme alınır. Örnek: 16. Boş bırakırsanız her sipariş verildiği gün işleme alınır; aynı gün kargo için saat girmeniz gerekir. Arife günlerinde ve 28 Ekim'de siparişler öğlen 12:00'ye kadar o gün sayılır.",
        transitDays: 'Kargo Süresi (gün)',
        transitDaysHint: 'Kargonun alıcıya ulaşması kaç gün sürer. Kargo firmaları pazartesiden cumartesiye dağıtım yapar.',
        dispatchWeekdays: 'Kargo Çıkış Günleri',
        dispatchWeekdaysHint: 'Paketleri kargoya teslim ettiğiniz günler. Teslim tarihleri yalnızca bu günleri sayar, resmi tatilleri saymaz.',
        weekdayNames: { 1: 'Pazartesi', 2: 'Salı', 3: 'Çarşamba', 4: 'Perşembe', 5: 'Cuma', 6: 'Cumartesi' },
        dispatchLastDay: 'En az bir gün seçili kalmalı.',
        slowerDispatch: (count: number, names: string) =>
            `Satıştaki ${count} ürünün kendi Kargoya Hazırlık süresi mağazanınkinden uzun (${names}). Bu ürünler için doğru olmayacağından ana sayfadaki güven satırı kargo süresini göstermiyor.`,
        deliveryText: 'Teslimat Notu',
        deliveryTextHint: 'Teslim tarihi hesaplanamadığında ürün sayfasında ve ödeme adımında görünür. En fazla 80 karakter.',
        deliveryTextPlaceholder: '2–4 iş gününde kargoda',
        contactPhone: 'İletişim Telefonu',
        contactEmail: 'İletişim E-postası',
        address: 'İşletme Adresi',
        published: 'Mağaza Aktif',
        accounts: 'Müşteri Hesapları',
        accountsHint:
            'Alışveriş yapanlar üye olup siparişlerini görebilir, adreslerini kaydedebilir ve iade talebi oluşturabilir. Misafir olarak alışveriş her zaman açık kalır.',
        basketReminders: 'Sepet Hatırlatması',
        basketRemindersHint:
            'Sepetinde ürün bırakan üyeye bir gün sonra tek bir e-posta gönderilir. Bu bir ticari iletidir: yalnızca mağazanızdan e-posta almayı kabul eden üyelere gider ve her birinde almayı bırakma bağlantısı bulunur.',
        saved: 'Kaydedildi.',
        visit: 'Mağazayı Sitede Gör',
        maintenance: {
            title: 'Bakım Modu',
            hint: 'Açıkken mağazanızın her sayfası mesajınızı gösterir ve sipariş alınmaz. Arama motorlarına mağazanın geri döneceği söylenir, böylece sıralamanız korunur. Mağazayı kapatırsanız ise mağaza tümden kaybolur.',
            on: (since: string) => `Mağazanız ${since} tarihinden beri bakım modunda. Ziyaretçiler şu mesajı görüyor:`,
            off: 'Mağazanız açık.',
            offClosed: 'Mağazanız kapalı, kimse ulaşamıyor. Bakım modu mağaza açıkken anlam kazanır.',
            switchOn: 'Bakım Modunu Aç',
            switchOff: 'Bakım Modunu Kapat',
            preview: 'Mağazayı Önizle',
            modalHint:
                'Bakım modunu kapatana kadar mağazanızın her sayfası bu mesajı gösterir. Mağazayı Önizle ile mağazanızı siz görmeye devam edersiniz.',
            message: 'Bakım Mesajı',
            messageHint: 'En fazla 280 karakter; örneğin ne zaman döneceğiniz. Boş bırakırsanız mağaza kısa süre sonra döneceğini söyler.',
        },
        legal: {
            hint: 'Şablonlar işletme bilgilerinizle dolar ve genel bir başlangıçtır. Mağazanıza göre düzenleyin; emin olmadığınız yerde bir hukukçuya danışın.',
            useTemplate: 'Şablonla Doldur',
            confirmReplace: {
                title: 'Metin Şablonla Değiştirilecek',
                body: 'Şu anki metin silinir, yerine şablon gelir. Kaydetmeden önce sayfayı yenilerseniz eski metin geri gelir.',
                action: 'Şablonla Değiştir',
            },
        },
        publishCheck: {
            missing: 'Mağazayı yayına almadan önce şunları tamamlayın:',
            liveMissing: 'Mağazanız yayında ama yasal olarak gereken bilgiler eksik:',
            taxId: 'Vergi No veya MERSİS No',
            contact: 'İletişim E-postası veya Telefonu',
        },
        sections: {
            general: 'Genel',
            appearance: 'Görünüm',
            sales: 'Kargo, Ödeme ve İade',
            shipping: 'Kargo ve Para Birimi',
            payment: 'Ödeme',
            paymentHint: 'Müşteri ödemeyi nasıl yapabilir. Kart ödemesi için ileride bir ödeme hizmeti bağlanır.',
            returns: 'İade',
            legal: 'Yasal Metinler',
            business: 'İşletme ve İletişim',
            businessHint: 'Hakkımızda sayfasında ve yasal metinlerde satıcı bilgisi olarak görünür.',
            sms: 'SMS',
            seo: 'SEO',
            seoHint:
                'Yalnız ana sayfanın başlığını ve açıklamasını değiştirir; arama motorları ana sayfanızı bunlarla listeler. Ürün, kategori ve diğer sayfalar kendi adlarını ve açıklamalarını kullanır. Mağazadaki yazıları değiştirmez.',
        },
        showcase: {
            title: 'Vitrin',
            hint: 'Mağazanın üst menüsü, ana sayfadaki koleksiyonlar ve kampanya afişleri. Boş bırakırsanız kategoriler menüde, standart vitrinler ana sayfada görünür.',
            navigation: 'Üst Menü',
            navigationHint:
                'Bağlantı bir mağaza yolu (/kategori/giyim, /ara?q=kupa, /koleksiyon/yaz) ya da https:// adresi olabilir. Boşsa üst kategoriler gösterilir.',
            navLabel: 'Etiket',
            navHref: 'Bağlantı',
            navAdd: 'Menü Öğesi Ekle',
            shortcuts: 'Kategori Kısayolları',
            shortcutsHint: 'Ana sayfada kampanya afişlerinin altındaki görselli kategoriler. En fazla 8; seçmezseniz ilk 8 üst kategori gösterilir.',
            shortcutPick: 'Kategori Seçin…',
            shortcutAdd: 'Kısayol Ekle',
            shortcutImage: 'Görsel',
            shortcutImageHint: 'Seçmezseniz kategorinin fotoğraflı ilk ürünü gösterilir. En az 300 piksel; JPEG, PNG veya WebP.',
            shortcutImageRemove: 'Görseli Kaldır',
            collections: 'Koleksiyonlar',
            collectionsHint: 'Ana sayfada bir vitrin ve /koleksiyon/<adres> altında kendi sayfası. Kategori seçmezseniz tüm ürünler.',
            collectionTitle: 'Başlık',
            collectionCategory: 'Kategori',
            collectionAllCategories: 'Tüm Ürünler',
            collectionSort: 'Sıralama',
            collectionAdd: 'Koleksiyon Ekle',
            sortDefault: 'Öne Çıkanlar',
            sortPriceAsc: 'Fiyat: Düşükten Yükseğe',
            sortPriceDesc: 'Fiyat: Yüksekten Düşüğe',
            sortNewest: 'En Yeniler',
            sortDiscount: 'En Çok İndirim',
            banners: 'Kampanya Afişleri',
            bannersHint: 'Ana sayfanın en üstündeki kaydırmalı alan; en fazla 5 afiş. Görsel yüklemezseniz vurgu rengi kullanılır.',
            bannerTitle: 'Başlık',
            bannerSubtext: 'Alt Metin',
            bannerHref: 'Bağlantı',
            bannerImage: 'Görsel',
            bannerImageHint: 'Başlıksız afiş 3:1, başlıklı afiş 4:3 gösterilir; 2560 piksel genişlik önerilir.',
            bannerAlt: 'Görsel Açıklaması',
            bannerAltHint:
                'Görselin ne gösterdiğini ya da üzerindeki yazıyı yazın. Görseli göremeyen ziyaretçiler ve arama motorları onu bu metinle tanır; başlığı olmayan afişte zorunludur.',
            bannerAdd: 'Afiş Ekle',
            bannerMobileImage: 'Telefon Görseli',
            bannerMobileImageHint: 'İsteğe bağlı; telefonda 4:3 gösterilir, 1200×900 piksel önerilir.',
            bannerMobileRemove: 'Telefon Görselini Kaldır',
            previewDesktop: 'Masaüstü',
            previewPhone: 'Telefon',
            remove: 'Sil',
            moveUp: 'Yukarı',
            uploadFailed: 'Görsel yüklenemedi.',
        },
        paymentCod: 'Kapıda Ödeme',
        paymentBankTransfer: 'Havale/EFT',
        paymentCard: 'Kart ile Ödeme',
        paymentCardAwaiting: 'Bir ödeme hizmeti bağlanıp kontrol edildiğinde müşterilere sunulur.',
        paymentService: {
            title: 'Ödeme Hizmeti',
            hint: 'Kartla ödeme, kendi üye iş yeri hesabınızla bağladığınız hizmet üzerinden alınır. Anahtarlarınız şifrelenerek saklanır ve bir daha gösterilmez.',
            add: 'Ödeme Hizmeti Bağla',
            empty: 'Henüz bağlı bir ödeme hizmeti yok.',
            callbackUrl: (name: string) => `${name}: Bildirim Adresi`,
            copy: { copy: 'Kopyala', copied: 'Kopyalandı' },
            connect: 'Bağla',
            connected: 'Bağlı',
            notReady: 'Henüz Kullanılamıyor',
            verify: 'Bağlantıyı Kontrol Et',
            disconnect: 'Bağlantıyı Kaldır',
            confirmDisconnect: {
                title: 'Ödeme Hizmeti Bağlantısı Kaldırılacak',
                body: 'Yeni bir hizmet bağlanana kadar müşterilere kartla ödeme sunulmaz. Alınmış ödemeler etkilenmez.',
                action: 'Bağlantıyı Kaldır',
            },
            formTitle: (service: string) => `${service} Bağlantısı`,
            settingsHint: 'Bu hizmetin ödemeyi nasıl alacağını belirleyen seçimler. Boş bırakılanlar hizmetin kendi varsayılanını alır.',
            problem: {
                unavailable: 'Hizmete şu anda ulaşılamıyor. Birkaç dakika sonra yeniden deneyin.',
                failed: 'Hizmet bu anahtarları kabul etmedi.',
            },
        },
        sms: {
            title: 'SMS Hizmeti',
            hint: 'Alıcılarınıza SMS’ler bağladığınız SMS hizmeti üzerinden, kendi hesabınız ve gönderici adınızla gider. Şifreniz şifrelenerek saklanır ve bir daha gösterilmez.',
            add: 'SMS Hizmeti Bağla',
            empty: 'Henüz bağlı bir SMS hizmeti yok.',
            connect: 'Bağla',
            notReady: 'Kullanılamıyor',
            verify: 'Bağlantıyı Kontrol Et',
            test: 'Test SMS Gönder',
            moreActions: 'SMS hizmeti için diğer işlemler',
            edit: 'Düzenle',
            disconnect: 'Bağlantıyı Kaldır',
            confirmDisconnect: {
                title: 'SMS Hizmetinin Bağlantısı Kaldırılacak',
                body: 'Yeniden bir hizmet bağlanana kadar alıcılarınıza SMS gönderilmez. Gönderilmiş SMS’ler etkilenmez.',
                action: 'Bağlantıyı Kaldır',
            },
            formTitle: (service: string) => `${service} Bağla`,
            editTitle: (name: string) => `${name} Düzenle`,
            settingsHint: 'SMS’lerinizin gideceği gönderici adı, SMS hizmetinizin onayladığı biçimde.',
            secretsNote: 'Yeni bir şifre yazmazsanız kayıtlı şifreniz korunur.',
            save: 'Kaydet',
            lastError: (when: string) => `Son gönderilemeyen SMS, ${when}:`,
            problem: {
                unavailable: 'Hizmete ulaşılamadı. Birkaç dakika sonra yeniden deneyin.',
                failed: 'Hizmet bu bağlantıyı kabul etmedi.',
            },
            testTitle: 'Test SMS Gönder',
            testHint: 'Yazdığınız numaraya hemen bir test cümlesi gönderilir. Kendi cep telefonu numaranızı yazın.',
            testPhone: 'Cep Telefonu',
            testSend: 'Gönder',
            testFailed: 'Test SMS gönderilemedi:',
            messages: 'Gönderilen SMS’ler',
            messagesHint: 'Son SMS’ler, numaraları gizlenmiş olarak; metnin kendisi saklanmaz.',
            messagesEmpty: 'Henüz SMS gönderilmedi.',
            kind: { order_received: 'Sipariş Alındı', dispatched: 'Kargoya Verildi', claim_decided: 'Talep Sonuçlandı', test: 'Test' },
            status: { pending: 'Gönderiliyor', sent: 'Gönderildi', failed: 'Gönderilemedi' },
            switches: 'Alıcılara SMS',
            switchesHint: 'Her SMS hesabınızdan bir SMS harcar. Yalnız siparişle ilgili bilgi gider; reklam gönderilmez.',
            switchesNeedService: 'Alıcılarınıza SMS göndermek için önce yukarıdan bir SMS hizmeti bağlayın.',
            orderReceived: 'Sipariş Alındı',
            orderReceivedHint: 'Sipariş verilir verilmez sipariş numarası ve takip bağlantısı.',
            dispatched: 'Kargoya Verildi',
            dispatchedHint: 'Kargo yola çıktığında, kargo firmasının takip bağlantısıyla.',
            claimDecided: 'Talep Sonuçlandı',
            claimDecidedHint: 'İade ya da iptal talebi onaylandığında veya kabul edilmediğinde.',
        },
        paymentAwaitingIban: 'IBAN Bekleniyor',
        // Without an account the shopper is told to pay and never told where,
        // so the order waits for money that cannot arrive.
        bank: 'Havale Hesabı',
        bankHint: 'Havale/EFT seçen müşteriye sipariş sonunda ve e-postada bu hesap gösterilir.',
        bankAccountName: 'Hesap Sahibi',
        bankName: 'Banka',
        bankIban: 'IBAN',
        bankIbanHint: 'TR ile başlayan 26 karakter.',
        bankMissing:
            'Havale/EFT, aşağıdaki hesap bilgileri girildiğinde mağazada gösterilir. Bu bilgiler olmadan müşteri ödemeyi nereye yapacağını göremez.',
        paymentLastMethod: 'Müşterinin ödeyebilmesi için en az bir yöntem açık kalmalı.',
        currency: 'Para Birimi',
        announcementEnabled: 'Duyuru Çubuğunu Göster',
        announcementText: 'Duyuru Metni',
        returnDays: 'İade Süresi (Gün)',
        returnDaysHint: '0 bırakırsanız iade rozeti gösterilmez. Örn. 14 → “14 gün içinde iade”.',
        whatsappHint: 'Telefon numarası (0535 123 45 67) ya da wa.me bağlantısı.',
        socialHint: 'Kullanıcı adınız (ör. magazaniz) ya da profil adresiniz.',
        policyReturns: 'İade Politikası Metni',
        policyShipping: 'Kargo Politikası Metni',
        distanceSalesTerms: 'Mesafeli Satış Sözleşmesi',
        privacyNotice: 'Gizlilik Politikası',
        legalHint: 'Türkiye’de internetten satış yapan her mağazada bulunması zorunludur.',
        legalName: 'Yasal Unvan',
        taxOffice: 'Vergi Dairesi',
        taxNumber: 'Vergi Numarası',
        businessFromCompany: 'Yasal unvan, vergi dairesi ve vergi numarası Firma Bilgileri’nden gelir.',
        editCompany: 'Firma Bilgilerini Düzenle',
        mersis: 'MERSİS No',
        tradeRegistryNo: 'Ticaret Sicil No',
        kep: 'KEP Adresi',
        kepHint: 'Kayıtlı elektronik posta adresiniz. Varsa yayımlanması zorunludur; yoksa boş bırakın.',
        metaTitle: 'Ana Sayfa Başlığı',
        metaTitleHint:
            'Mağazanızın adı ve ne sattığınız, 60 karakteri geçmeden: “Mavi Atölye — El Yapımı Seramik”. Anahtar kelime listesi yazmayın. Boşsa mağaza adı kullanılır.',
        metaDescription: 'Ana Sayfa Açıklaması',
        metaDescriptionHint:
            'Ana sayfada ne bulunacağını bir iki cümleyle anlatın, 160 karakteri geçmeden; uzun metin arama sonucunda kesilir. Boşsa Mağaza Hakkında metni kullanılır.',
        dangerZone: 'Tehlikeli Alan',
        delete: 'Mağazayı Sil',
        deleteHint: 'Mağaza ve indirim kodları kapanır, adresi ve verileri gizlenir. Aynı adresle yeniden açabilirsiniz.',
        confirmDelete: {
            title: 'Mağaza Silinecek',
            body: 'Mağaza ve indirim kodları kapanır, adresi ve verileri gizlenir. Aynı adresle yeniden açabilirsiniz.',
            action: 'Mağazayı Sil',
        },
    },

    shipping: {
        awaitingTransfer: 'Bu siparişin havalesi henüz gelmedi. Ödeme Alındı ile onayladığınızda kargoya verebilirsiniz.',
        title: 'Kargo',
        subtitle: 'Gönderilerinizi kim taşıyor, nereden çıkıyor ve yolda ne var.',
        tabs: {
            shipments: 'Gönderiler',
            carriers: 'Kargo Firmaları',
            origins: 'Çıkış Adresleri',
        },
        couriers: {
            title: 'Çalıştığım Kargo Firmaları',
            hint: 'Kargolarınızı kendi anlaşmanızla gönderirsiniz. Çalıştığınız firmaları seçin: ilki kargo kaydında varsayılan gelir ve mağazanız bu firmaları müşterilerine gösterir.',
            add: 'Kargo Firması Ekle',
            addTitle: 'Kargo Firması Ekle',
            addHint: 'Firmalar adıyla listelenir, hiçbir bağlantı kurulmaz. Takip numarası kuryenizin verdiği numaradır.',
            addOne: 'Ekle',
            allChosen: 'Listedeki bütün firmalar zaten seçili.',
            empty: 'Henüz Kargo Firması Seçilmedi',
            emptyHint: 'Gönderdiğiniz firmaları ekleyin. Kargo kaydında listede olmayan bir kuryeyi de yazabilirsiniz.',
            moveUp: 'Yukarı Taşı',
            remove: 'Kaldır',
        },
        editOrigin: 'Adresi Düzenle',
        removeOrigin: 'Adresi Kaldır',
        removeOriginBody: 'Bu çıkış adresi silinecek. Kargo gönderileri kalan adreslerden yapılır.',
        addOrigin: 'Adres Ekle',
        openParcels: 'Yolda Olanlar',
        nothingOpen: 'Çıkmayı Bekleyen Gönderi Yok',
        default: 'Varsayılan',
        problem: {
            unavailable: 'Kargo firmasına şu anda ulaşılamıyor. Birkaç dakika sonra yeniden deneyin.',
            unsupported: 'Bu kargo firması bu işlemi yapamıyor.',
            failed: 'Kargo firması işlemi tamamlayamadı. Birkaç dakika sonra yeniden deneyin.',
        },

        origins: 'Gönderilerin Çıktığı Adres',
        originsHint: 'Her kargo firması bir çıkış adresine göre kayıt açar. Bu, faturanızdaki adres değildir; ikinci bir depo ikinci bir adrestir.',
        originName: 'Ad',
        contactName: 'Yetkili',
        phone: 'Telefon',
        address: 'Adres',
        district: 'İlçe',
        city: 'İl',

        parcels: 'Gönderiler',
        open: 'Kargo Kaydı Aç',
        openHint: 'Koli hazır olduğunda kaydı açın; takip numarası siparişe ve müşteriye gider.',
        parcelStatus: {
            pending: 'Bekliyor',
            ready: 'Hazır',
            readytoship: 'Kargoya Hazır',
            // The shop's own tokens (ADR 0003 §7), as the seller sets them.
            confirmed: 'Onaylandı',
            packed: 'Paketlendi',
            picking: 'Toplanıyor',
            invoiced: 'Faturalandı',
            shipped: 'Kargoda',
            delivered: 'Teslim Edildi',
            cancelled: 'İptal Edildi',
            returned: 'İade',
            unpacked: 'Paket Bozuldu',
            open: 'Açık',
            packaged: 'Paketlendi',
            package: 'Paketlendi',
            intransit: 'Kargoda',
            cancelledbymerchant: 'Satıcı İptal Etti',
            cancelledbycustomer: 'Müşteri İptal Etti',
            cancelledbysap: 'İptal Edildi',
            claimcreated: 'İade Talebi Açıldı',
            created: 'Sipariş Alındı',
            cancelrequested: 'İptal Talebi Açıldı',
            atstore: 'Mağazada Bekliyor',
            atdeliverypoint: 'Teslimat Noktasında Bekliyor',
            undelivered: 'Teslim Edilemedi',
            unsupplied: 'Tedarik Edilemedi',
            returnrequested: 'İade Talebi Açıldı',
            returnrejected: 'İade Reddedildi',
            returnaccepted: 'İade Onaylandı',
            outfordelivery: 'Dağıtıma Çıktı',
            returnedtoseller: 'Satıcıya Geri Döndü',
        },
        parcelCount: (count: number) => `${count} koli`,
        parcelsLabel: 'Koli Adedi',
        notBooked: 'Kayıt Açılmadı',
        carrier: 'Kargo Firması',
        defaultCarrier: 'Varsayılan Firma',
        trackingNumber: 'Takip Numarası',
        trackingHint: 'Kargocunuzun verdiği numara. Numarayı firma üretiyorsa boş bırakın.',
        courier: 'Kurye Firması',
        courierHint: 'Koliyi taşıyan firma. Fatura, müşteri ve satış kanalı bu firmayı görür.',
        courierPick: 'Seçin',
        courierOther: 'Listede Yok',
        courierGroupChosen: 'Çalıştığım Firmalar',
        courierGroupOther: 'Diğer Firmalar',
        courierName: 'Kurye Firmasının Adı',
        courierTaxNumber: 'Kurye Firmasının Vergi Numarası',
        courierTaxNumberHint: 'e-Arşiv faturasında taşıyıcının 10 haneli vergi numarası yer alır.',
        desi: 'Desi',
        desiHint: 'Türkiye’de kargo desi üzerinden fiyatlanır. Yazmazsanız firma kendi ölçüsüyle fiyatlar.',
        cod: 'Kapıda Ödeme',
        book: 'Kargoyu Kaydet',
        printLabel: 'Yazdır',
        track: 'Takip Et',
        cancel: 'İptal Et',
        confirmCancel: {
            title: 'Kargo Kaydı İptal Edilecek',
            body: 'Takip numarası geçersiz olur. Koli çıktıysa kargo firmasını arayın.',
            action: 'Kaydı İptal Et',
        },

        status: {
            draft: 'Taslak',
            booked: 'Kayıt Açıldı',
            in_transit: 'Yolda',
            delivered: 'Teslim Edildi',
            returned: 'İade',
            cancelled: 'İptal Edildi',
            failed: 'Başarısız',
        },
    },

    claims: {
        pageTitle: 'İadeler',
        subtitle: 'Müşterilerin geri gönderdikleri ve sizin verdiğiniz kararlar.',
        title: 'İade ve Talepler',
        empty: 'Henüz İade Yok',
        emptyHint: 'Pazaryerinde veya mağazanızda açılan iadeler burada görünür.',
        kindLabel: 'Tür',
        kind: {
            return: 'İade',
            cancellation: 'İptal',
            missing: 'Ulaşmadı',
            damaged: 'Hasarlı Geldi',
            other: 'Diğer',
            exchange: 'Değişim',
        },
        status: {
            all: 'Tümü',
            open: 'Sizi Bekliyor',
            approved: 'Onaylandı',
            rejected: 'Reddedildi',
            resolved: 'Tamamlandı',
            cancelled: 'Geri Çekildi',
            preApproval: 'Ön Onay Bekliyor',
        },
        preApprove: 'Ön Onay Ver',
        preApprovalApiHint:
            'Pazaryeri bu talebin yanıtlanabilmesi için önce ön onayınızı bekliyor. Ön onay, ürünü inceledikten sonra talebi onaylayacağınızı ya da reddedeceğinizi bildirir.',
        confirmPreApprove: {
            title: 'Ön Onay Verilecek',
            body: 'Pazaryerine, ürünü inceledikten sonra talebi onaylayacağınızı ya da reddedeceğinizi bildirirsiniz. Talep açık kalır; kararınızı sonra verirsiniz.',
            action: 'Ön Onay Ver',
        },
        preApproved: 'Ön onayınız pazaryerine iletildi. Talep şimdi kararınızı bekliyor.',
        settleWithRefund: 'Para İadesi ile Kapat',
        confirmSettleWithRefund: {
            title: 'Değişim Talebi Para İadesiyle Kapatılacak',
            body: 'Müşteriye yeni ürün gönderilmez. Pazaryeri ödediği tutarı müşteriye iade eder ve ürün siparişte iade edilmiş sayılır.',
            action: 'Para İadesi ile Kapat',
        },
        exchangeRefunded: 'Bu değişim talebi para iadesiyle kapatıldı.',
        moreActions: 'Diğer İşlemler',
        preApprovalHint: 'Pazaryeri bu talebin yanıtlanabilmesi için önce ön onayınızı bekliyor. Ön onayı pazaryerinin kendi panelinden verin.',
        reasonLabel: 'Müşteri Ne Dedi',
        reasonHint: 'Kendi cümlesiyle. Açılır liste, ne olduğunu anlatan cümleyi kaybettirir.',
        restockOnApproval: 'Onaylandığında Ürünleri Stoğa Geri Al',
        restockNow: 'Ürünleri Şimdi Stoğa Geri Al',
        restockNowHint: 'İade, kaydı açtığınız anda onaylanır. Yalnızca ürünler elinize ulaştıysa işaretleyin.',
        recordApprovedHint: 'İade ve iptal kaydı açıldığı anda onaylanır.',
        recordOpenHint: 'Bu kayıt, siz karar verene kadar açık kalır.',
        open: 'İade Kaydı Aç',
        openHint: 'Müşteri telefonla ya da mağazadan iade istediğinde kaydı buradan açın.',
        none: 'Bu Siparişte İade Yok',
        rejectTitle: 'İade Reddedilecek',
        rejectHint: 'Gerekçe müşteriye ve pazaryerine gider; gerekçesiz ret pazaryerinde bozulur.',
        approve: 'Onayla',
        approveAndRestock: 'Onayla ve Stoğa Al',
        reject: 'Reddet',
        rejectReason: 'Neden Reddediyorsunuz?',
        rejectReasonHint: 'Gerekçesiz ret, pazaryerinin satıcı aleyhine çevirdiği rettir.',
        rejectCode: 'Kanal İçin Kodlu Gerekçe',
        rejectCodeHint: 'Kendi cümleniz yerine, pazaryerinin okuyabileceği gerekçe olarak gönderilir.',
        pickReason: 'Bir Gerekçe Seçin',
        refund: 'İade Tutarı',
        respondBy: 'Son Yanıt',
        openedAt: 'Talep Tarihi',
        count: (shown: number, total: number) => `${total} talepten ${shown} tanesi`,
        returnReasons: {
            title: 'İade Nedenleri',
            description: (days: number) => `Son ${days} günde açılan iadeler, belirtilen nedene göre.`,
            reason: 'Neden',
            channel: 'Kanal',
            count: 'İade',
            share: 'Oran',
            unspecified: 'Belirtilmedi',
            empty: (days: number) => `Son ${days} Günde İade Yok`,
            emptyHint: 'Pazaryerinde veya mağazanızda açılan her iade, nedeniyle burada sayılır.',
        },
        channelConfirmed: 'Kanal Onayladı',
        channelConfirmedHint: 'Sipariş durumu, kanal bu kararı işleyip bildirdiğinde güncellenir; bu bir süre alabilir.',
        channelFailed: 'Kanal Onaylamadı',
        fromAccount: 'Müşteri Hesabından',
        shopReasons: {
            damaged: 'Ürün hasarlı geldi',
            wrong_item: 'Yanlış ürün geldi',
            not_as_described: 'Ürün açıklamadaki gibi değil',
            size: 'Beden ya da ölçü uymadı',
            changed_mind: 'Vazgeçti',
            late: 'Sipariş gecikti',
            other: 'Diğer',
        },
    },

    pricing: {
        title: 'Fiyatlandırma',
        subtitle: 'Sattığınız fiyat, maliyetinizden hesaplanır.',
        preview: 'Önizle',
        apply: 'Uygula',
        applyRules: 'Kuralları Uygula',
        applied: (changed: number, considered: number, skipped: number) =>
            `${considered} teklifin ${changed} tanesinin fiyatı değişti, ${skipped} tanesine dokunulmadı.`,

        previewTitle: 'Ne Olacağı',
        previewDescription: 'Hiçbir şey yazılmaz. Her satır, pazaryeri, vergi ve kargo düşüldükten sonra elinizde ne kalacağını gösterir.',
        previewSummary: (considered: number, changed: number, skipped: number) =>
            `${considered} teklif değerlendirildi · ${changed} tanesi değişecek · ${skipped} tanesine dokunulmayacak`,
        noSamples: 'Hiçbir Teklif Bir Kuralla Eşleşmedi',

        columns: {
            product: 'Ürün',
            cost: 'Maliyet',
            currentPrice: 'Şu An',
            newPrice: 'Olacak',
            margin: 'Kâr Marjı',
        },

        modes: {
            markup: 'Maliyet Üstüne',
            margin: 'Hedef Marj',
            multiply: 'Maliyet Çarpanı',
            fixed: 'Sabit Fiyat',
            competitor: 'En Düşük Rakibe Göre',
            product_percent: 'Ürün Fiyatına Göre %',
            product_amount: 'Ürün Fiyatına Tutar Ekle',
        },

        rounding: {
            none: 'Olduğu Gibi Bırak',
            up: 'Bir Üst Tam Sayıya',
            nearest: 'En Yakın Tam Sayıya',
            ninetyNine: 'Bir Üst x,99’a',
        },

        scopes: {
            all: 'Tüm Ürünler',
            category: 'kategori',
            brand: 'marka',
            product: 'ürün',
            selection: (kind: string, count: number) => `${count} ${kind}`,
            filter: 'Toplu düzenleme seçimi',
        },

        rules: {
            currency: 'Kural Para Birimi',
            currencyHint:
                'Bu kuralın kendi tutarlarının hangi para biriminde yazıldığı — sabit fiyat, taban fiyat, tavan fiyat ve kargo maliyeti. Dolar cinsinden sabit bir kural, döviz cinsinden satış fiyatıdır: bir kez yazılır, her çalışmada günün kuruyla çevrilir.',
            cadence: 'Sıklık',
            cadenceHint: 'Yalnızca otomatik çalışma bekletilir. Kuralı kendiniz uyguladığınızda her zaman çalışır.',
            cadences: {
                daily: 'Her Gece',
                every3days: 'Üç Günde Bir',
                weekly: 'Haftalık',
                monthly: 'Aylık',
            },
            title: 'Kurallar',
            description:
                'Kurallar üst üste binmez: bir ürünle eşleşen ilk kural fiyatı belirler, böylece fiyatı hangi kuralın koyduğunu her zaman söyleyebilirsiniz.',
            add: 'Kural Ekle',
            edit: 'Kuralı Düzenle',
            name: 'Kural',
            mode: 'Fiyat Nasıl Hesaplansın',
            modeHint: 'Maliyet + %35, %26 marj bırakır; %35 değil. Hedef marj, gerçekten elinizde kalanı sorar.',
            value: 'Değer',
            undercut: 'Rakibin Altında (%)',
            undercutHint: 'Kanaldaki en düşük rakip teklifin yüzde kaç altında olsun. 0 yazarsanız rakiple aynı fiyatı verir.',
            floor: 'Taban Fiyat',
            floorHint: 'Kural fiyatı bunun altına indirmez.',
            ceiling: 'Tavan Fiyat',
            ceilingHint: 'Kural fiyatı bunun üstüne çıkarmaz. Sınır istemiyorsanız boş bırakın.',
            formula: 'Formül',
            channel: 'Kanal',
            channelHint: 'Boş bırakırsanız bağlı tüm kanallar bu kuralla fiyatlanır.',
            competitorChannelHint: 'Rakip fiyatları pazaryeri başına okunur; bu kural tek bir kanalı fiyatlar.',
            everyChannel: 'Tüm Kanallar',
            oneChannel: 'tek kanal',
            commission: 'Komisyon (%)',
            commissionHint: 'Pazaryerinin aldığı pay. Bunu yok sayan bir marj hesabı, marjın kendisinden fazla yanılır.',
            readCommission: 'Kanaldan Oku',
            commissionRead: (listed: number) => `Bu kuralın kapsadığı ${listed} teklif kanala soruldu.`,
            commissionMore: 'Kural kanalda daha fazla teklifi kapsıyor; oranlar bunların ilk bölümünden okundu.',
            commissionUnread: (count: number) => `Kanal ${count} tanesi için oran vermedi.`,
            commissionNoneListed: 'Bu kuralın kapsadığı tekliflerden hiçbiri henüz kanalda değil.',
            commissionPick: 'Bir oran seçin, Komisyon (%) alanına yazılsın. Kuralı kaydedene kadar hiçbir şey kaydedilmez.',
            commissionShare: (rate: string, listings: number) => `${rate} · ${listings} teklif`,
            shipping: 'Kargo Maliyeti',
            shippingHint: 'Kargoyu siz ödüyorsanız bir adedin size maliyeti.',
            rounding: 'Yuvarlama',
            costIncludesVat: 'Kayıtlı Maliyet KDV Dahil',
            neverBelowCost: 'Asla Maliyetin Altına İnme',
            autoApply: 'Bu Kuralı Her Gece Otomatik Uygula',
            priority: 'Sıra',
            state: 'Durum',
            active: 'Aktif',
            paused: 'Duraklatıldı',
            automatic: 'Otomatik',
            empty: 'Henüz Kural Yok',
            emptyHint: 'Bir kural ekleyin, ne yapacağını önizleyin, sonra uygulayın.',
            confirmDelete: {
                title: 'Kural Kaldırılacak',
                body: 'Koyduğu fiyatlar olduğu gibi kalır.',
                action: 'Kaldır',
            },
        },

        runs: {
            title: 'Fiyatlandırma Geçmişi',
            description: 'Değişen her fiyat ve nasıl geri alınacağı.',
            when: 'Zaman',
            result: 'Sonuç',
            status: 'Durum',
            counts: (changed: number, considered: number) => `${considered} teklifin ${changed} tanesi değişti`,
            skipped: (count: number) => `${count} tanesine dokunulmadı`,
            revert: 'Geri Al',
            confirmRevert: {
                title: 'Fiyatlar Geri Alınacak',
                body: 'Bu çalıştırmanın değiştirdiği her fiyat önceki değerine döner ve kanallara gönderilir.',
                action: 'Geri Al',
            },
            empty: 'Henüz Fiyatlandırma Yapılmadı',
        },

        runStatus: {
            running: 'Çalışıyor',
            succeeded: 'Tamam',
            failed: 'Başarısız',
            reverted: 'Geri Alındı',
        },

        rates: {
            title: 'Döviz Kurları',
            description: 'Merkez Bankası’ndan; tedarikçi maliyetini liraya çevirmek için kullanılır.',
            currency: 'Para Birimi',
            rate: 'Lira',
            validOn: 'Yayın Tarihi',
            empty: 'Henüz Kur Çekilmedi',
            emptyHint: 'Günde birkaç kez çekilir ve yalnızca başka para birimindeki maliyetler için gerekir.',
        },
    },

    warehouses: {
        edit: 'Depoyu Düzenle',
        title: 'Depolar',
        subtitle: 'Stokun tutulduğu ve gönderildiği yerler.',
        add: 'Depo Ekle',
        name: 'Depo',
        code: 'Kısa Ad',
        codeHint: 'Kanal ayarlarında ve kargoda depoyu tanıtan kısa ad. Sonradan değiştirilemez.',
        city: 'Şehir',
        priority: 'Öncelik',
        priorityHint: 'Sayısı küçük olan depo önce kullanılır: bir sipariş önce onun stoğundan karşılanır.',
        stock: 'Satılabilir',
        onHand: (onHand: number, variants: number) => `${onHand} elde · ${variants} ürün`,
        state: 'Durum',
        isDefault: 'Varsayılan',
        paused: 'Duraklatıldı',
        selling: 'Satışa Açık',
        sellingHint:
            'Duraklatılan deponun stoğu hiçbir kanalda satılabilir sayılmaz ve siparişler ondan karşılanmaz. Kaydettiğinizde kanallara hemen bildirilir.',
        makeDefault: 'Varsayılan Yap',
        empty: 'Henüz Depo Yok',
        confirmDelete: {
            title: 'Depo Kaldırılacak',
            body: 'Önce boşaltılmış olmalı. Stok kayıtları depoyla birlikte gider.',
            action: 'Kaldır',
        },
        transfer: {
            action: 'Stoğu Taşı',
            title: (name: string) => `${name} Stoğunu Taşı`,
            hint: 'Bu depodaki stoğun tamamı, siparişlere ayrılmış olanlar dışında, seçtiğiniz depoya taşınır. Her hareket stok geçmişine Transfer olarak yazılır.',
            target: 'Hedef Depo',
            submit: 'Stoğu Taşı',
        },
    },

    suppliers: {
        title: 'Tedarikçiler',
        subtitle: 'Başkasının hazırladığı kataloglar, sizinkiyle aynı hizada tutulur.',
        tabs: {
            mapping: 'Eşleme',
            rules: 'Kurallar',
            runs: 'Okuma Geçmişi',
            items: 'Ürünler',
            images: 'Görseller',
        },
        supplier: 'Tedarikçi',
        code: 'Kod',
        codeHint: 'İçe aktarılan stok kodlarının başına eklenir; 1001’den başlayan iki tedarikçi birbirine karışmaz.',
        backToList: '← Tedarikçiler',
        saveFirst: 'Önizle, İçe Aktar ve Yeniden Gönder kayıtlı eşleme ve kurallarla çalışır. Önce değişikliklerinizi kaydedin.',

        list: {
            title: 'Tedarikçiler',
            description: 'Katalogların sahibi.',
            add: 'Tedarikçi Ekle',
            empty: 'Henüz Tedarikçi Yok',
            edit: 'Tedarikçiyi Düzenle',
            feedCount: 'Ürün Listesi',
        },

        feeds: {
            title: 'Ürün Listeleri',
            description: 'Tedarikçi bir dosya yayınlar; sistem onu okur ve değişenleri aktarır.',
            add: 'Ürün Listesi Ekle',
            name: 'Ürün Listesi',
            format: 'Biçim',
            url: 'Adres',
            urlHint: 'Dosyanın https ile başlayan, herkese açık adresi. Kendi ağımızın içindeki adresler kabul edilmez.',
            schedule: 'Zamanlama',
            manual: 'İstenince',
            lastRun: 'Son Okuma',
            never: 'Hiç okunmadı',
            itemCount: (count: number) => `${count} kayıt`,
            empty: 'Henüz Ürün Listesi Yok',
            emptyHint: 'Önce tedarikçi ekleyin, sonra yayınladıkları dosyanın adresini.',
            confirmDelete: {
                title: 'Ürün Listesi Kaldırılacak',
                body: 'Aktardığı ürünler katalogda kalır. Bir daha okunmaz.',
                action: 'Kaldır',
            },
        },

        mapping: {
            title: 'Alan Eşleme',
            description: 'Tedarikçinin dosyasındaki hangi alan sizin hangi alanınızı besliyor.',
            read: 'Dosyayı Oku',
            reading: 'Okunuyor…',
            found: (records: number, fields: number) => `${records} kayıt okundu, ${fields} alan bulundu.`,
            itemPath: 'Kayıt Yolu',
            itemPathHint: 'Dosyada bir ürünün durduğu yer; örneğin Urunler/Urun. Otomatik bulunur, yanlış seçildiyse değiştirin.',
            unmapped: '— Eşlenmedi —',
            noFields: 'Dosya Henüz Okunmadı',
            noFieldsHint: 'Okuyun; her alan, içindeki gerçek değerlerle birlikte burada listelenir.',
            readHint: 'Dosyayı Oku yalnızca her alanın yanında örnek değerleri gösterir. Eşleme, Kaydet ile saklanır.',
            fields: {
                stockCode: 'Stok Kodu',
                title: 'Ürün Adı',
                description: 'Açıklama',
                barcode: 'Barkod',
                mpn: 'Üretici Kodu',
                brand: 'Marka',
                category: 'Kategori',
                salePrice: 'Tedarikçi Fiyatı',
                listPrice: 'Liste Fiyatı',
                currency: 'Para Birimi',
                vatRate: 'KDV Oranı',
                quantity: 'Stok',
                desi: 'Desi',
                weight: 'Ağırlık',
                images: 'Görseller',
            },
            imagesHint:
                'Görseller bu sırayla alınır; ilk alanın ilk görseli ana görseldir. Boş alan atlanır, alanlarda tekrarlanan bir adres bir kez alınır ve en fazla 12 görsel alınır.',
            imagesLater:
                'Görseller, liste ürünü oluştururken alınır. Yeniden Gönder yalnızca görseli olmayan ürünlere görsel ekler; sonradan eklenen bir alan ya da tedarikçinin değiştirdiği bir görsel, görseli olan ürüne geçmez.',
            imageField: (position: number) => `Görsel Alanı ${position}`,
            mainImage: 'Ana Görsel',
            noImageFields: 'Görsel alanı eşlenmedi.',
            addImageField: 'Görsel Alanı Ekle',
            moveUp: 'Yukarı Taşı',
            moveImageFieldUp: (position: number) => `${position}. görsel alanını yukarı taşı`,
            remove: 'Kaldır',
            removeImageField: (position: number) => `${position}. görsel alanını kaldır`,
        },

        rules: {
            title: 'Fiyat ve Stok Kuralları',
            description: 'Okunan değerlerle ne yapılacağı.',
            margin: 'Kâr Marjı (%)',
            marginHint: '35 yazarsanız tedarikçinin 100’ü 135’e satılır.',
            rounding: 'Yuvarlama',
            roundingNone: 'Olduğu Gibi Bırak',
            roundingUp: 'Bir Üst Tam Sayıya',
            roundingNearest: 'En Yakın Tam Sayıya',
            rounding99: 'Bir Üst x,99’a',
            skuPrefix: 'Stok Kodu Öneki',
            skuPrefixHint: 'Tedarikçinin kendi kodunun başına eklenir.',
            minQuantity: 'Stok Eşiği',
            minQuantityHint: 'Bu sayı ve altındaki stok, tükendi sayılır.',
            maxQuantity: 'Stok Tavanı',
            maxQuantityHint: 'Her üründe 99999 yazan bir dosya depoyu değil, varsayılan bir değeri gösterir. Olduğu gibi kabul etmek için 0 bırakın.',
            currency: 'Para Birimi',
            currencyAsFile: 'Dosyadaki Para Birimi',
            currencyHint: 'Dosyada yazmadığında kullanılır. Yalnızca lira ile satan bir tedarikçi bunu kırk bin kez yazmaz.',
            vatRate: 'KDV Oranı (%)',
            vatRateHint: 'Dosyada yazmadığında kullanılır.',
            desi: 'Desi',
            desiHint: 'Dosyada yazmadığında kullanılır.',
            listMultiplier: 'Üstü Çizili Fiyat Katsayısı',
            listMultiplierHint:
                '1,2 katsayısı, ₺100 fiyatın yanında ₺120 liste fiyatını çizili gösterir. Tedarikçi liste fiyatı vermediğinde kullanılır.',
            force: 'Dosyadaki Değeri Yok Say, Her Zaman Bunu Kullan',
            requireBarcode: 'Yalnızca Geçerli Barkodu Olan Ürünleri Aktar',
            requireBarcodeHint:
                'Gerçek bir GTIN taşımayan kayıtlar, pazaryerinde reddedilmek yerine burada tedarikçinin kendi koduyla birlikte reddedilir. Eksik barkod asla üretilmez: barkod bir üreticinin ürününü tüm sektöre tanıtır ve uyduracağımız bir numara başkasının ürününü gösterir.',
            createMissing: 'Katalogda Olmayan Ürünleri Oluştur',
            updatePrice: 'Sonraki Okumalarda Maliyet ve Künye Bilgilerini Güncelle',
            updateQuantity: 'Sonraki Okumalarda Stoğu Güncelle',
            zeroMissing: 'Tedarikçi Bir Ürünü Bırakınca Stoğunu Sıfırla',
            targetChannels: 'Hedef Kanallar',
            targetChannelsHint:
                'Ürünler bu kanallara gönderilir (fiyat + stok). Hiçbiri seçili değilse ürünler yalnızca kataloğa girer, hiçbir kanalda görünmez.',
            targetChannelsEmpty: 'Önce bir kanal bağlayın.',
            schedule: 'Zamanlama',
            scheduleHint: 'Cron ifadesi; altı saatte bir için 0 */6 * * *. Boş bırakırsanız yalnızca siz isteyince okunur.',
        },

        runs: {
            title: 'Okuma Geçmişi',
            description: 'Dosya her okunduğunda ne olduğu.',
            preview: 'Önizle',
            import: 'İçe Aktar',
            resync: 'Yeniden Gönder',
            resyncHint:
                'Değişmeyenler dahil tüm ürünleri mevcut ayarlarla yeniden işler: hedef kanallara gönderir, stoğu ayarlar, boş galerilere görsel çeker.',
            running: 'Dosya okunuyor. Bu ekran kendini günceller.',
            empty: 'Bu Ürün Listesi Henüz Okunmadı',
            when: 'Zaman',
            mode: 'Tür',
            status: 'Sonuç',
            result: 'Ürünler',
            counts: (seen: number, created: number, updated: number) => `${seen} okundu · ${created} yeni · ${updated} değişti`,
            overLimit: (count: number) => `${count} kayıt paket sınırı nedeniyle eklenmedi`,
            untouched: (unchanged: number, failed: number) => `${unchanged} aynı · ${failed} aktarılmadı`,
        },

        runStatus: {
            running: 'Okunuyor',
            succeeded: 'Tamam',
            failed: 'Başarısız',
            cancelled: 'Durduruldu',
        },

        items: {
            title: 'Bu Listedeki Ürünler',
            description: 'Tedarikçinin taşıdıkları ve burada ne olduğu.',
            search: 'Ara',
            searchPlaceholder: 'Kod veya isme göre ara',
            filter: 'Göster',
            all: 'Hepsi',
            code: 'Tedarikçi Kodu',
            status: 'Durum',
            cost: 'Tedarikçi Fiyatı',
            price: 'Sizin Fiyatınız',
            quantity: 'Stok',
            images: 'Görsel',
            product: 'Ürün',
            open: 'Aç',
            empty: 'Henüz Bir Şey Okunmadı',
            emptyHint: 'Listeyi önizleyin veya içe aktarın; ürünler burada görünür.',
            count: (shown: number, total: number) => `${total} üründen ${shown} tanesi`,
            overLimitHint: 'Paketinize dahil ürün sınırı nedeniyle eklenmedi. Paketinizde yer açıldığında sonraki içe aktarmada eklenir.',
        },

        itemStatus: {
            pending: 'Bekliyor',
            created: 'Yeni',
            updated: 'Değişti',
            unchanged: 'Aynı',
            skipped: 'Aktarılmadı',
            failed: 'Başarısız',
            missing: 'Bırakıldı',
            over_limit: 'Paket Sınırı',
        },

        images: {
            title: 'Ürün Görselleri',
            description: 'Ürünler okumada aktarılır; görselleri ardından arka planda alınır. Bekleyen görsel varken bu ekran kendini günceller.',
            summary: (pending: string, stored: string, failed: string) => `Görseller: ${pending} bekliyor · ${stored} alındı · ${failed} alınamadı.`,
            stage: {
                pending: 'Ürünler aktarıldı, görseller işleniyor.',
                failed: 'Ürünler aktarıldı; bazı görseller alınamadı.',
                done: 'Ürünler ve görselleri aktarıldı.',
            },
            none: 'Bu liste henüz görsel sunmadı.',
            retry: 'Yeniden Dene',
            retried: (count: string) => `${count} görsel yeniden alınmak üzere sıraya girdi.`,
            caption: 'Görseli Alınamayan Ürünler',
            failures: 'Alınamayan Görseller',
            empty: 'Alınamayan Görsel Yok',
            emptyHint: 'Bu listenin sunduğu her görsel alındı ya da hâlâ yolda.',
            count: (shown: number, total: number) => `${total} üründen ${shown} tanesi`,
        },
    },

    customers: {
        anonymise: 'Kişisel Verileri Sil (KVKK)',
        anonymiseConfirm: {
            title: 'Müşteri Bilgileri Silinecek',
            body: 'Adı, e-postası ve telefonu kalıcı olarak silinir. Siparişler kalır ama artık kimseyi işaret etmez.',
            action: 'Kalıcı Olarak Sil',
        },
        title: 'Müşteriler',
        subtitle: 'Nereden alırsa alsın, sizden alışveriş yapan herkes.',
        search: 'İsim, telefon, e-posta veya vergi numarası ile ara',
        empty: 'Henüz Müşteri Yok',
        emptyHint: 'İlk sipariş geldiğinde müşteri burada görünür.',
        name: 'Müşteri',
        company: 'Kurumsal',
        member: 'Tezgah Üyesi',
        membersOnly: 'Tezgah Üyeleri',
        contact: 'İletişim',
        orderCount: 'Sipariş',
        totalSpent: 'Harcama',
        lastOrder: 'Son Sipariş',
        count: (shown: number, total: number) => `${total} müşteriden ${shown} tanesi`,
        noContact: 'İletişim bilgisi yok',
        spentSoFar: (amount: string, count: number) => `${count} siparişte ${amount}`,
        details: 'Bilgiler',
        editDetails: 'Bilgileri Düzenle',
        editDetailsHint: 'Boş bıraktığınız alan olduğu gibi kalır.',
        email: 'E-posta',
        phone: 'Telefon',
        taxNumber: 'Vergi Numarası',
        taxOffice: 'Vergi Dairesi',
        identityNumber: 'TC Kimlik Numarası',
        noTaxNumber: 'Vergi veya TC numarası yok — bu müşteriye henüz fatura kesilemez.',
        tags: 'Etiketler',
        noTags: 'Etiket yok',
        tagsLabel: 'Etiketler',
        tagsHint: 'Virgülle ayırın. Kendi kelimeleriniz: toptancı, hassas, sürekli.',
        orders: 'Siparişler',
        noOrders: 'Sipariş Yok',
        notes: 'Notlar',
        noNotes: 'Henüz Not Yazılmamış',
        notesHint: 'Bu müşteriyle çalışırken öğrenilenler. Notlar saklanır, üzerine yazılmaz.',
        addNote: 'Not Yazın',
        saveNote: 'Notu Ekle',
    },

    sellerSupport: {
        title: 'Destek',
        subtitle: 'Hesabınız veya panel hakkında her şeyi bize sorabilirsiniz.',
        formTitle: 'Bize Yazın',
        formHint: 'E-posta ile yanıtlıyoruz, genellikle bir iş günü içinde.',
        replyingTo: (email: string) => `${email} adresine yanıt vereceğiz.`,
        subject: 'Konu',
        message: 'Mesajınız',
        messageHint: 'Ne yapmaya çalıştığınızı ve ne olduğunu yazın. Sipariş veya ürün numarası yardımcı olur.',
        send: 'Gönder',
        sent: 'Mesajınız bize ulaştı. E-posta ile yanıt vereceğiz.',
    },
    subscription: {
        title: 'Abonelik',
        subtitle: 'Paketiniz, sınırlarınız ve bu ayki kullanımınız.',
        currentPlan: 'Mevcut Paket',
        status: 'Durum',
        trialEnds: 'Deneme Bitişi',
        limits: {
            products: 'Ürünler',
            channels: 'Pazaryeri Hesapları',
            users: 'Kullanıcılar',
            orders_monthly: 'Bu Ayki Siparişler',
        },
        availablePlans: 'Paketler',
        planDescription: {
            starter: 'İlk kanallarını bağlayan satıcı için.',
            growth: 'Birden çok pazaryerinde büyüyen katalog için.',
            scale: 'Sınırsız alan isteyen yüksek hacimli satıcılar için.',
        },
        perMonth: '/ ay',
        perYear: '/ yıl, peşin',
        free: 'Ücretsiz',
        price: {
            net: (net: string) => `${net} + KDV`,
            total: (total: string) => `KDV dahil ${total}`,
        },
        switchTo: 'Bu Pakete Geç',
        switchTitle: (plan: string) => `${plan} Paketine Geçiş`,
        switchBody: 'Ödeme altyapısı bağlanana kadar paket değişikliğini biz yapıyoruz. Talebiniz ekibimize düşer; aynı gün dönüş yaparız.',
        switchAction: 'Talep Gönder',
        message: 'Not (İsteğe Bağlı)',
        messageHint: 'Ne zaman geçmek istediğinizi ya da sorunuzu yazın.',
        requestSent: 'Talebiniz alındı. Ekibimiz e-posta ile dönüş yapacak.',
        // A package priced by offer: its price, and its action, which asks for one (docs/15 §2).
        contactSales: 'Teklifle belirlenir',
        requestOffer: 'Teklif İsteyin',
        offerTitle: (plan: string) => `${plan} Paketi İçin Teklif İsteyin`,
        offerBody: 'Talebiniz ekibimize ulaşır; ürün hacminize ve ihtiyaçlarınıza göre hazırladığımız teklifi e-postayla iletiriz.',
        recommended: 'Önerilen Paket',
        includes: 'Pakete dahil olanlar',
        notIncluded: 'Dahil değil',
        footnote:
            'Ürün, stok, sipariş, kargo, müşteri ve ekip yönetimi her pakette vardır. Sipariş başına ek ücret alınmaz. e-Fatura hizmetinizin, ödeme hizmetinizin ve kargo firmanızın ücretleri paket bedeline dahil değildir.',
        viewPlans: 'Paketleri Görüntüle',
        trialStarted: 'Deneme Başlangıcı',
        trialLeft: 'Kalan Süre',
        days: (count: number) => `${count} gün`,
        trialOver: 'Sona erdi',
        period: 'Dönem',
        periods: { monthly: 'Aylık', yearly: 'Yıllık' },
        periodEnds: 'Dönem Bitişi',
        yearlyNotSold: 'Yıllık satılmıyor',
        buy: 'Satın Al',
        extend: 'Süreyi Uzat',
        buyTitle: (plan: string) => `${plan} Paketini Satın Al`,
        plan: 'Paket',
        amount: 'Tutar',
        netAmount: 'Paket Bedeli',
        vatAmount: (rate: number) => `KDV (%${rate})`,
        totalAmount: 'Ödenecek Tutar',
        scheduled: (date: string, plan: string, period: string) => `${date} tarihinde ${plan} paketine geçilecek (${period}).`,
        // A seller cancelling their own package at the end of the paid period (ADR 0016 §6).
        cancel: {
            action: 'Aboneliği İptal Et',
            confirm: {
                title: 'Abonelik İptal Edilecek',
                body: (plan: string, date: string) =>
                    `${plan} paketiniz ${date} tarihine kadar kullanılabilir ve yenilenmez. Bu tarihten sonra siparişleriniz, faturalarınız ve stokunuz çalışmaya devam eder; yeni ürün, pazaryeri hesabı, kullanıcı ve otomasyon eklenemez. Ödenen dönem için iade yapılmaz. Bu tarihe kadar iptali geri alabilirsiniz.`,
                action: 'Aboneliği İptal Et',
            },
            cancelled: (plan: string, date: string) =>
                `Aboneliğinizi iptal ettiniz. ${plan} paketiniz ${date} tarihine kadar kullanılabilir ve yenilenmeyecek.`,
            resume: 'İptali Geri Al',
            resumed: 'İptal geri alındı. Paketiniz iptal edilmeden önceki gibi devam ediyor.',
            ended: (date: string) =>
                `İptal ettiğiniz paketin dönemi ${date} tarihinde sona erdi. Yeniden paket aldığınızda tüm özellikler hemen açılır.`,
        },
        // One change waits at a time (ADR 0017 §8): while it does, nothing is bought.
        scheduledLocked:
            'Bekleyen bir paket değişikliğiniz olduğu için şu an başka bir paket satın alınamaz ve süre uzatılamaz. Değişiklik için destek ekibimizle iletişime geçin.',
        contactSupport: 'Destek Ekibine Yazın',
        appliesAt: (date: string, plan: string) =>
            `${date} tarihinde, ödediğiniz dönem bitince ${plan} paketine geçilecek. O tarihe kadar mevcut paketiniz devam eder.`,
        loses: 'Yeni pakette kapanacak özellikler:',
        features: {
            storefront: 'Tezgah',
            invoicing: 'e-Fatura',
            price_rules: 'Fiyatlandırma',
            suppliers: 'Tedarikçiler',
            integrations: 'API Erişimi ve Webhook’lar',
            finance: 'Finans',
        },
        exceeding: 'Kullanımınız yeni paketin sınırlarının üzerinde. Mevcut kayıtlarınız korunur; yenisi eklenemez:',
        method: 'Ödeme Yöntemi',
        methods: { bank_transfer: 'Havale/EFT', card: 'Kart ile Ödeme' },
        transferHint: 'Havaleniz onaylandığında paketiniz hemen başlar.',
        createTransfer: 'Onayla ve Havale Bilgilerini Göster',
        payByCard: 'Onayla ve Öde',
        agreement: {
            title: 'Ön Bilgilendirme Formu ve Mesafeli Hizmet Sözleşmesi',
            loading: 'Sözleşme hazırlanıyor',
            preInformation: "Ön Bilgilendirme Formu'nu ve Mesafeli Hizmet Sözleşmesi'ni okudum, onaylıyorum.",
            commercialPurpose: 'Bu satın alımı ticari veya mesleki faaliyetim için yapıyorum.',
            immediateStart: 'Hizmetin ödemeyle birlikte hemen başlamasını onaylıyorum; bu nedenle cayma hakkımın bulunmadığını biliyorum.',
            view: 'Sözleşmeyi Görüntüle',
            viewLabel: (reference: string) => `${reference} ödemesinin sözleşmesini görüntüle`,
            viewerTitle: 'Sözleşme',
            acceptedAt: 'Onay Zamanı',
            reference: 'Ödeme Numarası',
        },
        closed: 'Paketler henüz buradan satın alınamıyor. Paket değişikliği için talep bırakabilirsiniz.',
        switched: 'Paketiniz değiştirildi.',
        pending: {
            title: 'Bekleyen Ödeme',
            transferBody: 'Havaleyi, açıklamasına aşağıdaki numarayı yazarak bu hesaba gönderin. Onaylandığında paketiniz hemen başlar.',
            cardBody: 'Kartla ödeme tamamlanmadı.',
            holder: 'Hesap Sahibi',
            bank: 'Banka',
            iban: 'IBAN',
            reference: 'Açıklama',
            referenceHint: 'Havale açıklamasına bu numarayı yazın.',
            cancel: 'Ödemeyi İptal Et',
            confirmCancel: {
                title: 'Ödeme Talimatı İptal Edilecek',
                body: 'Bu ödeme talimatı geçersiz olur. Gönderilmiş bir havale ekibimizce iade edilir.',
                action: 'İptal Et',
            },
        },
        history: 'Ödeme Geçmişi',
        historyEmpty: 'Henüz Ödeme Yok',
        columns: {
            date: 'Tarih',
            plan: 'Paket',
            amount: 'Tutar',
            method: 'Yöntem',
            status: 'Durum',
            reference: 'Açıklama',
            invoice: 'Fatura',
            agreement: 'Sözleşme',
        },
        downloadInvoice: 'Faturayı İndir',
        invoicePreparing: 'Hazırlanıyor',
        chargeStatus: {
            pending: 'Onay Bekliyor',
            paid: 'Ödendi',
            held: 'Bekletiliyor',
            failed: 'Başarısız',
            cancelled: 'İptal Edildi',
            refunded: 'İade Edildi',
        },
        cardPending: 'Tamamlanmadı',
        companyIncomplete: 'Paket satın almak için Firma Bilgileri’nde yasal unvan, vergi numarası, adres ve telefon eksiksiz olmalı.',
        completeCompany: 'Firma Bilgilerini Tamamla',
        card: {
            paid: 'Ödemeniz alındı. Paketiniz başladı.',
            failed: 'Ödemeniz alınamadı. Kartla yeniden deneyebilirsiniz.',
            failedBecause: (reason: string) => `Ödemeniz alınamadı: ${reason}`,
            cancelled: 'Ödemeyi tamamlamadınız; kartınızdan bir şey çekilmedi.',
            held: 'Ödemeniz alındı. Hesabınızda bekleyen bir paket değişikliği olduğu için henüz uygulanmadı; destek ekibimiz sizinle iletişime geçecek.',
            waiting: 'Ödeme hizmetinin onayı bekleniyor. Birkaç saniye sürer.',
        },
        banner: {
            pastDue:
                'Aboneliğinizin dönemi sona erdi. Üç gün içinde ödeme yapılmazsa siparişleriniz, faturalarınız ve stokunuz çalışmaya devam eder, yeni kayıt eklenemez.',
            expired:
                'Aboneliğinizin süresi doldu. Siparişleriniz, kargo, fatura, iade ve stok çalışmaya devam eder; yeni ürün, pazaryeri hesabı, kullanıcı, tedarikçi ve otomasyon ödemenizi bekler, ödemeniz alındığında hemen açılır.',
            action: 'Aboneliğe Git',
        },
        statuses: {
            trialing: 'Deneme',
            active: 'Aktif',
            past_due: 'Ödeme Bekliyor',
            canceled: 'İptal Edildi',
            expired: 'Süresi Doldu',
        },
    },

    planLimits: {
        reached: {
            products:
                'Paketinize dahil ürün sınırına ulaştınız. Mevcut ürünleriniz ve siparişleriniz işlenmeye devam eder. Yeni ürün eklemek için ürün sayınızı azaltın ya da başka bir paket seçin.',
            channels:
                'Paketinize dahil pazaryeri hesabı sınırına ulaştınız. Bağlı hesaplarınız ve siparişleriniz işlenmeye devam eder. Yeni bir hesap bağlamak için bir bağlantıyı kaldırın ya da başka bir paket seçin.',
            users: 'Paketinize dahil kullanıcı sınırına ulaştınız. Ekibiniz çalışmaya devam eder. Yeni kullanıcı eklemek için bir üyeyi devre dışı bırakın ya da başka bir paket seçin.',
        },
        over: {
            products: (used: string, limit: string) =>
                `Ürün sayınız paketinizin sınırını aşıyor (${used} / ${limit}). Mevcut ürünleriniz korunur; yenisi eklenemez.`,
            channels: (used: string, limit: string) =>
                `Pazaryeri hesabı sayınız paketinizin sınırını aşıyor (${used} / ${limit}). Bağlı hesaplarınız korunur; yenisi bağlanamaz.`,
            users: (used: string, limit: string) =>
                `Kullanıcı sayınız paketinizin sınırını aşıyor (${used} / ${limit}). Mevcut kullanıcılarınız korunur; yenisi eklenemez.`,
        },
    },

    bulk: {
        export: 'Excel İçin Dışa Aktar',
        importProducts: 'Ürünleri İçe Aktar',
        importTitle: 'Ürünleri İçe Aktar',
        importSubtitle:
            'Ürünleri fiyatı, stoğu ve görselleriyle birlikte Excel dosyasıyla (.xlsx) ya da Excel’den CSV olarak kaydedilmiş dosyayla toplu ekleyin. Önce şablonu indirin.',
        downloadTemplate: 'Şablonu İndir',
        preview: 'Önizle',
        commit: 'İçe Aktar',
        previewNote: 'Bu bir önizlemedir; hiçbir şey kaydedilmez.',
        resultCreated: 'Oluşturulan',
        resultExisting: 'Zaten Kayıtlı',
        resultOverLimit: 'Paket Sınırı Dışında',
        existing: 'Zaten kayıtlı',
        rowOverLimit: 'Eklenmedi: paketinize dahil ürün sınırı.',
        rowLapsed: 'Eklenmedi: aboneliğinizin süresi doldu.',
        rowNotPermitted: 'Eklenmedi: rolünüz bu satırdaki fiyatı ya da stoğu yazamıyor.',
        overLimitNotice: (count: number, committed: boolean) => `${count} satır ${committed ? 'eklenmedi' : 'eklenmeyecek'}.`,
        resultFailed: 'Hatalı',
        resultTotal: 'Toplam Satır',
        line: 'Satır',
        sku: 'SKU',
        salePrice: 'Satış Fiyatı',
        listPrice: 'Liste Fiyatı',
        stock: 'Stok',
        images: 'Görsel',
        status: 'Durum',
        ok: 'Tamam',
        pickFirst: 'Önce bir dosya seçin.',
        detectedWorkbook: 'Okunan dosya: Excel dosyası (.xlsx), ilk sayfası.',
        detectedText: (separator: string, encoding: string) =>
            `Okunan dosya: Excel’den CSV olarak kaydedilmiş dosya; ayraç ${separator}, karakter kodlaması ${encoding}.`,
        separators: { semicolon: 'noktalı virgül (;)', comma: 'virgül (,)', tab: 'sekme' },
        encodings: { 'utf-8': 'UTF-8', 'utf-16': 'UTF-16 (Unicode Metin)', 'windows-1254': 'Windows-1254 (Türkçe)' },
        columnsTitle: 'Sütunlar',
        columnsHint: 'İlk satır, şablondaki gibi sütunların adlarını taşır. Başka sütunlar alınmaz.',
        columns: {
            sku: 'Ürün kodu. Zorunlu, en fazla 64 karakter; dosyada bir kez.',
            title: 'Ürün adı. Zorunlu, en fazla 300 karakter.',
            description: 'Açıklama. İsteğe bağlı.',
            taxRate: 'KDV oranı: 0, 1, 10 ya da 20. Boş bırakılırsa 0.',
            salePrice: 'Satış fiyatı. İsteğe bağlı; her satış kanalı bu fiyatı kullanabilir ya da kendi fiyatını alabilir.',
            listPrice: 'Liste fiyatı (üstü çizili olan). İsteğe bağlı; yalnızca satış fiyatıyla birlikte yazılır ve ondan düşük olamaz.',
            currency: 'Fiyatın para birimi: ₺, USD, EUR ya da GBP. Boş bırakılırsa ₺.',
            stock: 'Açılış stoğu, tam sayı; varsayılan depoya girer. İsteğe bağlı.',
            images: 'Görsel adresleri, her sütuna https ile başlayan bir adres. İlki ana görsel olur; görseller ürün eklendikten sonra birkaç dakika içinde gelir.',
        },
        limitsHint: 'Bir dosyada en fazla 5.000 satır ve 16 MB olabilir. Formüller hesaplanmaz; Excel’in kaydettiği değer okunur.',
        lettersHint: 'Türkçe harfler bozuk görünürse dosyayı Excel’de .xlsx olarak kaydedin.',
        existingHint:
            'Kodu katalogda olan bir ürünün satırı hiçbir şeyi değiştirmez: ürünün fiyatı, stoğu ve görselleri olduğu gibi kalır. Fiyatı ya da stoğu Toplu Düzenleme sayfasından ya da ürünün Stok ve Fiyat sekmesinden değiştirin.',
        draftHint:
            'İçe aktarılan ürünler Taslak olarak eklenir ve hiçbir satış kanalına gönderilmez. Mağazanız yalnızca aktif ürünleri gösterir: Ürünler sayfasında seçip Aktifleştir ile aktifleştirin, Kanala Ekle ile satış kanallarına ekleyin.',
        supplier: {
            title: 'Tedarikçi Ürün Listesi',
            open: 'Tedarikçiniz ürünlerini bir adreste yayınlıyorsa (örneğin bir XML bağlantısı), o ürün listesini Tedarikçiler sayfasında ekleyin. Ürünler oradan aktarılır ve liste her okunduğunda güncel tutulur.',
            link: 'Tedarikçilere Git',
            notInPlan: 'Tedarikçinizin bir adreste yayınladığı ürün listeleri (örneğin bir XML bağlantısı) paketinize dahil değil.',
            includedIn: (plan: string) => `${plan} paketi bunları içerir.`,
        },
        selectAll: 'Tümünü seç',
        selectRow: 'Satırı seç',
        selected: 'seçili',
        selectAllMatching: (n: number) => `Eşleşen ${n} Ürünün Tümünü Seç`,
        allSelected: (n: number) => `Eşleşen ${n} ürünün tümü seçili`,
        activate: 'Aktifleştir',
        archive: 'Arşivle',
        clear: 'Temizle',
        undo: 'Geri Al',
        statusDone: (count: number, status: string) => `${count} ürün ${status} durumuna getirildi.`,
        statusDoneWithdrawn: (count: number, status: string) =>
            `${count} ürün ${status} durumuna getirildi. Satış kanallarından çekilmeleri sıraya alındı.`,
        deleteDone: (count: number) => `${count} ürün silindi.`,
        categoryDone: (count: number, category: string) => `${count} ürün “${category}” kategorisine taşındı.`,
        categoryCleared: (count: number) => `${count} ürünün kategorisi kaldırıldı.`,
        undone: (count: number) => `Toplu düzenleme geri alındı. ${count} ürün önceki haline döndü.`,
        rematch: 'Yeniden Eşle',
        issueInvoices: 'Fatura Kes',
        confirmIssue: {
            title: (count: number) => `${count} Sipariş Faturalanacak`,
            body: 'Her biri varsayılan e-Fatura hizmetiyle kesilir. Kesilen fatura yasal belgedir; geri alınamaz, yalnızca iade faturasıyla karşılanır.',
            action: 'Fatura Kes',
        },
        issued: (issued: number, failed: number) =>
            failed === 0 ? `${issued} fatura kesildi.` : `${issued} fatura kesildi, ${failed} sipariş faturalanamadı.`,
        issuing: (done: number, total: number) => `Faturalar arka planda kesiliyor: ${total} siparişten ${done} tanesi tamamlandı.`,
        issueRefusedQuoted: (count: number) =>
            `e-Fatura hizmeti ${count} siparişin faturasını kendi gerekçesiyle reddetti. Gerekçeyi, siparişin sayfasından faturayı yeniden kestiğinizde görürsünüz.`,
        advance: 'Durum Değiştir',
        advanceHint: (count: number) => `Seçilen ${count} siparişin bütün paketlerine bu durum gönderilecek.`,
        advanceStatusHint:
            'Yalnızca seçilen siparişlerin kanallarının hepsinin kabul ettiği durumlar. Teslim alanı soran bir durum siparişin kendi sayfasından verilir.',
        advanceNone: 'Seçilen siparişlerin kanallarında hepsine birden verilebilecek ortak bir durum yok. Seçimi tek bir kanala daraltın.',
        advanced: (advanced: number, skipped: number, failed: number) =>
            `${advanced} sipariş gönderildi, ${skipped} siparişin paketi yoktu, ${failed} sipariş reddedildi.`,
        more: 'Seçilenler İçin Diğer İşlemler',
        printSlips: 'Paket Fişi Bas',
        slipsReady: (count: number) => `${count} fiş hazır.`,
        slipsFailed: (ready: number, failed: number) => `${ready} fiş hazır, ${failed} tanesi oluşturulamadı.`,
        slipsOpen: 'Basmak için her fişi açın.',
        slipPerOrder: 'Sipariş Başına Tek Fiş',
        slipPerOrderHint: 'Pazaryeri siparişi birden fazla pakete böldüyse, hepsini tek fişte toplar. Barkod sipariş numarasıdır.',
        slipName: (orderNumber: string) => `Paket Fişi - ${orderNumber}`,
        slipNameNumbered: (orderNumber: string, index: number) => `Paket Fişi - ${orderNumber} - ${index}`,
    },

    account: {
        title: 'Hesabım',
        subtitle: 'Adınız, e-posta adresiniz ve parolanız.',
        company: {
            title: 'Firma Bilgileri',
            hint: 'Faturalarda, mağazanızda, pazaryeri kayıtlarında ve paket satın alırken kullanılan bilgiler.',
            name: 'Firma Adı',
            legalName: 'Yasal Unvan',
            legalNameHint: 'Ticaret sicilindeki tam unvan. Boşsa firma adı kullanılır.',
            taxNumber: 'Vergi Numarası',
            taxOffice: 'Vergi Dairesi',
            addressLine: 'Adres',
            city: 'İl',
            district: 'İlçe',
            choose: 'Seçin',
            phone: 'Telefon',
            saved: 'Kaydedildi.',
            addressNote: (address: string) => `Mağaza adresiniz ${address}. Şirket bilgileri değişse de bu adres aynı kalır.`,
            exportAll: 'Tüm Verileri İndir',
            exportAllHint:
                'Görebildiğiniz ürünler, siparişler, faturalar, müşteriler, iadeler ve bülten aboneleri; Excel’de açılan dosyalar olarak tek bir ZIP dosyasında.',
        },
        profile: {
            title: 'Bilgileriniz',
            hint: 'Panelde ve gönderdiğiniz e-postalarda görünen ad.',
            name: 'Ad Soyad',
            saved: 'Kaydedildi.',
        },
        email: {
            title: 'E-posta Adresi',
            hint: 'Giriş yaparken kullandığınız adres.',
            current: 'Şu Anki Adres',
            unverified: 'doğrulanmadı',
            next: 'Yeni Adres',
            nextHint: 'Yeni adrese bir onay bağlantısı göndeririz. Siz onaylayana kadar hesabınız eski adresinde kalır.',
            password: 'Parolanız',
            passwordHint: 'Hesabın sizin olduğunu doğrulamak için.',
            submit: 'Onay Bağlantısı Gönder',
            sent: (email: string) => `Onay bağlantısını ${email} adresine gönderdik.`,
        },
        notificationEmail: {
            title: 'E-posta Bildirimleri',
            hint: (company: string) =>
                `Gelen kutunuzdaki hangi bildirimlerin e-posta adresinize de gönderileceğini seçin. Seçiminiz yalnızca ${company} için geçerlidir; üye olduğunuz her firmanın seçimi ayrıdır.`,
            unverified:
                'E-posta adresiniz henüz doğrulanmadığı için bu adrese bildirim gönderilmez. Size gönderdiğimiz bağlantıyla adresinizi doğrulayın.',
            empty: 'Seçilecek Bildirim Yok',
            emptyHint: 'Rolünüz, e-posta ile gönderilebilen bildirimlerin hiçbirini görmenize izin vermiyor.',
            saved: 'Kaydedildi.',
            types: {
                'order.received': { label: 'Yeni Sipariş', hint: 'Bir satış kanalından veya mağazanızdan sipariş geldiğinde.' },
                'question.received': { label: 'Yeni Müşteri Sorusu', hint: 'Bir müşteri ürünlerinizden biri hakkında soru sorduğunda.' },
                'claim.opened': { label: 'Yeni İade veya İptal Talebi', hint: 'Bir müşteri iade ya da iptal talebi açtığında.' },
                'invoice.failed': { label: 'Kesilemeyen Fatura', hint: 'e-Fatura hizmeti bir faturayı kesemediğinde.' },
                'listing.rejected': { label: 'Reddedilen Ürünler', hint: 'Bir satış kanalı gönderdiğiniz ürünleri reddettiğinde.' },
                'listing.prices_sent_back': {
                    label: 'Yeniden Gönderilen Fiyat ve Stoklar',
                    hint: 'Bir satış kanalı sizinkinden farklı bir fiyat ya da rafınızdakinden fazla stok gösterdiği için sizinki yeniden gönderildiğinde.',
                },
                'listing.buybox_lost': { label: 'Buy Box Kaybı', hint: 'Bir pazaryerinde Buy Box’taki ilk sıranızı kaybettiğinizde.' },
                'channel.failed': { label: 'Satış Kanalı Bağlantı Sorunu', hint: 'Bir satış kanalının bağlantısı başarısız olduğunda.' },
                'subscription.changed': { label: 'Abonelik Değişikliği', hint: 'Ekibimiz aboneliğinizi güncellediğinde.' },
                'stock.low': { label: 'Azalan Stok', hint: 'Bir ürünün satılabilir stoğu azaldığında.' },
                'product.loss': { label: 'Zararına Satılan Ürünler', hint: 'Ürünler, bir gün önce hakedişi gelen siparişlerde zarar ettiğinde.' },
            },
        },
        password: {
            title: 'Parola',
            hint: 'Parolanızı değiştirdiğinizde diğer tüm oturumlarınız kapanır.',
            current: 'Mevcut Parola',
            next: 'Yeni Parola',
            nextHint: 'En az 12 karakter.',
            again: 'Yeni Parola (Tekrar)',
            mismatch: 'İki parola aynı değil.',
            submit: 'Parolayı Değiştir',
            saved: 'Parolanız değişti.',
        },
        twoFactor: {
            title: 'İki Adımlı Doğrulama',
            hint: 'Girişte parolanızın yanında telefonunuzdaki doğrulama uygulamasının gösterdiği kod da istenir.',
            on: 'Açık',
            off: 'Kapalı',
            enable: 'Aç',
            disable: 'Kapat',
            setupTitle: 'İki Adımlı Doğrulamayı Aç',
            scan: 'Bu kodu Google Authenticator veya Microsoft Authenticator gibi bir doğrulama uygulamasıyla okutun, ardından uygulamanın gösterdiği kodu girin.',
            qrLabel: 'Doğrulama uygulaması için QR kod',
            setupKey: 'Kurulum Anahtarı',
            setupKeyHint: 'Kodu okutamıyorsanız bu anahtarı uygulamaya girin.',
            code: 'Doğrulama Kodu',
            confirm: 'Aç',
            recoveryTitle: 'Kurtarma Kodlarınız',
            recoveryHint:
                'Telefonunuzu kaybederseniz her kod bir kez giriş yapmanızı sağlar. Güvenli bir yerde saklayın: kodlar yalnızca şimdi gösterilir.',
            done: 'Kaydettim',
            disableTitle: 'İki Adımlı Doğrulamayı Kapat',
            disableHint: 'Parolanızı ve doğrulama uygulamanızdaki kodu ya da bir kurtarma kodunu girin.',
            password: 'Parola',
            disableConfirm: 'Kapat',
            copy: { copy: 'Kopyala', copied: 'Kopyalandı' },
        },
        sessions: {
            title: 'Açık Oturumlar',
            hint: 'Hesabınızın açık olduğu yerler. Tanımadığınız birini kapatın.',
            empty: 'Başka Açık Oturum Yok',
            thisOne: 'Bu Cihaz',
            end: 'Oturumu Kapat',
            endTitle: 'Oturumu Kapat',
            endBody: 'Bu cihazdaki oturum kapanacak ve yeniden giriş gerekecek.',
            unknownDevice: 'Bilinmeyen Cihaz',
            unknownPlace: 'bilinmeyen konum',
            endOthers: 'Diğer Oturumları Kapat',
            endOthersTitle: 'Diğer Oturumlar Kapatılacak',
            endOthersBody: 'Bu cihaz dışındaki bütün cihazlarda hesabınızdan çıkılacak ve yeniden giriş gerekecek.',
            endedOthers: 'Diğer oturumlar kapatıldı.',
        },
    },

    questions: {
        allSources: 'Tüm Kaynaklar',
        sourceLabel: 'Kaynak',
        sourceManual: 'Elle Girilen',
        overdueOnly: 'Yalnızca Süresi Geçenler',
        searchPlaceholder: 'Sorularda ara',
        showing: (shown: number, total: number) => `${total} sorudan ${shown} tanesi`,
        title: 'Sorular',
        subtitle: 'Açık sorular önce ve süresi en yakın olan üstte.',
        open: 'Açık',
        answered: 'Yanıtlanan',
        all: 'Tümü',
        empty: 'Bekleyen Soru Yok',
        emptyFiltered: 'Filtreye Uyan Soru Yok',
        answerPlaceholder: 'Yanıtınızı yazın…',
        send: 'Gönder',
        cannedInsert: 'Hazır Yanıt Ekle',
        overdue: 'Süresi geçti',
        askedAt: 'Soru Tarihi',
        timeLeft: 'Kalan Yanıt Süresi',
        underAnHour: '1 saatten az',
        hoursLeft: (hours: number) => `${hours} saat`,
        daysLeft: (days: number) => `${days} gün`,
        from: 'Soran',
        answeredLabel: 'Yanıt',
        subject: 'Soru',
        product: 'Ürün',
        statusColumn: 'Durum',
        channelFailed: 'Pazaryerine İletilemedi',
        channelFailedHint: 'Bu yanıt pazaryerine ulaşmadı. Pazaryeri panelinden elle yazın.',
        channelFailedResend: 'Bu yanıt pazaryerine ulaşmadı. Yukarıdaki yanıtı düzeltip yeniden gönderebilirsiniz.',
        statusAnswered: 'Yanıtlandı',
        statusSending: 'Pazaryerine Gönderiliyor',
        statusClosed: 'Kapatıldı',
        resend: 'Yeniden Gönder',
        reply: 'Yanıtla',
        refused: 'İletilemedi',
        conversation: 'Konuşma',
        fromBuyer: 'Müşteri',
        fromSeller: 'Siz',
        answerLimit: (min: number, max: number) => `${min}–${max} karakter`,
        answerCount: (length: number, max: number) => `${length} / ${max} karakter`,
        answerTooShort: (min: number) => `Bu pazaryeri en az ${min} karakter istiyor.`,
    },

    sizeGuides: {
        title: 'Beden Tabloları',
        subtitle: 'Bir kategoriye bağladığınız tablo, mağazanızda o kategorinin ve altındakilerin ürün sayfalarında gösterilir.',
        add: 'Beden Tablosu Oluştur',
        edit: 'Beden Tablosunu Düzenle',
        name: 'Başlık',
        nameHint: 'Müşteri tablonun üstünde görür, örneğin "Kadın Üst Giyim".',
        table: 'Tablo',
        tableHint: 'İlk sütun çoğunlukla beden, diğerleri ölçülerdir. Boş bırakılan satır kaydedilmez.',
        column: (index: number) => `${index}. sütun başlığı`,
        cell: (row: number, column: number) => `${row}. satır, ${column}. sütun`,
        addColumn: 'Sütun Ekle',
        addRow: 'Satır Ekle',
        removeColumn: (index: number) => `${index}. sütunu sil`,
        removeRow: (index: number) => `${index}. satırı sil`,
        note: 'Nasıl Ölçülür',
        noteHint: 'İsteğe bağlı. Tablonun altında gösterilir.',
        categories: 'Kategoriler',
        categoriesHint: 'Kendi tablosu olmayan alt kategorilerde de gösterilir. Bir kategorinin tek tablosu olur.',
        save: 'Kaydet',
        delete: 'Sil',
        empty: 'Henüz Beden Tablosu Yok',
        emptyHint: 'Bir tablo yazıp kategoriye bağlayın; o kategorinin ürün sayfaları tabloya bağlantı verir.',
        filedUnder: (count: number) => (count === 0 ? 'Kategoriye bağlı değil' : `${count} kategori`),
        sizes: (count: number) => `${count} beden`,
        updated: 'Güncellendi',
        starter: { size: 'Beden', chest: 'Göğüs (cm)', waist: 'Bel (cm)' },
        confirmDelete: {
            title: 'Beden Tablosu Silinecek',
            body: (title: string) => `"${title}" silinir; kategorilerindeki ürün sayfaları artık bu tabloyu göstermez.`,
            action: 'Sil',
        },
    },

    reviews: {
        title: 'Yorumlar',
        subtitle: 'Müşteriler mağazada yorum bırakır; onayladıklarınız ürün sayfasında görünür.',
        pending: 'Bekleyen',
        approved: 'Onaylı',
        rejected: 'Reddedilen',
        empty: 'Bu Durumda Yorum Yok',
        product: 'Ürün',
        author: 'Yazan',
        rating: 'Puan',
        outOfFive: (value: number) => `5 üzerinden ${value}`,
        date: 'Tarih',
        approve: 'Onayla',
        reject: 'Reddet',
        reply: 'Yanıtla',
        replyPlaceholder: 'Müşteriye görünen kısa yanıt…',
        replySave: 'Yanıtı Kaydet',
        verifiedPurchase: (orderNumber: string) => `Doğrulanmış alışveriş · ${orderNumber}`,
        pendingBadge: (count: number) => `${count} Bekleyen`,
        photos: 'Fotoğraflar',
        photo: (index: number) => `${index}. fotoğraf`,
        hiddenPhoto: 'Gizli',
        hidePhoto: 'Gizle',
        showPhoto: 'Göster',
        photosHint: 'Gizlediğiniz fotoğraf değerlendirmeyle birlikte kalır, mağazada gösterilmez.',
    },

    notifications: {
        title: 'Bildirimler',
        subtitle: 'Platform duyuruları ve sistem olayları tek yerde.',
        empty: 'Bildirim Yok',
        markRead: 'Okundu İşaretle',
        markAll: 'Tümünü Okundu İşaretle',
        unreadCount: (count: number) => `${count} okunmamış`,
        allRead: 'Hepsi okundu.',
        level: {
            info: 'Bilgi',
            success: 'Başarılı',
            warning: 'Uyarı',
            error: 'Hata',
        },
    },

    newsletter: {
        title: 'Bülten',
        subtitle: 'Mağazanızın abonelerine yazdığınız kampanyalar, abone listesi ve stok bekleyenler.',
        tabs: {
            campaigns: 'Kampanyalar',
            subscribers: 'Aboneler',
            waits: 'Stok Bekleyenler',
        },
        campaignsHint:
            'Kampanya, iznini geri almamış her aboneye gider. Her e-postada mağazanızın adı ve adresi ile tek tıkla abonelikten çıkma bağlantısı bulunur.',
        add: 'Yeni Kampanya',
        edit: 'Kampanyayı Düzenle',
        empty: 'Henüz Kampanya Yok',
        emptyHint: 'İlk kampanyanızı yazın, önce kendinize deneme olarak gönderin.',
        subject: 'Konu',
        subjectHint: 'Gelen kutusunda görünen başlık.',
        body: 'Kampanya Metni',
        editor: {
            editor: 'Kampanya Metni',
            block: 'Yazı Biçimi',
            paragraph: 'Paragraf',
            heading2: 'Başlık',
            heading3: 'Alt Başlık',
            bold: 'Kalın',
            italic: 'İtalik',
            underline: 'Altı Çizili',
            strike: 'Üstü Çizili',
            bulletList: 'Madde İşaretli Liste',
            orderedList: 'Numaralı Liste',
            quote: 'Alıntı',
            link: 'Bağlantı Ekle',
            linkPrompt: 'Bağlanacak adres (kaldırmak için boş bırakın)',
            linkApply: 'Uygula',
            linkCancel: 'Vazgeç',
            source: 'HTML',
        },
        status: 'Durum',
        statuses: {
            draft: 'Taslak',
            sending: 'Gönderiliyor',
            sent: 'Gönderildi',
        },
        recipients: 'Gönderim',
        progress: (sent: number, total: number, failed: number, skipped: number) =>
            [`${sent} / ${total} gönderildi`, failed > 0 ? `${failed} başarısız` : '', skipped > 0 ? `${skipped} atlandı` : '']
                .filter(Boolean)
                .join(' · '),
        date: 'Tarih',
        send: 'Gönder',
        confirmSend: {
            title: 'Kampanya Gönderilecek',
            body: (subject: string, count: number) => `"${subject}" ${count} aboneye gönderilecek. Gönderildikten sonra değiştirilemez ve silinemez.`,
            action: 'Gönder',
        },
        save: 'Kaydet',
        test: 'Deneme Gönder',
        testSent: 'Deneme e-postası kendi adresinize gönderildi.',
        delete: 'Sil',
        confirmDelete: {
            title: 'Taslak Silinecek',
            body: 'Bu kampanya taslağı silinecek.',
            action: 'Sil',
        },
        search: 'E-posta Ara',
        all: 'Tümü',
        subscriberStatus: {
            active: 'Abone',
            left: 'Ayrıldı',
            removed: 'Listeden Çıkarıldı',
        },
        download: 'Listeyi İndir',
        counts: (active: number, left: number) => `${active} abone · ${left} ayrıldı`,
        email: 'E-posta',
        source: 'Kaynak',
        sources: {
            footer: 'Alt Bilgi',
            signup: 'Üyelik Formu',
            account: 'Hesabım',
            other: 'Diğer',
        },
        consentedAt: 'İzin Tarihi',
        withdraw: 'Listeden Çıkar',
        confirmWithdraw: {
            title: 'Abone Listeden Çıkarılacak',
            body: (email: string) => `${email} artık kampanya almayacak. Kayıt, iznin geri alındığı tarihle saklanır.`,
            action: 'Listeden Çıkar',
        },
        noSubscribers: 'Henüz Abone Yok',
        noSubscribersHint: 'Mağazanızın alt bilgisindeki bülten kutusundan ya da üyelik formundan abone olanlar burada görünür.',
        more: 'Daha Fazla Göster',
        waitsHint: 'Tükenen bir ürün için "Gelince Haber Ver" diyenler. Stok gelince her birine e-posta gider.',
        noWaits: 'Stok Bekleyen Yok',
        noWaitsHint: 'Tükenen bir ürünün sayfasında e-posta bırakanlar burada görünür.',
        product: 'Ürün',
        sku: 'Stok Kodu',
        waiting: 'Bekleyen',
        since: 'İlk İstek',
    },
    coupons: {
        title: 'İndirim Kodları',
        subtitle: (ownStore: string) => `${ownStore} için indirim kodları. Ödeme anında uygulanır.`,
        add: 'Kod Oluştur',
        edit: 'Kodu Düzenle',
        code: 'Kod',
        codeHint: 'Müşterinin ödeme sırasında yazacağı kod, ör. YAZ10.',
        codeLocked: 'Kod oluşturulduktan sonra değiştirilemez.',
        kind: 'İndirim Türü',
        percentage: 'Yüzde (%)',
        fixed: 'Sabit Tutar',
        valuePercentage: 'İndirim Oranı (%)',
        valueFixed: 'İndirim Tutarı',
        minSubtotal: 'En Düşük Sepet Tutarı',
        minSubtotalHint: 'Sepet bu tutarın altındaysa kod geçmez. Boş bırakırsanız her sepette geçer.',
        usageLimit: 'Kullanım Sınırı',
        usageLimitHint: 'Kod en fazla bu kadar siparişte kullanılabilir. Boş bırakırsanız sınır olmaz.',
        startsOn: 'Başlangıç Tarihi',
        endsOn: 'Bitiş Tarihi',
        windowHint: 'Boş bırakırsanız kod, siz kapatana kadar geçerli olur.',
        freeShipping: 'Ücretsiz Kargo',
        save: 'Kaydet',
        empty: 'Henüz Kod Yok',
        count: (count: number) => `${count} indirim kodu`,
        discount: 'İndirim',
        usage: 'Kullanım',
        active: 'Aktif',
        inactive: 'Pasif',
        notStarted: 'Başlamadı',
        expired: 'Süresi Doldu',
        usedUp: 'Tükendi',
        delete: 'Sil',
        confirmDelete: {
            title: 'İndirim Kodu Silinecek',
            body: (code: string) => `${code} kodu artık ödemede kabul edilmez. Kodun kullanıldığı siparişler değişmez.`,
            action: 'Kodu Sil',
        },
    },

    promotions: {
        title: 'Otomatik İndirimler',
        subtitle: (ownStore: string) => `${ownStore} için kod gerekmeden uygulanan indirimler. İndirim kodu, bunlardan sonra kalan tutara uygulanır.`,
        add: 'İndirim Oluştur',
        edit: 'İndirimi Düzenle',
        name: 'Ad',
        nameHint: 'Alıcı bu adı sepette görür, ör. "Mutfak Haftası".',
        kind: 'İndirim Türü',
        percentage: 'Yüzde İndirim',
        buyXPayYKind: 'X Al Y Öde',
        percent: 'İndirim Oranı (%)',
        percentHint:
            'Her adetten düşülür ve fiyat olarak gösterilir. Üstü çizili fiyat, mağazanızın indirim başlamadan önceki on gün içindeki en düşük fiyatıdır; bu bir indirim değilse üstü çizili fiyat gösterilmez.',
        begunHint:
            'Bu indirim başladı. İndirim oranını, adetlerini ya da kapsadığı ürünleri değiştirir veya başlangıcını ileri alırsanız bu indirim şimdi sona erer ve yeni koşullarla yenisi başlar.',
        buy: 'Al',
        pay: 'Öde',
        buyXPayYHint: 'Bu gruptan alınan her takımda en düşük fiyatlı adetler ücretsiz olur.',
        buyXPayY: (buy: number, pay: number) => `${buy} Al ${pay} Öde`,
        scope: 'Uygulandığı Ürünler',
        onCategory: 'Bir Kategori',
        onProducts: 'Seçili Ürünler',
        category: 'Kategori',
        categoryHint: 'Alt kategorileri de kapsar.',
        pickCategory: 'Kategori seçin',
        products: 'Ürünler',
        searchProducts: 'Ürün adı veya koduyla arayın',
        searchResults: 'Bulunan ürünler',
        chosenProducts: 'Seçili ürünler',
        productsNeedCatalogue: 'Ürün aramak için ürün listesine erişiminiz olmalı.',
        noProducts: 'Henüz ürün seçilmedi.',
        pick: 'Ekle',
        added: 'Eklendi',
        remove: 'Çıkar',
        addProduct: (title: string) => `${title} ürününü ekle`,
        removeProduct: (title: string) => `${title} ürününü çıkar`,
        productCount: (count: number) => `${count} ürün`,
        startsOn: 'Başlangıç Tarihi',
        endsOn: 'Bitiş Tarihi',
        endsOnHint: 'Boş bırakırsanız siz sonlandırana kadar geçerli olur.',
        save: 'Kaydet',
        empty: 'Henüz Otomatik İndirim Yok',
        emptyHint: 'Bir kategoriye veya seçtiğiniz ürünlere yüzde indirim ya da X Al Y Öde indirimi oluşturun.',
        count: (count: number) => `${count} otomatik indirim`,
        discount: 'İndirim',
        active: 'Aktif',
        notStarted: 'Başlamadı',
        ended: 'Sona Erdi',
        end: 'Sonlandır',
        confirmEnd: {
            title: 'İndirim Sonlandırılacak',
            body: (name: string) => `${name} mağazada artık uygulanmaz. Bu indirimle verilmiş siparişler değişmez.`,
            action: 'İndirimi Sonlandır',
        },
    },

    apiTokens: {
        title: 'Anahtarlar',
        count: (count: number) => `${count} anahtar`,
        subtitle: 'Kendi yazılımınızın hesabınıza bağlanması için anahtar oluşturun. Her anahtar yalnızca izin verdiğiniz işleri yapabilir.',
        add: 'Anahtar Oluştur',
        name: 'Anahtar Adı',
        nameHint: 'Anahtarı hangi yazılımın kullandığını hatırlamanız için, ör. "Muhasebe programı".',
        scopes: 'İzinler',
        scopesHint: 'Bir anahtara yalnızca sizin de sahip olduğunuz izinler verilebilir. Sizden bir izin kalkarsa anahtarlarınızdan da kalkar.',
        create: 'Oluştur',
        createdTitle: 'Anahtar Oluşturuldu — Sadece Şimdi Gösterilir',
        createdNote: 'Bu anahtarı güvenli bir yere kopyalayın. Tekrar gösterilmeyecek.',
        empty: 'Henüz Anahtar Yok',
        emptyHint: 'Kendi yazılımınızı hesabınıza bağlamak için bir anahtar oluşturun.',
        prefix: 'Anahtar',
        lastUsed: 'Son Kullanım',
        never: 'Hiç',
        revoked: 'İptal Edildi',
        revoke: 'İptal Et',
        confirmRevoke: {
            title: 'Anahtar İptal Edilecek',
            body: 'Bu anahtarı kullanan yazılım hesabınıza artık bağlanamaz. Bu işlem geri alınamaz.',
            action: 'İptal Et',
        },
    },

    webhooks: {
        title: 'Bildirim Adresleri',
        count: (count: number) => `${count} adres`,
        subtitle: 'Yeni sipariş gibi bir olay olduğunda kendi yazılımınıza anında haber verin.',
        add: 'Adres Ekle',
        url: 'Adres',
        urlHint: 'Bildirimleri alacak, https ile başlayan, herkese açık adres.',
        secret: 'Gizli Anahtar',
        secretHint:
            'En az 8 karakter. Her bildirim bu anahtarla imzalanır; yazılımınız X-Webhook-Signature başlığındaki imzayı kontrol ederek bildirimin bizden geldiğini anlar.',
        events: 'Olaylar',
        eventsHint: 'Virgülle ayırın, ör. order.created. Boş bırakırsanız tüm olaylar gönderilir.',
        allEvents: 'Tüm Olaylar',
        save: 'Kaydet',
        failed: 'Adres kaydedilemedi. Adresin https ile başladığını ve gizli anahtarın en az 8 karakter olduğunu kontrol edin.',
        sendTest: 'Test Bildirimi Gönder',
        testSent: 'Test bildirimi gönderildi. Sonucu Son bildirimler listesinde görebilirsiniz.',
        endpointsEmpty: 'Henüz Bildirim Adresi Yok',
        endpointsEmptyHint: 'Bir olay olduğunda kendi yazılımınıza haber vermek için adres ekleyin.',
        deliveries: 'Son Bildirimler',
        deliveriesEmpty: 'Henüz Bildirim Gönderilmedi',
        event: 'Olay',
        eventNames: {
            'order.created': 'Yeni Sipariş',
            ping: 'Test Bildirimi',
        },
        status: 'Durum',
        attempts: 'Deneme Sayısı',
        response: 'Yanıt Kodu',
        when: 'Tarih',
        delete: 'Sil',
        confirmDelete: {
            title: 'Bildirim Adresi Silinecek',
            body: 'Bu adrese artık bildirim gönderilmez.',
            action: 'Sil',
        },
        statuses: {
            pending: 'Bekliyor',
            success: 'Başarılı',
            failed: 'Başarısız',
        },
    },

    roles: {
        owner: 'Sahip',
        admin: 'Yönetici',
        operations: 'Operasyon',
        finance: 'Muhasebe',
        readonly: 'Salt Okunur',
    },
};
